import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../services/api';
import { getAlgos, createAlgo, updateAlgo, deleteAlgo, getStockConfig, getTrades, getTradeDates, getMyStrategies, updateMyStrategy, getMyStrategyTrades, getMyStrategyTradeDates, squareOffMyStrategyTrade, stopAlgoTrade, noReverseTrade, getGlobalSettings, saveGlobalSettings, manualSquareOffAll, getPnLStatus, getNseStocksStatus, triggerSnapshot, downloadNseStocksData, toggleAlgoFavorite, toggleMyStrategyFavorite, toggleMyStrategyAutopilotEligible, getBacktestDates, getBacktestCandles, simulateOBMBacktest, getUserDataDates, getUserDataCandles, getMyTradesDates, getMyTrades, squareOffMyTrade } from '../services/api';
import { HideAlgoBacktestIcons } from '../config';
import './AlgosPage.css';

function GsHelp({ text }) {
  const ref = useRef();
  const [style, setStyle] = useState(null);
  const show = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const w = 220;
    let left = rect.left + rect.width / 2;
    left = Math.max(w / 2 + 8, Math.min(left, window.innerWidth - w / 2 - 8));
    setStyle({ position: 'fixed', top: rect.top - 10, left, transform: 'translate(-50%, -100%)' });
  };
  return (
    <span className="gs-help" ref={ref} onMouseEnter={show} onMouseLeave={() => setStyle(null)}>
      ?{style && <span className="gs-popup" style={style}>{text}</span>}
    </span>
  );
}

const ALGO_TYPES = ['intraday', 'positional', 'scalping', 'swing'];

const MY_STRATEGY_DEFS = {
  obmVwapHybrid: {
    type: 'obmVwapHybrid',
    name: 'OBM + VWAP Hybrid',
    abbr: 'HYB',
    noAutopilot: true, // leaves stop-loss orders resting in Kite — conflicts with Manage My P/L / Autopilot square-offs
    description: 'Regime-aware hybrid: classifies each day as TRENDING or RANGING using NIFTY 50\'s opening range vs ATR. TRENDING days run the Order Book Momentum scan on all NSE stocks. RANGING days run VWAP reversion on your configured stocks. Needs previous-day 3PM snapshot.',
    example: [
      'Regime check (9:30 AM): NIFTY 50\'s opening range (9:15–9:30 AM) = 142 pts high-to-low. 14-day ATR = 320 pts. Ratio = 0.44 — above the 0.35 threshold → TRENDING day. The OBM scan fires on all 883 NSE stocks.',
      'TRENDING — Entry (9:30 AM): HDFCBANK shows buy-side depth at 2.8× yesterday\'s 3 PM snapshot; sell-side is only 22% of buy-side (< 50% ✓). BUY 30 shares at ₹1,640 (₹50,000 ÷ ₹1,640). SL-M at ₹1,631.8 (−0.5%). Depth stays strong → pyramid entries at 9:35 AM (₹1,644, 30 shares) and 9:40 AM (₹1,648, 30 shares). Total position: 90 shares at avg ₹1,644.',
      'TRENDING — Exit: Trailing SL ratchets every 10 min over 60 min. By 10:30 AM SL is ₹1,662. At 10:42 AM depth collapses, price dips to ₹1,661 → SL triggers. All 90 shares exit. P&L: (₹1,661 − avg ₹1,644) × 90 = +₹1,530.',
      'RANGING scenario (different day): NIFTY OR = 65 pts, ATR = 310 pts, ratio = 0.21 → RANGING. VWAP mode activates on your configured stocks. At 10:45 AM INFY is at ₹1,919 — 2σ below VWAP (₹1,925) with 1.9× volume surge. Mean-reversion BUY 26 shares at ₹1,920. At 11:08 AM price returns to VWAP (₹1,927) → auto-exit. P&L: ₹7 × 26 = +₹182. On active RANGING days, 3–5 such reversions across configured stocks can stack to ₹3,000–₹6,000.',
    ],
    requiresSnapshot: true,
    docUrl: '/docs/hyb-analysis',
    fields: [
      { key: '_section_regime',             label: 'Regime Detection',                 type: 'section'                                              },
      { key: 'regime_check_time',           label: 'Regime Check Time',                type: 'time',                                default: '09:30', help: 'Time at which NIFTY 50\'s opening range is measured to classify the day as TRENDING or RANGING.' },
      { key: 'regime_threshold',            label: 'TRENDING Threshold (OR/ATR)',      type: 'number', step: 0.05, min: 0.05, max: 1, default: 0.35, help: 'If NIFTY\'s OR ÷ ATR ≥ this value, today is TRENDING (OBM scan runs). Below it → RANGING (VWAP mode).' },
      { key: 'atr_days',                    label: 'ATR Lookback (days)',              type: 'number', step: 1,    min: 5, max: 30,   default: 14,   help: 'Days of NIFTY history used to compute ATR for the regime detection ratio.' },
      { key: '_section_timing',             label: 'Timing',                           type: 'section'                                              },
      { key: 'entry_time',                  label: 'Entry Start Time',                 type: 'time',                                default: '09:30', help: 'Earliest time entries are placed in both TRENDING and RANGING modes.' },
      { key: 'square_off_time',             label: 'Square-off Time',                  type: 'time',                                default: '15:15', help: 'All open positions are force-closed at this time.' },
      { key: 'vwap_entry_end',              label: 'VWAP Entries Until (Ranging only)',type: 'time',                                default: '14:00', help: 'In RANGING mode, no new VWAP crossover entries after this time. Existing positions stay open.' },
      { key: '_section_position',           label: 'Position Sizing',                  type: 'section'                                              },
      { key: 'max_positions',               label: 'Max Open Positions',               type: 'number', step: 1,    min: 1, max: 5,   default: 3,    help: 'Maximum simultaneous open positions across both TRENDING and RANGING modes.' },
      { key: 'max_position_value',          label: 'Max Position Value (₹)',           type: 'number', step: 1000, min: 1000,        default: 50000, help: 'Maximum ₹ value per trade. Qty = floor(this ÷ entry price), locked at first entry.' },
      { key: '_section_risk',               label: 'Risk Management',                  type: 'section'                                              },
      { key: 'sl_pct',                      label: 'Stop Loss (%)',                    type: 'number', step: 0.1,  min: 0.1,         default: 0.5,  help: 'Stop loss distance as % from entry price. Applies in both TRENDING and RANGING modes.' },
      { key: '_section_trending',           label: 'TRENDING Mode (OBM Scan)',         type: 'section'                                              },
      { key: 'long_multiplier',             label: 'Long Bid Surge Multiplier (×)',    type: 'number', step: 0.1,  min: 1,           default: 2.0,  help: 'Live buy-side depth must be ≥ this × the previous-day snapshot to trigger a BUY signal.' },
      { key: 'short_multiplier',            label: 'Short Bid Surge Multiplier (×)',   type: 'number', step: 0.1,  min: 1,           default: 2.0,  help: 'Live sell-side depth must be ≥ this × the snapshot to trigger a SELL signal.' },
      { key: 'long_short_ratio',            label: 'Max Opposite Side Ratio',          type: 'number', step: 0.05, min: 0.1,         default: 0.5,  help: 'Opposing side must be ≤ this ratio to confirm one-directional pressure. Guards against fake imbalance.' },
      { key: 'min_price',                   label: 'Min Stock Price (₹)',              type: 'number', step: 1,    min: 1,           default: 10,   help: 'Stocks priced below this are skipped in the OBM scan (avoids illiquid penny stocks).' },
      { key: 'max_price',                   label: 'Max Stock Price (₹)',              type: 'number', step: 100,  min: 10,          default: 10000, help: 'Stocks priced above this are skipped (avoids high-priced stocks with low liquidity at these qty sizes).' },
      { key: 'max_gap_pct',                 label: 'Max Gap at Open (%)',              type: 'number', step: 0.1,  min: 0,           default: 0,    help: 'Skip a stock if its open vs prev-close gap exceeds this %. Set to 0 to disable the filter.' },
      { key: 'trend_filter_pct',            label: 'Trend Filter (%)',                 type: 'number', step: 0.5,  min: 0,           default: 0,    help: 'In TRENDING mode: skip BUY if price is already down more than this % from today\'s open; skip SELL if already up more. 0 = disabled.' },
      { key: 'depth_confirmation_seconds',  label: 'Depth Confirmation Delay (sec)',   type: 'number', step: 10,   min: 0,           default: 0,    help: 'Re-check order book depth after this many seconds before entering. Guards against spoofed orders. 0 = no delay.' },
      { key: 'max_pyramids',                label: 'Max Pyramid Entries',             type: 'number', step: 1,    min: 0, max: 10,   default: 3,    help: 'Max additional orders to add if the depth signal remains strong after the initial entry.' },
      { key: 'pyramid_interval_mins',       label: 'Pyramid Interval (min)',           type: 'number', step: 1,    min: 1,           default: 5,    help: 'Minimum minutes between consecutive pyramid entries.' },
      { key: 'pyramid_depth_confirm_pct',   label: 'Min Pyramid Depth Ratio (%)',      type: 'number', step: 5,    min: 0,           default: 50,   help: 'For a pyramid entry, live depth ratio must still be ≥ this % of the original imbalance.' },
      { key: '_section_ranging',            label: 'RANGING Mode (VWAP Reversion)',    type: 'section'                                              },
      { key: 'vwap_band_k',                 label: 'VWAP Band Width (σ multiplier)',   type: 'number', step: 0.25, min: 0.5, max: 4,  default: 2.0, help: 'Width of the VWAP envelope in standard deviations. Price must re-enter the band for a valid entry.' },
      { key: 'vwap_volume_sma_period',      label: 'Volume SMA Period (candles)',      type: 'number', step: 1,    min: 5,           default: 20,   help: 'Candles used to compute the average volume baseline for VWAP crossover confirmation.' },
      { key: 'vwap_volume_multiplier',      label: 'Volume Surge Multiplier (×)',      type: 'number', step: 0.25, min: 0,           default: 1.5,  help: 'Volume on the crossover candle must exceed the SMA by this multiple to confirm the VWAP signal.' },
    ],
  },
  orderBookMomentum: {
    type: 'orderBookMomentum',
    name: 'Order Book Momentum',
    abbr: 'OBM',
    noAutopilot: true, // leaves stop-loss orders resting in Kite — conflicts with Manage My P/L / Autopilot square-offs
    description: 'Scans all 883 NSE stocks at entry time and opens up to 3 positions in stocks showing extreme order book imbalance vs previous-day 3PM depth. Pyramids up to 5× on confirming signals, then trails SL for 1 hour. Exchange-side SL-M orders. MIS only.',
    example: [
      'Setup: The algo holds yesterday\'s 3 PM order-book depth snapshot for all 883 NSE stocks. At 9:30 AM the live scan fires — it compares live depth to the snapshot for every stock. Two qualify today: RELIANCE (buy-side 3.1× snapshot, sell-side only 28% of buy-side ✓) and INFY (buy-side 2.4×, sell-side 31% ✓). Both pass the imbalance and ratio tests.',
      'Entry (9:30 AM): RELIANCE at ₹2,485 → BUY 20 shares (₹50,000 ÷ ₹2,485). Exchange SL-M placed at ₹2,472.6 (−0.5%). INFY at ₹1,820 → BUY 27 shares. SL-M at ₹1,810.9. Two of three max-position slots are now used.',
      'Pyramid (9:40 AM): Re-check fires 10 min after entry. RELIANCE buy-side ratio is still 2.9× (above 50% of original 3.1× ✓) and price moved +0.5% ✓. Pyramid BUY: 20 more shares at ₹2,489. Avg cost = ₹2,487. SL-M updated to ₹2,476.6 (slot #3 used).',
      'Trailing SL — RELIANCE (60-min window): SL steps up with price every 10 min: 10:00→₹2,494, 10:10→₹2,499, 10:20→₹2,507, 10:30→₹2,511. At 10:36 AM price slides to ₹2,510 → exchange SL-M triggers → exit at ₹2,511. P&L: (₹2,511 − avg ₹2,487) × 40 = +₹960.',
      'INFY continues until 15:15 force square-off, exiting at ₹1,854. P&L: (₹1,854 − ₹1,820) × 27 = +₹918. Combined: +₹1,878. On a strong momentum day with 3 fully-pyramided positions (max_pyramids=5) this scales to ₹15,000–₹25,000+ depending on move size.',
    ],
    requiresSnapshot: true,
    docUrl: '/docs/obm-analysis',
    fields: [
      { key: '_section_timing',           label: 'Timing',                          type: 'section'                                              },
      { key: 'entry_time',                label: 'Entry Scan Time',                 type: 'time',                                default: '09:30', help: 'When the depth scan runs. 09:30 is typical — opening volatility has settled by then.' },
      { key: 'square_off_time',           label: 'Square-off Time',                 type: 'time',                                default: '15:15', help: 'All open positions are force-closed at this time.' },
      { key: '_section_interval',         label: 'Interval Scanning',               type: 'section'                                              },
      { key: 'scan_interval_mins',        label: 'Re-scan Every (min, 0=once)',      type: 'number', step: 5,    min: 0,  default: 0,    help: 'Re-run the depth scan every N minutes to find new opportunities. Set to 0 to scan only once at entry_time.' },
      { key: 'max_daily_entries',         label: 'Max Entries/Day (0=max_pos)',      type: 'number', step: 1,    min: 0,  default: 0,    help: 'Total entries allowed per day across all scans. 0 = use max_positions as the cap.' },
      { key: '_section_position',         label: 'Position Sizing',                 type: 'section'                                              },
      { key: 'max_positions',             label: 'Max Open Positions',              type: 'number', step: 1,     min: 1, max: 5,  default: 3,     help: 'Maximum stocks open at the same time.' },
      { key: 'max_position_value',        label: 'Max Position Value (₹)',          type: 'number', step: 1000,  min: 1000,       default: 50000, help: '₹ value per position. Qty = floor(this ÷ entry price), fixed at first entry.' },
      { key: 'min_price',                 label: 'Min Stock Price (₹)',             type: 'number', step: 1,     min: 1,          default: 10,    help: 'Stocks below this price are skipped (avoids illiquid penny stocks).' },
      { key: 'max_price',                 label: 'Max Stock Price (₹)',             type: 'number', step: 100,   min: 10,         default: 10000, help: 'Stocks above this price are skipped.' },
      { key: '_section_entry',            label: 'Entry Criteria',                  type: 'section'                                              },
      { key: 'long_multiplier',           label: 'Long Bid Surge Multiplier (×)',   type: 'number', step: 0.1,   min: 1,          default: 2.0,   help: 'Live buy-side depth must be ≥ this × the prev-day 3PM snapshot to trigger a BUY.' },
      { key: 'short_multiplier',          label: 'Short Bid Surge Multiplier (×)',  type: 'number', step: 0.1,   min: 1,          default: 2.0,   help: 'Live sell-side depth must be ≥ this × the snapshot to trigger a SELL.' },
      { key: 'long_short_ratio',          label: 'Max Opposite Side Ratio',         type: 'number', step: 0.05,  min: 0.1,        default: 0.5,   help: 'Opposing side must be ≤ this ratio to confirm one-directional pressure. Filters out balanced books.' },
      { key: '_section_risk',             label: 'Risk Management',                 type: 'section'                                              },
      { key: 'sl_pct',                    label: 'Stop Loss (%)',                   type: 'number', step: 0.1,   min: 0.1,        default: 0.5,   help: 'Stop loss as % from entry price. An exchange-side SL-M order is placed immediately after entry.' },
      { key: '_section_pyramid',          label: 'Pyramiding',                      type: 'section'                                              },
      { key: 'max_pyramids',              label: 'Max Pyramid Entries',             type: 'number', step: 1,     min: 1, max: 10,  default: 5,    help: 'Max additional entries after the initial position if the depth signal remains strong.' },
      { key: 'pyramid_interval_mins',     label: 'Pyramid Interval (min)',          type: 'number', step: 1,     min: 1,           default: 5,    help: 'Minimum minutes between consecutive pyramid entries.' },
      { key: 'pyramid_min_move_pct',      label: 'Min Price Move for Pyramid (%)',  type: 'number', step: 0.1,   min: 0,           default: 0.5,  help: 'Price must have moved at least this % from the last pyramid price (or entry) in the trade direction before another pyramid is placed. 0 = disabled.' },
      { key: '_section_trailing',         label: 'Trailing SL Phase',               type: 'section'                                              },
      { key: 'trailing_interval_mins',    label: 'Trailing Check Interval (min)',   type: 'number', step: 1,    min: 1,  default: 10,   help: 'How often to re-evaluate the trailing SL once in the trailing phase.' },
      { key: 'trailing_duration_mins',    label: 'Trailing Duration (min)',         type: 'number', step: 5,    min: 5,  default: 60,   help: 'How long to stay in trailing SL mode before force-closing the position.' },
      { key: 'trailing_trigger_pct',      label: 'Instant Trigger on Move (%)',     type: 'number', step: 0.1,  min: 0.1, default: 0.5, help: 'Exit immediately if price moves adversely by this % during the trailing phase (no waiting for interval).' },
      { key: '_section_scan',             label: 'Anti-Spoof Confirmation',          type: 'section'                                   },
      { key: 'depth_confirmation_seconds', label: 'Depth Confirmation Delay (sec)', type: 'number', step: 10,   min: 0,  default: 0,    help: 'Re-check depth after this many seconds before acting. Guards against spoofed orders. 0 = enter immediately.' },
      { key: '_section_filters',          label: 'Entry Filters',                   type: 'section'                                   },
      { key: 'max_gap_pct',               label: 'Max Gap at Open (%)',              type: 'number', step: 0.1,  min: 0,  default: 0,    help: 'Skip a stock if its open vs prev-close gap exceeds this %. 0 = no gap filter.' },
      { key: 'trend_filter_pct',          label: 'Trend Filter (%)',                 type: 'number', step: 0.5,  min: 0,  default: 0,    help: 'Skip BUY if price is already down more than this % from today\'s open; skip SELL if already up more. Prevents counter-trend entries during strong moves. 0 = disabled.' },
      { key: '_section_pyramid_adv',      label: 'Pyramid Confirmation',            type: 'section'                                   },
      { key: 'pyramid_depth_confirm_pct', label: 'Min Pyramid Depth Ratio (%)',     type: 'number', step: 5,    min: 0,  default: 50,   help: 'For a pyramid entry, the current depth ratio must still be ≥ this % of the original imbalance ratio.' },
    ],
  },
  slowButSteady: {
    type: 'slowButSteady',
    name: 'Slow But Steady',
    abbr: 'SBS',
    hideStocks: true,
    description: 'Whole-NSE momentum scanner: every cycle, stocks moved at least the min % from previous close are sorted by volume then depth imbalance, and the top N are entered in the direction of momentum (price vs prev close, matching buy/sell dominance). Each position is then managed independently every few minutes — a rolling window of direction checks can add to the position (if enough checks, including the most recent one, agree with its direction) or reverse it (if enough checks, including the most recent one, agree against it), capped by a max investment per stock. The 30-min scan only ever picks new entries — it never touches existing positions. All positions are force-closed at square-off time.',
    example: [
      'Scan (10:00 AM): 883 stocks filtered to those moved ≥4% from previous close, sorted by volume ↓ then depth imbalance ↓. Top 1 (positions_per_scan = 1): TATAPOWER — price ₹412 > prevClose ₹395 (+4.3%) and buyQty 2.1M > sellQty 0.8M ✓. Q = floor(₹50,000 ÷ ₹412) = 121. BUY 121 shares @ ₹412.',
      'Momentum checks (every 3 min): three checks run on TATAPOWER — window = [same, same, same]. 3/3 ≥ momentum_win_count (2) and the most recent check is also "same" → addition: 121 × qty_multiplier_additions more shares bought, as long as (qty + addition) × price stays under max_investment.',
      'Reversal example: window = [same, opposite, opposite] on a later cycle — 2/3 opposite and the most recent check is also "opposite" → reversal: MARKET SELL of min(121 × qty_multiplier_reversals, current qty), which can only shrink or fully close the position.',
      'Square-off (3:15 PM): all open Slow But Steady positions are closed at MARKET regardless of momentum state.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                   label: 'Timing',                              type: 'section' },
      { key: 'start_time',                        label: 'Start Time',                          type: 'time',   default: '09:15',  help: 'Scanning starts at this time.' },
      { key: 'scan_interval_mins',                label: 'Scan Interval (minutes)',              type: 'number', step: 5,  min: 5,  max: 120, default: 30, help: 'How often the full scan (steps 1-5) re-runs.' },
      { key: 'scan_not_allowed_after',            label: 'Scans Not Allowed After',              type: 'time',   default: '14:00',  help: 'No new full scans after this time. Open positions continue to be managed.' },
      { key: 'square_off_time',                   label: 'Final Square-off Time',               type: 'time',   default: '15:15',  help: 'All open positions are force-closed at MARKET at this time.' },
      { key: '_section_filter',                   label: 'Filter',                               type: 'section' },
      { key: 'min_price_change_pct',               label: 'Min Price Change From Prev Close (%)', type: 'number', step: 0.5, min: 0.5, default: 4, help: 'Only stocks moved at least this % (up or down) from previous day close are eligible.' },
      { key: '_section_sizing',                   label: 'Position Sizing',                     type: 'section' },
      { key: 'first_investment',                  label: 'First Investment (₹)',                type: 'number', step: 5000, min: 1000, default: 50000,  help: 'Rupee value used to size the initial entry. Q = floor(this ÷ entry price).' },
      { key: 'max_investment',                    label: 'Maximum Investment (₹)',              type: 'number', step: 5000, min: 1000, default: 200000, help: 'An addition is only placed if (current qty + addition qty) × price stays at or below this rupee amount.' },
      { key: 'qty_multiplier_additions',          label: 'Quantity Multiplier for Additions',   type: 'number', step: 0.5, min: 0.5, default: 1, help: 'Addition order qty = Q × this multiplier, where Q is the quantity from the original entry.' },
      { key: 'qty_multiplier_reversals',          label: 'Quantity Multiplier for Reversals',   type: 'number', step: 0.5, min: 0.5, default: 1, help: 'Reversal order qty = min(Q × this multiplier, current position qty) — can only shrink or fully close a position, never overshoot into a flip.' },
      { key: 'positions_per_scan',                label: 'Positions Per Scan',                   type: 'number', step: 1,   min: 1,   max: 10, default: 1, help: 'Maximum new entries taken from a single scan cycle.' },
      { key: 'max_positions',                     label: 'Max Positions Allowed',               type: 'number', step: 1,   min: 1,   max: 20, default: 5, help: 'Cap on total simultaneously open positions.' },
      { key: '_section_momentum',                 label: 'Momentum Checks',                     type: 'section' },
      { key: 'momentum_check_interval_mins',      label: 'Momentum Check Interval (min)',       type: 'number', step: 1, min: 1, max: 30, default: 3, help: 'How often (in minutes) each open position\'s direction is re-checked.' },
      { key: 'momentum_confirm_count',            label: 'Checks to Run Before Action',          type: 'number', step: 1, min: 1, max: 10, default: 3, help: 'Total number of momentum checks in one rolling window before a verdict is possible.' },
      { key: 'momentum_win_count',                label: 'Wins Required Out of Those Checks',   type: 'number', step: 1, min: 1, max: 10, default: 2, help: 'How many of those checks must agree (same or opposite direction) to trigger an addition or reversal. Must be ≤ checks to run.' },
      { key: '_section_symbols',                  label: 'Ignored Symbols',                     type: 'section' },
      { key: 'ignore_symbols',                    label: 'Symbols to Ignore (comma-separated)', type: 'text', default: '', help: 'Comma-separated NSE symbols excluded from new-entry scanning, and — if a position in one is already open — from the 3-min momentum loop too (no more additions/reversals). Still force-closed at square-off time. (e.g. IDEA,SUZLON).' },
    ],
  },
  tttt: {
    type: 'tttt',
    name: 'Target The Top Three',
    abbr: 'TTTT',
    hideStocks: true,
    description: 'Reuses the Analysis page\'s own top-50-by-volume list — on each price check, sorts it by |Change from Prev Close| (biggest movers either way, same magnitude convention the Analysis grid itself uses for this column — a big loser qualifies just as well as a big gainer) and enters the top 3 (if not already open) in each one\'s Buy/Sell recommendation. An open position only gets averaged into while its symbol is STILL in the top 3; once it slips out, it\'s left alone (no forced exit) until/unless it re-enters. Always trades MIS, sized off its own Investment Amount and capped by its own Max Position Quantity below — entirely separate from the Analysis page\'s Traded Value/Product Type/Max Position Quantity, which this algo never reads. Also launchable from the Analysis page\'s Autopilot switch (toggle it on for this algo via the ⚡ button below to make it selectable there).',
    example: [
      'Cycle (10:00:00): Top 3 by |Change from Prev Close| are RELIANCE (+3.2%), YESBANK (−3.0%, a big loser qualifies just as well as a big gainer), TATAPOWER (+2.8%) — none currently open. Each gets entered at Investment Amount ÷ price in its own suggested direction: BUY 20×RELIANCE, SELL 340×YESBANK, BUY 27×TATAPOWER.',
      'Cycle (10:00:30): RELIANCE is still top 3 and price fell 0.6% from its last order (≥0.5% threshold) → another BUY 20×RELIANCE added, avg price recalculated. YESBANK fell out of top 3 this cycle (now #4) — left exactly as-is, no more additions, until it re-enters.',
      'Cycle (10:01:00): TATAPOWER\'s live P&L on its position hits +₹520 (≥ Max Profit of ₹500) → squared off entirely. It was still top 3 this cycle, so its slot is NOT backfilled this cycle (deferred one cycle) — only the other 2 top-3 members trade this round.',
      'Cycle (10:01:30): TATAPOWER is still top 3 → a brand new position opens for it (fresh profit tracking, so it does not instantly hit the same ₹500 target again). If a position instead hits Max Loss, re-entry is allowed immediately in that same cycle, not deferred.',
      'Square-off time: every still-open position is force-closed regardless of P&L — including ones that long ago slipped out of the top 3 and were just sitting untouched.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',        label: 'Timing',  type: 'section' },
      { key: 'start_time',             label: 'Start Time', type: 'time', default: '09:20', help: 'Entries/averaging begin at this time. Activated earlier, it just waits; activated after this time has already passed today, it starts on the very next price check.' },
      { key: 'price_check_interval_seconds', label: 'Price Check Interval (sec)', type: 'number', step: 5, min: 5, max: 300, default: 30, help: 'How often — every entry, averaging check, P&L check, and top-3 re-evaluation all happen on this same cadence.' },
      { key: 'square_off_time',        label: 'Square-off Time', type: 'time', default: '15:10', help: 'Every open position is force-closed at this time regardless of P&L, even one long since fallen out of the top 3. Keep this before 15:12 — Kite rejects new MIS orders after that, so a later time just fails and gets released from tracking instead of actually closing the position.' },
      { key: '_section_sizing',        label: 'Position Sizing',  type: 'section' },
      { key: 'investment_amount',      label: 'Investment Amount (₹)', type: 'number', step: 500, min: 500, default: 5000, help: 'Rupee value used to size every entry and every averaging addition. Qty = floor(this ÷ price), fixed per symbol for the day.' },
      { key: 'average_step_pct',       label: 'Averaging Step (%)', type: 'number', step: 0.1, min: 0.1, default: 0.5, help: 'A position still in the top 3 gets another addition once price moves at least this % against its last order, or moves past its very first order\'s price in its favor.' },
      { key: 'max_positions',          label: 'Max Open Positions', type: 'number', step: 1, min: 1, max: 20, default: 10, help: 'Cap on total simultaneously open positions across all rotating top-3 entries.' },
      { key: 'max_position_qty_multiplier', label: 'Max Position Quantity (×)', type: 'number', step: 1, min: 1, max: 100, default: 10, help: 'Per-symbol cap = this × the qty from its first entry. An averaging addition that would push a position past this is skipped. Entirely independent of the Analysis page\'s own Max Position Quantity setting.' },
      { key: '_section_risk',          label: 'Risk Management',  type: 'section' },
      { key: 'max_profit_rupees',      label: 'Max Profit Per Position (₹)', type: 'number', step: 50, min: 50, default: 500, help: 'A position is squared off once its live P&L reaches this. If it\'s still in the top 3 that cycle, its slot is not backfilled until the next cycle.' },
      { key: 'max_loss_rupees',        label: 'Max Loss Per Position (₹)', type: 'number', step: 50, min: 50, default: 500, help: 'A position is squared off once its live loss reaches this. Unlike a profit exit, immediate same-cycle re-entry is allowed if it\'s still in the top 3.' },
    ],
  },
  sps: {
    type: 'sps',
    name: 'Santosham Param Sukham',
    abbr: 'SPS',
    hideStocks: true,
    description: 'Trades the Analysis page\'s own top-50-by-volume list off NIFTY 50\'s direction. A scan runs at Start Time (or on activation, if later), then every Scan Interval. Each scan compares NIFTY 50, and every stock, with its price at the previous scan (no previous scan: previous day\'s close at 09:15, otherwise its 09:15 value). A NIFTY change within ±Min Market Move means the scan does nothing at all. Otherwise: every open position going against BOTH the market and its own stock is reduced by one trade quantity; then the list is sorted by Change from Prev Close (signed, gainers on top) and, in an UP market, the top 4 and the bottom 1 are entered (DOWN market: the bottom 4 and the top 1) — each in its OWN change\'s direction (up today → BUY, down → SELL). A pick SPS already holds is skipped, with no replacement. Every Price Check Interval, each open position is squared off on Max Profit / Max Loss, or — once its P&L has reached the activation level — on falling back to a trailing min-profit floor that locks a % of its P&L and only ratchets up with each new high (same as Dhurandhar, but on the position\'s total P&L including what scan reduces have already booked); otherwise it adds one trade quantity whenever price is at or beyond its last order in its favor, or Add on Move Against — Quantity × trade quantity once it has moved at least Add on Move Against % against it (any add trimmed to fit under Max Position Quantity). Trade quantity = Investment Amount ÷ price, fixed per stock for the session. No new position is opened once today\'s SPS orders (buys and sells combined) reach Maximum Charges × 2000 ÷ Investment Amount. Always trades MIS, entirely separate from the Analysis page\'s own Traded Value/Max Position Quantity. Launchable from the Analysis page\'s Autopilot (toggle it on via the ⚡ button below); switching Autopilot off releases every open SPS position as-is (no orders — manage it in Kite).',
    example: [
      'Scan (09:15): NIFTY 24,100 vs prev close 24,000 (+0.42%) → UP. Top 50 sorted by Change from Prev Close, highest first: A +3.1%, B +2.4%, C +1.8%, D +1.2% … Z −2.9% → BUY A, B, C, D and SELL Z, each ₹10,000 ÷ price.',
      'Scan (09:45): NIFTY 24,110 vs 24,100 (+0.04%) → within ±0.05% → nothing done this scan.',
      'Scan (10:15): NIFTY 24,050 vs 24,110 (−0.25%) → DOWN. Long A is now below its 09:45 price → against the market AND its stock → SELL one trade qty. Long B is above its 09:45 price → left alone. Then the bottom 4 and the top 1 are picked — a stock already held is skipped, not replaced.',
      'Price check (every 10s): long A, last order @ ₹500 → price ₹500 or higher adds one trade qty; ₹495 (−1%) also adds; ₹498 does nothing.',
      'Profit: a position\'s P&L reaches ₹1,000 → floor armed at ₹750 (75%). P&L climbs to ₹1,600 → floor ratchets to ₹1,200. P&L slips back to ₹1,200 → whole position squared off, keeping ~₹1,200. Had it kept rising to ₹2,000 instead, Max Profit would have closed it there. A ₹500 loss closes it too.',
      'Square-off time: everything still open is closed at MARKET.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',              label: 'Timing', type: 'section' },
      { key: 'start_time',                   label: 'Start Time', type: 'time', default: '09:15', help: 'The first scan runs at this time. Activated later, it runs immediately and the schedule counts from that moment.' },
      { key: 'scan_interval_mins',           label: 'Scan Interval (min)', type: 'number', step: 5, min: 5, max: 120, default: 30, help: 'Scans repeat at this interval.' },
      { key: 'last_scan_time',               label: 'Last Scan Time', type: 'time', default: '14:30', help: 'No scan starts after this time. Price checks on open positions continue.' },
      { key: 'price_check_interval_seconds', label: 'Price Check Interval (sec)', type: 'number', step: 5, min: 5, max: 300, default: 10, help: 'How often open positions are checked for adds and Max Profit / Max Loss. Skipped on a tick where a scan runs.' },
      { key: 'square_off_time',              label: 'Square-off Time', type: 'time', default: '15:10', help: 'Every open position is closed at this time. Keep it before 15:12 — Kite rejects new MIS orders after that.' },
      { key: '_section_market',              label: 'Market Direction', type: 'section' },
      { key: 'min_market_move_pct',          label: 'Min Market Move (%)', type: 'number', step: 0.01, min: 0, default: 0.05, help: 'A NIFTY 50 change vs the previous scan within ± this much means that scan does nothing — no reduces, no entries.' },
      { key: '_section_sizing',              label: 'Position Sizing', type: 'section' },
      { key: 'investment_amount',            label: 'Investment Amount (₹)', type: 'number', step: 500, min: 500, default: 10000, help: 'Trade quantity = floor(this ÷ price), computed the first time SPS trades a stock and reused for every entry, add and reduce of it for the rest of the session. A stock priced above this is skipped.' },
      { key: 'max_position_qty_multiplier',  label: 'Max Position Quantity (×)', type: 'number', step: 1, min: 1, max: 200, default: 10, help: 'A position never grows beyond this × its trade quantity. Independent of the Analysis page\'s own setting.' },
      { key: 'max_positions',                label: 'Max Open Positions', type: 'number', step: 1, min: 1, max: 100, default: 20, help: 'Scans stop entering new stocks once this many SPS positions are open.' },
      { key: '_section_price_check',         label: 'Price Check', type: 'section' },
      { key: 'add_opposite_dir_pct',         label: 'Add on Move Against (%)', type: 'number', step: 0.1, min: 0.1, default: 1, help: 'Add to the position once price moves this much against it since its last SPS order (how much: the next field). Any move in its favor (or no move) always adds one trade quantity.' },
      { key: 'add_opposite_qty_multiplier',  label: 'Add on Move Against — Quantity', type: 'select', default: 1,
        options: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map(m => ({ value: m, label: `${m}× trade qty` })),
        help: 'How much an against-move add buys/sells: this × trade quantity, rounded down (minimum 1 share). Trimmed to fit under Max Position Quantity — e.g. at 900 of a 1,000 cap, a 2× (200) add places 100.' },
      { key: '_section_risk',                label: 'Profit & Loss', type: 'section', toggleKey: 'pnl_management_enabled', toggleDefault: true, toggleHelp: 'Switched off, SPS never squares off on Max Profit, Max Loss or the min-profit floor, and Maximum Charges no longer limits new positions.' },
      { key: 'max_profit_rupees',            label: 'Max Profit Per Position (₹)', type: 'number', step: 100, min: 100, default: 2000, help: 'A position is squared off entirely once its P&L reaches this. P&L here (and below) is the position\'s total — booked by scan reduces plus still open.' },
      { key: 'profit_activation_threshold',  label: 'Profit Value to Activate Min Profit (₹)', type: 'number', step: 100, min: 1, default: 1000, help: 'Per position: the trailing min-profit floor only comes into play once that position\'s P&L has reached this amount. Keep it below Max Profit, or the floor never arms.' },
      { key: 'min_profit_ratio',             label: 'Min Profit Value vs Current Profit (%)', type: 'number', step: 5, min: 1, max: 100, default: 75, help: 'Once activated, the floor locks in this % of the position\'s P&L and re-locks higher every time P&L makes a new high — it only ever ratchets up. P&L falling back to the floor squares off the whole position.' },
      { key: 'max_loss_rupees',              label: 'Max Loss Per Position (₹)', type: 'number', step: 50, min: 50, default: 500, help: 'A position is squared off entirely once its loss reaches this.' },
      { key: 'max_charges_rupees',          label: 'Maximum Charges (₹)', type: 'number', step: 100, min: 100, default: 4000, help: 'Limits how many orders SPS places in a day: Max Orders = this × 2000 ÷ Investment Amount (e.g. ₹4,000 × 2000 ÷ ₹25,000 = 320). Once today\'s SPS orders — buys and sells combined — reach that number, no new position is opened. Positions already open keep being managed (adds, reduces, exits).' },
    ],
  },
  ftm: {
    type: 'ftm',
    name: 'Follow The Market',
    abbr: 'FTM',
    hideStocks: true,
    noAutopilot: true,
    description: 'Trades the whole NSE list off NIFTY 50\'s direction, with two alternating scans. A Volume scan (at Start Time, then every Scan Interval) compares NIFTY with the previous Volume scan (the very first one: previous day\'s close at 09:15, otherwise NIFTY\'s 09:15 value), takes the top stocks by volume, and enters the biggest movers WITH the market (3 if Strong, else 1) plus the single biggest mover AGAINST it. A Price scan (Price Scan Offset after each Volume scan) compares NIFTY with the previous Price scan (the very first one: NIFTY\'s 09:15 value), then BUYs the highest-volume stocks among the top gainers and SELLs the highest-volume among the top losers (3 on the side a Strong market is moving, else 1). A NIFTY change of exactly 0% means no orders that scan. A pick is skipped (next one used instead) if FTM already holds it, if its own move from previous close disagrees with the order, or once Max Open Positions is reached. Every order is one trade quantity = Trade Value ÷ price. Between scans, every Price Check Interval, each open position adds one trade quantity on a move in its favor or a bigger move against it (measured from its own last order, capped at Max Position Quantity), and reduces by ONE trade quantity when its profit or loss target is hit. Not available for Autopilot. Deactivating releases every open FTM position as-is (no orders — manage it in Kite) and wipes all cached scan state, so nothing more is placed automatically.',
    example: [
      'Volume scan (09:15): NIFTY 24,100 vs prev close 24,000 (+0.42%) → Strong UP. Top 20 by volume sorted by change from prev close, highest first → BUY 1 trade qty each of the top 3 gainers (₹10,000 ÷ price). Same 20 sorted lowest first → SELL the single biggest loser (skipped for the next one if it is actually up today).',
      'Price scan (09:30): NIFTY 24,110 vs its 09:15 value 24,100 (+0.04%) → Weak UP. Top 20 gainers sorted by volume → BUY the top 1. Top 20 losers sorted by volume → SELL the top 1.',
      'Volume scan (09:45): NIFTY 24,060 vs the 09:15 Volume scan\'s 24,100 (−0.17%) → Weak DOWN → SELL 1 with the market, BUY 1 against it. Any stock FTM already holds is skipped for the next one in the list.',
      'Price check (every 10s): RELIANCE long, last order @ ₹2,450 → price ₹2,455 (+0.2%) adds 1 trade qty; or price ₹2,437 (−0.5%) also adds 1 trade qty — up to 50× trade qty in total.',
      'Targets: a ₹40,000 position at +₹400 hits the profit target (lower of ₹1,000 and 1% = ₹400) → reverse order for ONE trade qty. A loss of max(₹500, 0.5%) does the same. Reduced to zero → closed and not watched until a later scan picks it again.',
      'Square-off time: everything still open is closed at MARKET.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',              label: 'Timing', type: 'section' },
      { key: 'start_time',                   label: 'Start Time', type: 'time', default: '09:15', help: 'The first Volume scan runs at this time. Activated later, it runs immediately and the schedule counts from that moment.' },
      { key: 'scan_interval_mins',           label: 'Scan Interval (min)', type: 'number', step: 5, min: 5, max: 120, default: 30, help: 'Volume scans repeat at this interval; so do Price scans.' },
      { key: 'price_scan_offset_mins',       label: 'Price Scan Offset (min)', type: 'number', step: 1, min: 1, max: 60, default: 15, help: 'Each Price scan runs this long after its Volume scan.' },
      { key: 'last_scan_time',               label: 'Last Scan Time', type: 'time', default: '14:30', help: 'No Volume or Price scan starts after this time. Price checks on open positions continue.' },
      { key: 'price_check_interval_seconds', label: 'Price Check Interval (sec)', type: 'number', step: 5, min: 5, max: 300, default: 10, help: 'How often open positions are checked for adds and profit/loss targets. Skipped on a tick where a scan runs.' },
      { key: 'square_off_time',              label: 'Square-off Time', type: 'time', default: '15:10', help: 'Every open position is closed at this time. Keep it before 15:12 — Kite rejects new MIS orders after that.' },
      { key: '_section_market',              label: 'Market Direction & Picks', type: 'section' },
      { key: 'strong_market_pct',            label: 'Strong Market Threshold (%)', type: 'number', step: 0.05, min: 0.05, default: 0.2, help: 'A NIFTY 50 change vs that scan\'s reference of at least this much (either way) is a Strong market; anything smaller (but not 0) is Weak.' },
      { key: 'top_n',                        label: 'Stocks Per List', type: 'number', step: 1, min: 5, max: 100, default: 20, help: 'Size of each candidate list: top by volume (Volume scan), top gainers / top losers (Price scan).' },
      { key: 'strong_pick_count',            label: 'Picks in a Strong Market', type: 'number', step: 1, min: 1, max: 10, default: 3, help: 'Stocks entered on the side of the market\'s direction when it is Strong.' },
      { key: 'normal_pick_count',            label: 'Picks Otherwise', type: 'number', step: 1, min: 1, max: 10, default: 1, help: 'Stocks entered in every other case (Weak market, and the side against the market).' },
      { key: '_section_sizing',              label: 'Position Sizing', type: 'section' },
      { key: 'trade_value',                  label: 'Trade Value (₹)', type: 'number', step: 1000, min: 1000, default: 10000, help: 'Trade quantity = floor(this ÷ price), fixed per position. Every entry, add and reduce is one trade quantity. A stock priced above this is skipped.' },
      { key: 'max_qty_multiplier',           label: 'Max Position Quantity (×)', type: 'number', step: 1, min: 1, max: 200, default: 50, help: 'A position never grows beyond this × its trade quantity. Independent of the Analysis page\'s own setting.' },
      { key: 'max_positions',                label: 'Max Open Positions', type: 'number', step: 1, min: 1, max: 100, default: 20, help: 'Scans stop entering new stocks once this many FTM positions are open.' },
      { key: '_section_price_check',         label: 'Price Check', type: 'section' },
      { key: 'add_same_dir_pct',             label: 'Add on Move in Favor (%)', type: 'number', step: 0.05, min: 0.05, default: 0.2, help: 'Add one trade quantity once price moves this much in the position\'s favor since its last FTM order.' },
      { key: 'add_opposite_dir_pct',         label: 'Add on Move Against (%)', type: 'number', step: 0.05, min: 0.05, default: 0.5, help: 'Add one trade quantity once price moves this much against the position since its last FTM order.' },
      { key: 'profit_target_rupees',         label: 'Profit Target (₹)', type: 'number', step: 100, min: 100, default: 1000, help: 'Profit target = the LOWER of this and Profit Target % of current investment. Hitting it reduces the position by one trade quantity.' },
      { key: 'profit_target_pct',            label: 'Profit Target (% of investment)', type: 'number', step: 0.1, min: 0.1, default: 1, help: 'Current investment = open quantity × average price.' },
      { key: 'loss_limit_rupees',            label: 'Loss Limit (₹)', type: 'number', step: 100, min: 100, default: 500, help: 'Loss limit = the LARGER of this and Loss Limit % of current investment. Hitting it reduces the position by one trade quantity.' },
      { key: 'loss_limit_pct',               label: 'Loss Limit (% of investment)', type: 'number', step: 0.1, min: 0.1, default: 0.5, help: 'Current investment = open quantity × average price.' },
    ],
  },
  bahubali: {
    type: 'bahubali',
    name: 'Bahubali',
    abbr: 'BAH',
    hideStocks: true,
    description: 'Same NIFTY-direction-gated, gap-to-circuit + volume-sort candidate selection and same per-position price/depth/volume-confirmed add(Q)/reduce(Q/2) loop as Hit Or Miss — but scans repeatedly all day instead of once. Every Rescan Interval, if a position slot is free (below Max Positions) and before Entry Cutoff Time, a fresh scan runs — re-checking on every attempt whether NIFTY has reversed against its own opening bias since market open (not just on the first scan). A reversal delays that scan by Direction Reversal Delay and, because the delay always shifts the NEXT scan\'s due time too, every later scan is pushed back by the same amount. A cycle whose scan was delayed by a detected reversal enters at HALF size — and because every later add/reduce for that position is a fraction of its own entry size, the halving carries through everything that position does afterward. Re-entry eligibility is a toggle: ON blocks a symbol only if SOMEONE (any algo, or a manual position) currently holds it live, checked directly against the broker; OFF falls back to the usual same-day cross-algo lock. Every add/reduce also needs at least Min Price Move % change since that position\'s own last EXECUTED order (not just any tick) before it\'s allowed to fire, on top of the existing book/volume confirmation. Each position also gets a Max Hold Time force-close, skipped once its own min-profit floor has armed — exactly like Fingers Crossed. Because going flat is now a normal, routine part of the day (not "done for the day" like Hit Or Miss), the algo never self-deactivates on its own — it just keeps scanning and managing positions until Entry Cutoff Time / Final Square-off Time. A symbol whose last TWO closed positions both ended via Max Loss or a reduce-to-zero (repeatedly getting stopped out, not a deliberate profit-take/circuit/time/manual exit) is auto-excluded from further scans for the rest of the day — same effect as manually hitting Stop Trade on it. Even when a scan is otherwise due, it only actually filters/sorts/picks if NIFTY has moved at least Min Market Move Since Last Scan % since the last scan that passed this same check — a quiet market means most scans are skipped outright (cheaply — before the expensive whole-universe fetch), and the required move keeps accumulating across skipped cycles rather than resetting. From the trades popup, a "No Reverse" button on any open position tells the algo to hold instead of cutting it when price reverses against its direction without book/volume confirming the move — the "buying the dip" add on a confirmed reversal is unaffected. Scoped to that one position only; a later re-entry into the same symbol starts fresh.',
    example: [
      'Scan #1 (9:15 AM, Rescan Interval 15 min): NIFTY up 0.6% → market direction UP. Max Positions = 5, currently 0 open → 5 free slots. No reversal detected (Start Time is market open, so the check is skipped). Stocks Per Scan = 1 → the top-ranked candidate, RELIANCE, enters full-size: Entry Amount ₹1,00,000 ÷ ₹2,450 ≈ 40 shares. BUY 40×RELIANCE @ ₹2,450 (this becomes its own last-order price too).',
      'Scan #2 (9:30 AM): 1 open position, 4 free slots. NIFTY has since eased back below its own opening level (even though still above prev close) → reversal detected. This scan is delayed 10 minutes (Direction Reversal Delay) instead of entering immediately, AND scan #3\'s due time shifts from 9:45 to 9:50 as a result.',
      'Scan #2, resumed (9:40 AM): direction re-derived fresh — still UP. Since this scan was reversal-delayed, entries are HALF size: ₹1,00,000 ÷ ₹610 ÷ 2 ≈ 82 shares of TATAPOWER instead of 164. BUY 82×TATAPOWER @ ₹610. From here on, every add/reduce for this position is a fraction of 82, not 164.',
      'Re-entry example: BEL was traded and closed earlier today by KTLO. With Allow Re-entry ON, Bahubali can still pick BEL in a later scan as long as no one — KTLO, Bahubali, or a manual trade — currently holds it live. With it OFF, BEL stays locked out to KTLO for the rest of the day, same as every other algo pair.',
      'Min price move gate: RELIANCE\'s price ticks from ₹2,450 to ₹2,452 (its last order price) — book and volume confirm a same-direction add, but the move is only 0.08%, below Min Price Move % (0.2) → held, no order placed. A later tick to ₹2,455 (0.2% from ₹2,450) with the same confirmation → add fires.',
      'Max Hold Time: RELIANCE hasn\'t armed its min-profit floor and hits 30 minutes held → force-closed at MARKET, reason max_hold_exit. Scan #3 (whenever it\'s next due) can then re-fill that freed slot from scratch.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                    label: 'Timing',                                type: 'section' },
      { key: 'start_time',                         label: 'Start Time',                            type: 'time',   default: '09:15', help: 'The very first scan attempt happens at this time; every later scan is spaced out by Rescan Interval from whenever the previous one actually ran (which can shift later if a reversal delayed it).' },
      { key: 'rescan_interval_mins',                label: 'Rescan Interval (min)',                  type: 'number', step: 1, min: 1, default: 15, help: 'How often a fresh scan is attempted, once a free position slot exists. Unlike Hit Or Miss, this algo never stops scanning for the day on its own.' },
      { key: 'entry_cutoff_time',                   label: 'Entry Cutoff Time',                     type: 'time',   default: '14:30', help: 'No NEW scans/entries after this time — leaves enough runway for a fresh position to develop and exit before the final square-off. Existing positions are unaffected and still managed normally.' },
      { key: 'direction_reversal_delay_mins',       label: 'Direction Reversal Delay (min)',        type: 'number', step: 1, min: 0, default: 10, help: 'If NIFTY has moved against its own opening bias since market open (e.g. opened up but has since eased back down, even if still above prev close) at the moment a scan is due, that scan is delayed by this many minutes instead of entering immediately into a weakening/reversing move — and the NEXT scan\'s due time shifts back by the same amount. Market direction is re-checked fresh once the delay elapses, and that scan\'s entries are sized at HALF (see Entry Amount).' },
      { key: 'final_square_off_time',               label: 'Final Square-off Time',                 type: 'time',   default: '15:15', help: 'Any still-open position is force-closed at MARKET at this time if nothing else has triggered yet.' },
      { key: '_section_filter',                     label: 'Selection Filter',                      type: 'section' },
      { key: 'stocks_per_scan',                     label: 'Stocks Per Scan',                       type: 'number', step: 1, min: 1, default: 1, help: 'How many candidates a single scan may enter at once (top-ranked by gap-to-circuit then volume), bounded by however many free slots Max Positions currently allows.' },
      { key: 'max_positions',                       label: 'Max Positions',                          type: 'number', step: 1, min: 1, default: 5, help: 'Maximum number of positions Bahubali may hold at once. No new scan is even attempted while at this cap — a scan is attempted again once a slot frees up.' },
      { key: 'allow_reentry',                        label: 'Allow Re-entry',                         type: 'checkbox', default: true, help: 'ON: a symbol is only skipped if SOMEONE currently holds a live position in it right now (any algo, or a manual trade) — checked directly against the broker each scan. The usual same-day "traded already" lock is bypassed. OFF: falls back to the standard rule every other algo uses — a symbol stays off-limits to other algos for the rest of the day once any algo has traded it, even after that position closes.' },
      { key: 'min_scan_move_pct',                    label: 'Min Market Move Since Last Scan (%)',    type: 'number', step: 0.05, min: 0, default: 0.1, help: 'Even when a scan is otherwise due, filtering/sorting/picking is skipped unless NIFTY has moved at least this % since the last scan that itself passed this check (the very first scan of the day always proceeds). A skipped scan doesn\'t reset the comparison point — the required move keeps accumulating across skipped cycles rather than resetting each time.' },
      { key: 'min_gap_from_prevclose_pct',          label: 'Min Gap From Prev Close (%)',           type: 'number', step: 0.5, min: 0.5, default: 2, help: 'A stock is dropped from consideration if its move from the PREVIOUS TRADING DAY\'s close (in NIFTY\'s direction) is smaller than this % — always the previous day\'s close, on every single scan, never the running/current price.' },
      { key: 'circuit_pool_size',                   label: 'Circuit Pool Size',                     type: 'number', step: 10, min: 1, default: 100, help: 'After sorting eligible stocks by gap-to-circuit descending (furthest from locking first), keep this many before the volume-based final pick.' },
      { key: '_section_sizing',                     label: 'Sizing',                                type: 'section' },
      { key: 'entry_amount',                        label: 'Entry Amount (₹)',                      type: 'number', step: 5000, min: 1000, default: 100000, help: 'Initial entry quantity Q = floor(this ÷ price) for a normal scan, or floor(this ÷ price ÷ 2) for a scan that was delayed by a detected reversal. A confirmed same-direction move adds +Q; a confirmed against-direction move adds +Q/2; an unconfirmed against-direction move cuts −Q/2 — all scaled down together when Q itself was halved.' },
      { key: 'min_price_move_pct',                  label: 'Min Price Move Since Last Order (%)',   type: 'number', step: 0.1, min: 0, default: 0.2, help: 'An add or reduce only fires if price has moved at least this % since this position\'s own last EXECUTED order (not just any tick) — on top of, not instead of, the existing book/volume confirmation.' },
      { key: 'no_reverse',                           label: 'No Reverse',                            type: 'checkbox', default: false, help: 'When ON, no position ever gets cut for an unconfirmed against-direction move (book/volume don\'t back it up) — every position just holds through it instead. The confirmed "buying the dip" add is unaffected. Read fresh every tick, so flipping this applies immediately to every open position, not just future entries. Same effect as clicking "No Reverse" on every position individually from the trades popup — that per-position button still works independently of this.' },
      { key: 'scan_interval_seconds',                label: 'Scan Interval (seconds)',                type: 'number', step: 5, min: 1, default: 10, help: 'How often the price/depth/volume check (add/reduce) runs once a position is open. Each check compares price, order-book depth, and volume traded against the previous scan — a bare price tick without book+volume confirmation is held, not acted on.' },
      { key: 'position_cap_multiplier',              label: 'Position Cap (× Q)',                    type: 'number', step: 1, min: 1, default: 5, help: 'Additions stop once quantity reaches this multiple of Q (that position\'s own, possibly-halved, entry quantity). It\'s a live ceiling — if a later reduction drops qty back below the cap, additions can resume.' },
      { key: '_section_risk',                        label: 'Risk',                                  type: 'section' },
      { key: 'max_hold_mins',                        label: 'Max Hold Time (min)',                    type: 'number', step: 5, min: 1, default: 30, help: 'A position is force-closed at MARKET if held this long. Skipped once that position\'s own Min Profit floor has armed — an armed floor means it already reached a meaningful profit level and is being protected by the floor instead, so the time cutoff shouldn\'t force it closed while it still has room to run.' },
      { key: 'max_loss_rupees',                      label: 'Max Loss (₹)',                          type: 'number', step: 500, min: 1, default: 5000, help: 'Applied per position (not combined across positions): a given stock closes immediately once ITS OWN P&L falls to or below −this amount. Other open positions are unaffected.' },
      { key: 'max_profit_rupees',                    label: 'Max Profit (₹)',                        type: 'number', step: 500, min: 1, default: 15000, help: 'Applied per position: a given stock closes immediately once ITS OWN P&L reaches this amount. Other open positions are unaffected.' },
      { key: 'profit_activation_threshold',          label: 'Profit Value to Activate Min Profit (₹)', type: 'number', step: 500, min: 1, default: 2000, help: 'Per position: the trailing min-profit floor only comes into play once that position\'s OWN P&L has ever reached this amount — before that, there is no floor for it at all.' },
      { key: 'min_profit_ratio',                     label: 'Min Profit Value vs Current Profit (%)', type: 'number', step: 5, min: 1, max: 100, default: 75, help: 'Once activated for a position, its floor locks in this % of whatever that position\'s current profit is, and re-locks higher every time its profit reaches a new high — it only ever ratchets up, never down. Computed purely from that position\'s own entry price, so a new position always starts fresh.' },
      { key: 'square_off_at_circuit',                label: 'Square Off at Circuit',                  type: 'checkbox', default: true, help: 'When enabled, a position is force-closed at MARKET the moment its own price reaches the upper circuit (for a BUY) or lower circuit (for a SELL).' },
      { key: '_section_orders',                      label: 'Order Fallback',                         type: 'section' },
      { key: 'pending_order_timeout_mins',           label: 'Pending Entry/Addition Timeout (min)',   type: 'number', step: 1, min: 1, default: 2, help: 'Every order tries MARKET first, then falls back to a LIMIT order (10% disclosed qty) at the latest price if rejected — never treated as executed until Kite actually confirms the fill. Exits retry indefinitely until filled. The entry and additions are cancelled and abandoned if still unfilled after this many minutes.' },
    ],
  },
  sos: {
    type: 'sos',
    name: 'SOS',
    abbr: 'SOS',
    hideStocks: true,
    description: 'Bahubali\'s sibling with no scanning at all — trades only the stocks YOU pick, via the Watchlist below. Search and add symbols to build a persistent watchlist, then check which of those you want traded this session. At Start Time, EVERY checked stock gets a position unconditionally — direction purely from its own price vs previous close (BUY if up, SELL if down, skipped if flat) — no ranking, no gap filter, no NIFTY-direction gate, no delay. Once open, positions are managed exactly like Bahubali: the same price/depth/volume-confirmed add(Q)/reduce(Q/2) loop, Min Price Move %, Max Hold Time, circuit/final-square-off/max-loss/max-profit/trailing-min-profit-floor, and "No Reverse". There are no rescans, ever — the algo enters once per session and stops. A genuine square-off (Max Profit, Min Profit floor, Max Loss, Final Square-off Time, Circuit, or manually via Square Off/Stop Trade) automatically UNCHECKS every stock in the watchlist, forcing you to consciously reselect before it can trade again — a position that simply reduces itself down to zero through the normal add/reduce loop does NOT count as a square-off and leaves your selections untouched. Deactivating then reactivating SOS is always a full fresh start: whatever is currently checked gets a brand new entry pass, and nothing from the previous session carries over.',
    example: [
      'Setup: you search and add RELIANCE, TCS, and INFY to the Watchlist, then check all three. Start Time 09:15.',
      'Entry (09:15): RELIANCE is up vs prev close → BUY. TCS is down vs prev close → SELL. INFY is unchanged vs prev close → skipped entirely. Entry Amount ₹1,00,000 ÷ price sizes each position, same formula as Bahubali. No further scan happens today.',
      'Management: RELIANCE and TCS are now managed exactly like Bahubali positions — price/book/volume-confirmed adds and reduces, Max Hold Time, the trailing min-profit floor, etc.',
      'Square-off: RELIANCE\'s P&L reaches Max Profit → force-closed, reason max_profit. Because this is a genuine square-off, ALL THREE checkboxes in the Watchlist (RELIANCE, TCS, and even INFY, which was never entered) get unchecked automatically — TCS keeps running untouched, but the next time SOS is reactivated, nothing is pre-selected until you check boxes again.',
      'Contrast: if instead RELIANCE had simply been reduced down to zero organically through the normal add/reduce loop (exitReason "reduce"), the Watchlist checkboxes would NOT be touched at all.',
      'Restart same day: you deactivate SOS, check TCS and INFY only, and reactivate. This is a full fresh session — a brand new entry pass runs immediately (Start Time has already passed) for whatever is now checked, regardless of anything from before.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                    label: 'Timing',                                type: 'section' },
      { key: 'start_time',                         label: 'Start Time',                            type: 'time',   default: '09:15', help: 'The one entry pass happens at this time (or immediately, if the algo is reactivated later than this). Every currently-checked stock enters unconditionally — no filter, no delay.' },
      { key: 'final_square_off_time',               label: 'Final Square-off Time',                 type: 'time',   default: '15:15', help: 'Any still-open position is force-closed at MARKET at this time if nothing else has triggered yet — this counts as a genuine square-off and unchecks the whole watchlist.' },
      { key: '_section_sizing',                     label: 'Sizing',                                type: 'section' },
      { key: 'entry_amount',                        label: 'Entry Amount (₹)',                      type: 'number', step: 5000, min: 1000, default: 100000, help: 'Entry quantity Q = floor(this ÷ price) for every checked stock. A confirmed same-direction move adds +Q; a confirmed against-direction move adds +Q/2; an unconfirmed against-direction move cuts −Q/2.' },
      { key: 'min_price_move_pct',                  label: 'Min Price Move Since Last Order (%)',   type: 'number', step: 0.1, min: 0, default: 0.2, help: 'An add or reduce only fires if price has moved at least this % since this position\'s own last EXECUTED order (not just any tick) — on top of, not instead of, the existing book/volume confirmation.' },
      { key: 'no_reverse',                           label: 'No Reverse',                            type: 'checkbox', default: false, help: 'When ON, no position ever gets cut for an unconfirmed against-direction move (book/volume don\'t back it up) — every position just holds through it instead. Read fresh every tick, so flipping this applies immediately to every open position. Same effect as clicking "No Reverse" on a position individually from the trades popup.' },
      { key: 'scan_interval_seconds',                label: 'Scan Interval (seconds)',                type: 'number', step: 5, min: 1, default: 10, help: 'How often the price/depth/volume check (add/reduce) runs once a position is open.' },
      { key: 'position_cap_multiplier',              label: 'Position Cap (× Q)',                    type: 'number', step: 1, min: 1, default: 5, help: 'Additions stop once quantity reaches this multiple of Q. Live ceiling — a later reduction dropping qty back below the cap lets additions resume.' },
      { key: '_section_risk',                        label: 'Risk',                                  type: 'section' },
      { key: 'max_hold_mins',                        label: 'Max Hold Time (min)',                    type: 'number', step: 5, min: 1, default: 30, help: 'A position is force-closed at MARKET if held this long. Skipped once that position\'s own Min Profit floor has armed. Counts as a genuine square-off.' },
      { key: 'max_loss_rupees',                      label: 'Max Loss (₹)',                          type: 'number', step: 500, min: 1, default: 5000, help: 'Per position: closes immediately once ITS OWN P&L falls to or below −this amount. Counts as a genuine square-off.' },
      { key: 'max_profit_rupees',                    label: 'Max Profit (₹)',                        type: 'number', step: 500, min: 1, default: 15000, help: 'Per position: closes immediately once ITS OWN P&L reaches this amount. Counts as a genuine square-off.' },
      { key: 'profit_activation_threshold',          label: 'Profit Value to Activate Min Profit (₹)', type: 'number', step: 500, min: 1, default: 2000, help: 'Per position: the trailing min-profit floor only comes into play once that position\'s OWN P&L has ever reached this amount.' },
      { key: 'min_profit_ratio',                     label: 'Min Profit Value vs Current Profit (%)', type: 'number', step: 5, min: 1, max: 100, default: 75, help: 'Once activated, the floor locks in this % of the position\'s current profit, ratcheting up only. Breaching it counts as a genuine square-off.' },
      { key: 'square_off_at_circuit',                label: 'Square Off at Circuit',                  type: 'checkbox', default: true, help: 'Force-close at MARKET the moment a position\'s price reaches the upper/lower circuit. Counts as a genuine square-off.' },
      { key: '_section_orders',                      label: 'Order Fallback',                         type: 'section' },
      { key: 'pending_order_timeout_mins',           label: 'Pending Entry/Addition Timeout (min)',   type: 'number', step: 1, min: 1, default: 2, help: 'Every order tries MARKET first, then falls back to a LIMIT order (10% disclosed qty) if rejected. Exits retry indefinitely; entries/additions are cancelled after this many minutes unfilled.' },
    ],
  },
  ddr: {
    type: 'ddr',
    name: 'Dhurandhar',
    abbr: 'DDR',
    hideStocks: true,
    description: 'Starts from Keep The Lights On\'s whole-universe scan (NIFTY-direction on-market/off-market classification, circuit-pool + volume ranking, On/Off-Market Final Count) but ONLY for the very first scan of the day. Every scan after that runs continuously (every Scan Interval seconds, all day — not once) and switches signal entirely: a stock qualifies only when its price sits within High/Low Proximity % of the GAP BETWEEN TODAY\'S OPEN AND its high (bullish) or low (bearish) — not a % of the price itself, so a stock that gapped further has proportionally more room — confirmed by BOTH a volume-delta spike (at least Min Volume % of Peak of that stock\'s own highest per-scan volume burst today) AND book depth favoring that side by at least the Depth Dominance Ratio. Off-market (contrarian) positions are only ever opened on the first scan — every scan after that is on-market only. Picking Max Fresh Stocks per Scan brand-new stocks is NOT guaranteed: qualifying fresh stocks are shortlisted by volume, qualifying "winners" (stocks that already had a position today which closed in profit, Exceptions included once closed) are ALL collected with no separate cap, the two pools are combined and re-ranked by volume together, and only the top N enter — N being however many position slots are actually empty right now, so a lower-volume candidate from either pool can lose out to a higher-volume one from the other. Scan Interval (new candidates) and Verification Interval (existing positions) are separate, independently configurable cadences. Ongoing position management reuses the same signal: matches the position\'s direction → add half its original quantity immediately. An OPPOSITE signal does NOT reduce right away — it opens a rolling confirmation window (Reversal Confirmation Window verifications, the triggering one counts as #1) during which no add or reduce happens at all; once the window completes, if at least Reversal Confirm Count of those verifications were reverse-direction, IT reduces by half — otherwise the reversal wasn\'t confirmed and it adds half instead, same as a normal same-direction signal. Quantity is capped at 5× the original entry size and floored at 0 (a position closes rather than ever flipping sides). Add "Exceptions" below to force specific stocks into the very first scan: direction is decided purely by price vs today\'s own open (up → BUY, down → SELL) — no market-direction check, no volume/depth confirmation, no circuit-gap check, nothing else. Once a position opens for an exception stock, it\'s automatically unchecked (won\'t be force-picked again) but remains eligible for the winner-re-entry pool later, exactly like any other stock, once that position closes.',
    example: [
      'First scan (09:15): NIFTY is UP vs its previous close → market direction UP. Stocks trending WITH that (on-market) and AGAINST it (off-market) are both classified, pooled by circuit-gap, ranked by volume, and the top On-Market Final Count (8) + Off-Market Final Count (2) enter — up to 10 positions, sized at Entry Amount ÷ price each.',
      'First scan, Exceptions: TATASTEEL is checked in Exceptions. It skips classification, pools, and circuit-gap entirely — price ₹3,120 vs today\'s open ₹3,080 → BUY, no other check. Its checkbox unchecks itself the moment the position opens.',
      'Every scan after that: 4 position slots are empty, 3 stocks qualify as "winners" (profitable closes today), and 2 fresh stocks qualify and get shortlisted. All 5 are ranked by volume together — only the top 4 (by volume) actually enter this scan; the 5th stays a candidate for the next scan.',
      'Ongoing management, same direction: an open BUY position sees another bullish signal on its own symbol → adds half the original quantity immediately (capped at 5×).',
      'Ongoing management, reversal: that same position instead sees a bearish signal. It does NOT reduce yet — a 6-verification confirmation window opens. Over the next 6 checks, 4 come back bearish (reverse) and 2 bullish → 4 ≥ Reversal Confirm Count (4), so it reduces by half. Had only 3 of 6 come back bearish, it would have added half instead, treating the reversal as unconfirmed.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                    label: 'Timing',                                type: 'section' },
      { key: 'start_time',                          label: 'Start Time',                             type: 'time',   default: '09:15', help: 'The first scan (KTLO-style classification, both on- and off-market) happens at or after this time — retried every Scan Interval until NIFTY produces a valid, non-flat direction.' },
      { key: 'last_scan_time',                      label: 'Last Scan Time (new entries)',            type: 'time',   default: '12:15', help: 'No NEW positions are opened after this time. Existing positions keep being managed (add/reduce/risk exits) all the way to Final Square-off Time.' },
      { key: 'final_square_off_time',                label: 'Final Square-off Time',                   type: 'time',   default: '14:15', help: 'Any still-open position is force-closed at MARKET at this time if nothing else has triggered yet.' },
      { key: 'scan_interval_seconds',                label: 'Scan Interval (seconds)',                 type: 'number', step: 5, min: 5, default: 10, help: 'How often DDR re-scans the whole NSE universe for new candidates — every scan, all day. Independent of Verification Interval below, which governs existing positions instead.' },
      { key: 'verification_interval_seconds',        label: 'Verification Interval (seconds)',         type: 'number', step: 5, min: 5, default: 10, help: 'How often each OPEN position gets re-checked for add/reduce. Independent of Scan Interval above — you can scan for new candidates less often than you verify existing positions, or vice versa.' },
      { key: '_section_classification',              label: 'First-Scan Classification',               type: 'section' },
      { key: 'min_gap_from_prevclose_pct',           label: 'Min Gap From Prev Close (%)',             type: 'number', step: 0.5, min: 0, default: 2, help: 'First scan only: a stock must have moved at least this % from its own previous close (either direction) to be classified as a candidate at all.' },
      { key: 'on_market_circuit_pool_size',          label: 'On-Market Circuit Pool Size',              type: 'number', step: 10, min: 1, default: 100, help: 'First scan only: on-market candidates are sorted by distance to their own circuit (furthest first) and the top N of those form the pool that gets ranked by volume next.' },
      { key: 'off_market_circuit_pool_size',         label: 'Off-Market Circuit Pool Size',             type: 'number', step: 10, min: 1, default: 50, help: 'Same as the on-market pool size, but for off-market (contrarian) candidates.' },
      { key: 'on_market_final_count',                label: 'On-Market Final Count',                    type: 'number', step: 1, min: 1, default: 8, help: 'First scan only: after circuit-pooling, the top N on-market candidates by volume actually get entered.' },
      { key: 'off_market_final_count',               label: 'Off-Market Final Count',                   type: 'number', step: 1, min: 0, default: 2, help: 'Same as On-Market Final Count, for off-market (contrarian) candidates. Off-market positions are ONLY ever opened on the first scan of the day.' },
      { key: '_section_signal',                       label: 'Ongoing Signal (every scan after the first)', type: 'section' },
      { key: 'nifty_gap_from_open_pct',               label: 'NIFTY Gap From Open (%)',                 type: 'number', step: 0.05, min: 0, default: 0.1, help: 'NIFTY must have moved at least this % from today\'s open for a market direction to be established this scan.' },
      { key: 'nifty_high_low_proximity_pct',          label: 'NIFTY High/Low Proximity (%)',            type: 'number', step: 0.05, min: 0, default: 0.1, help: 'AND NIFTY must be within this % of today\'s high (→ UP) or low (→ DOWN). Neither condition met → no market direction this scan → no new entries (existing positions still managed).' },
      { key: 'price_high_low_proximity_pct',          label: 'Stock High/Low Proximity (% of Open Gap)', type: 'number', step: 5, min: 0, default: 20, help: 'A stock qualifies (bullish or bearish) only when its price is within this % of the GAP BETWEEN TODAY\'S OPEN AND its high (or low) — e.g. 20% means within one-fifth of the open-to-high distance from the high itself. Proportional to how far the stock has already moved, not a flat % of price. Used for both new-candidate selection (fresh + winner pools) and ongoing add/reduce.' },
      { key: 'min_volume_pct_of_peak',                label: 'Min Volume % of Peak',                    type: 'number', step: 5, min: 1, max: 100, default: 60, help: 'The volume traded since the LAST scan (not cumulative) must be at least this % of that stock\'s own highest such per-scan burst so far today.' },
      { key: 'depth_dominance_ratio',                 label: 'Depth Dominance Ratio',                   type: 'number', step: 0.5, min: 1, default: 2, help: 'Book depth on the matching side (buy-side for bullish, sell-side for bearish) must be at least this many times the opposite side.' },
      { key: 'min_gap_from_circuit_pct',              label: 'Min Gap From Circuit (%)',                type: 'number', step: 0.5, min: 0, default: 1, help: 'A candidate is rejected if its gap to the relevant circuit (upper for a buy, lower for a sell) is below this % — applies on every scan, including the first.' },
      { key: 'max_fresh_stocks_per_scan',              label: 'Max Fresh Stocks per Scan',               type: 'number', step: 1, min: 0, default: 2, help: 'Every scan after the first, up to this many qualifying brand-new (never-traded-today) stocks are shortlisted by volume. NOT a guaranteed pick: the shortlist is then combined with every qualifying "winner" (a stock that already had a position today which closed in profit, exceptions included once closed), the combined pool is re-ranked by volume, and only the top N enter — N being however many position slots are actually empty right now. A lower-volume fresh or winner candidate can lose out to a higher-volume one from the other pool.' },
      { key: '_section_sizing',                       label: 'Sizing',                                  type: 'section' },
      { key: 'entry_amount',                          label: 'Entry Amount (₹)',                        type: 'number', step: 5000, min: 1000, default: 100000, help: 'Entry quantity Q = floor(this ÷ price), fixed for the life of the position. Every add/reduce moves in units of Q/2.' },
      { key: 'position_cap_multiplier',               label: 'Position Cap (× Q)',                      type: 'number', step: 1, min: 1, default: 5, help: 'Additions stop once quantity reaches this multiple of Q. Reductions are floored at 0 — a position closes rather than ever flipping to the opposite side.' },
      { key: 'max_positions',                         label: 'Max Concurrent Positions',                type: 'number', step: 1, min: 1, default: 20, help: 'Total open positions across on- and off-market combined. Once at this cap, no new REGULAR-scan entries happen until a position closes and frees a slot. Exceptions/forced stocks always enter regardless of this cap — they can push the count above it.' },
      { key: 'reversal_window_size',                  label: 'Reversal Confirmation Window',            type: 'number', step: 1, min: 1, default: 6, help: 'When an open position sees a signal opposite to its own direction, it does NOT reduce immediately — instead it pauses (no add or reduce at all) for this many verifications (the triggering one counts as #1), tallying how many of them were also reverse-direction.' },
      { key: 'reversal_confirm_count',                label: 'Reversal Confirm Count',                  type: 'number', step: 1, min: 1, default: 4, help: 'Once the Reversal Confirmation Window completes: if at least this many of those verifications were reverse-direction, the position reduces by Q/2. Otherwise the reversal wasn\'t confirmed and it adds Q/2 instead, same as a normal same-direction signal.' },
      { key: '_section_risk',                         label: 'Risk',                                    type: 'section' },
      { key: 'max_loss_rupees',                       label: 'Max Loss (₹)',                            type: 'number', step: 100, min: 1, default: 500, help: 'Per position: closes immediately once ITS OWN P&L falls to or below −this amount.' },
      { key: 'max_profit_rupees',                     label: 'Max Profit (₹)',                          type: 'number', step: 100, min: 1, default: 2000, help: 'Per position: closes immediately once ITS OWN P&L reaches this amount.' },
      { key: 'profit_activation_threshold',           label: 'Profit Value to Activate Min Profit (₹)', type: 'number', step: 100, min: 1, default: 1300, help: 'Per position: the trailing min-profit floor only comes into play once that position\'s OWN P&L has ever reached this amount.' },
      { key: 'min_profit_ratio',                      label: 'Min Profit Value vs Current Profit (%)',  type: 'number', step: 5, min: 1, max: 100, default: 75, help: 'Once activated, the floor locks in this % of the position\'s current profit, ratcheting up only.' },
      { key: 'square_off_at_circuit',                 label: 'Square Off at Circuit',                   type: 'checkbox', default: true, help: 'Force-close at MARKET the moment a position\'s price reaches the upper/lower circuit.' },
      { key: '_section_orders',                       label: 'Order Fallback',                          type: 'section' },
      { key: 'pending_order_timeout_mins',            label: 'Pending Entry/Addition Timeout (min)',    type: 'number', step: 1, min: 1, default: 2, help: 'Every order tries MARKET first, then falls back to a LIMIT order (10% disclosed qty) if rejected. Exits retry indefinitely; entries/additions are cancelled after this many minutes unfilled.' },
    ],
  },
  terminator: {
    type: 'terminator',
    name: 'Terminator',
    abbr: 'TERM',
    hideStocks: true,
    description: 'A single-position-per-day algo — no ongoing candidate scanning, just one pick and then watching it. At Start Time, the ONE-AND-ONLY scan of the day fires (never retried): across the whole NSE universe EXCLUDING every F&O-eligible stock, it keeps only stocks whose (day high − day low) / day high is at least Day High/Low Gap %, AND that are currently trading at least Day Low Recovery % above their own day low. Those survivors are ranked by that gap % descending, the top Top N by Gap are kept, and from JUST those, whichever has recovered the MOST off its own day low is the one actually bought — Entry Amount worth, MARKET-first with LIMIT fallback like every other algo. If nothing qualifies, or activation happens after Accumulate Until Time has already passed, no position is taken that day. The resulting quantity is LOCKED as the base size for the rest of the position\'s life. Every Scan Interval seconds after that: circuit exit, then Square-off Time force-close, then Max Loss %, then Max Profit %, then the trailing min-profit floor (Profit Activation % / Min Profit Value vs Current Profit %) — all four computed against the position\'s own CUMULATIVE invested amount, which grows with every addition. Max Loss % is special: breaching it squares off the position AND DEACTIVATES THE WHOLE ALGO (won\'t self-restart tomorrow until you reactivate it) — every other exit just closes the position, leaving the algo active for the next day. If none of those trigger and it\'s still before Accumulate Until Time, it ADDS the same locked quantity again — regardless of whether the position is currently in profit or a small loss — placed as a straight LIMIT order at the current price (not MARKET-first, unlike every other order in this app). After Accumulate Until Time, no more additions happen — the position just sits there, still fully risk-managed, until Square-off Time.',
    example: [
      'Initial scan (3:05 PM): Of 1,282 non-F&O stocks, 40 have swung ≥9% from day high to day low. Of those, 12 are currently trading ≥2% above their own day low. Ranked by gap % descending, the top 5 are shortlisted — among just those 5, RELAXO has recovered the most off its own low (6.8%) — it\'s the pick. BUY ₹10,000 worth at ₹340 → 29 shares, locked as the base quantity for the rest of the day.',
      'No qualifying stock: if nothing clears both the 9% gap bar and the 2% recovery bar, no position is taken — the algo just sits idle for the rest of the day (still watching for nothing, since there\'s nothing open).',
      'Addition in profit (3:06 PM): RELAXO is now at ₹345, a small profit. Since no risk threshold triggered and it\'s still before 3:15 PM, another 29 shares are added — via a LIMIT order at ₹345, not a market order.',
      'Addition in a small loss (3:07 PM): RELAXO dips to ₹338 — a loss, but well inside Max Loss % (1%). Another 29 shares are still added, same as the profit case — additions don\'t care about direction, only about whether a risk threshold has been breached.',
      'Max Loss breach (any time before square-off): RELAXO\'s cumulative position P&L falls to −1% of its own total invested amount → the whole position is squared off AND the Terminator algo is deactivated — it will NOT auto-restart tomorrow; you have to turn it back on yourself.',
      'End of accumulation (3:15 PM onward): no more additions happen even if RELAXO dips into a small loss again — it just sits there, still watched every Scan Interval for Max Loss/Max Profit/trailing floor, until Square-off Time (3:24 PM) force-closes whatever is left.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                    label: 'Timing',                                 type: 'section' },
      { key: 'start_time',                          label: 'Start Time',                             type: 'time',   default: '15:05', help: 'The one-and-only scan of the day fires on the first tick at/after this time. Never retried — whatever it finds (or doesn\'t) is final for today.' },
      { key: 'accumulate_until_time',                label: 'Accumulate Until Time',                   type: 'time',   default: '15:15', help: 'Additions to the open position only happen up to this time. Also the deadline for the initial scan itself — activating the algo after this has passed does nothing until tomorrow.' },
      { key: 'square_off_time',                      label: 'Square-off Time',                         type: 'time',   default: '15:24', help: 'Any still-open position is force-closed at MARKET at this time if nothing else has triggered yet.' },
      { key: 'scan_interval_seconds',                label: 'Scan Interval (seconds)',                 type: 'number', step: 5, min: 5, default: 20, help: 'How often the open position is re-checked for risk exits and additions. The one-shot initial scan itself is NOT on this cadence — it fires exactly once at Start Time.' },
      { key: '_section_selection',                   label: 'Initial-Scan Selection',                  type: 'section' },
      { key: 'day_high_low_gap_pct',                 label: 'Day High/Low Gap (%)',                    type: 'number', step: 0.5, min: 0, default: 9, help: '(Day High − Day Low) / Day High × 100 must be at least this % — a stock that swung a lot from its high to its low sometime today.' },
      { key: 'day_low_recovery_pct',                 label: 'Day Low Recovery (%)',                    type: 'number', step: 0.5, min: 0, default: 2, help: 'The stock must currently be trading at least this % above its own day low — i.e. it has already started recovering.' },
      { key: 'top_n_by_gap',                         label: 'Top N by Gap',                            type: 'number', step: 1, min: 1, default: 5, help: 'Qualifying stocks are ranked by Day High/Low Gap % descending and the top N are kept. From JUST those N, the one with the highest Day Low Recovery % is the actual buy.' },
      { key: '_section_sizing',                       label: 'Sizing',                                  type: 'section' },
      { key: 'entry_amount',                          label: 'Entry Amount (₹)',                        type: 'number', step: 500, min: 500, default: 10000, help: 'Initial buy size. Quantity = floor(this ÷ price), LOCKED for the life of the position — every addition uses this exact same quantity, never recomputed.' },
      { key: '_section_risk',                         label: 'Risk (% of the position\'s own cumulative invested amount)', type: 'section' },
      { key: 'max_loss_pct',                          label: 'Max Loss (% of invested amount)',         type: 'number', step: 0.1, min: 0.1, default: 1, help: 'Closes the position AND DEACTIVATES THE ALGO once its own P&L falls to or below −(this % × total invested so far). The algo will not auto-restart tomorrow.' },
      { key: 'max_profit_pct',                        label: 'Max Profit (% of invested amount)',       type: 'number', step: 0.1, min: 0.1, default: 3, help: 'Closes the position (algo stays active for tomorrow) once its own P&L reaches this % × total invested so far.' },
      { key: 'profit_activation_pct',                 label: 'Profit Activation (% of invested amount)', type: 'number', step: 0.1, min: 0.1, default: 2, help: 'The trailing min-profit floor only arms once the position\'s own P&L has ever reached this % × total invested so far.' },
      { key: 'min_profit_ratio',                      label: 'Min Profit Value vs Current Profit (%)',  type: 'number', step: 5, min: 1, max: 100, default: 75, help: 'Once activated, the floor locks in this % of the position\'s current profit, ratcheting up only.' },
      { key: 'square_off_at_circuit',                 label: 'Square Off at Circuit',                   type: 'checkbox', default: true, help: 'Force-close at MARKET the moment the position\'s price reaches the upper circuit.' },
      { key: '_section_orders',                       label: 'Order Fallback',                          type: 'section' },
      { key: 'pending_order_timeout_mins',            label: 'Pending Entry Timeout (min)',             type: 'number', step: 1, min: 1, default: 2, help: 'The initial entry order tries MARKET first, then falls back to LIMIT if rejected, and is cancelled after this many minutes unfilled. Additions are always LIMIT orders and are left resting (never time-cancelled) rather than retried.' },
    ],
  },
  jaiMaaLakshmi: {
    type: 'jaiMaaLakshmi',
    name: 'Jai Maa Lakshmi',
    abbr: 'JML',
    hideStocks: true,
    description: 'Three independent, sequential phases in one day — Phase 1 (09:15–10:30), Phase 2 (10:45–12:45), Phase 3 (13:00–14:30) — each with its OWN one-shot scan (never repeated) and its OWN forced square-off at MARKET at the phase\'s end time, regardless of P&L. There is deliberately NO max-loss exit anywhere in this algo. Phase 1 reuses Dhurandhar\'s NIFTY-direction on/off-market classification (Min Gap From Prev Close %, circuit-gap pool → volume rank) but the on/off SPLIT is derived from Positions to be Created (default 10): if NIFTY has moved ≥ NIFTY Gap Threshold % from its own open, split On-Market Split % / remainder (default 80/20); otherwise a fixed 50/50. No reversal, no rescans, no Max Concurrent Positions — the target count is forced. Phase 2 (no NIFTY): a stock qualifies if its day high or day low is ≥ Phase 2 Day Gap % from today\'s open, and price is currently ≥ Phase 2 Pullback % away from that extreme IN THE PULLBACK DIRECTION — a high-extreme pullback → SELL (rides it further down), a low-extreme bounce → BUY (rides it further up) — filtered by book-depth dominance, sorted by volume, top Phase 2 Position Count (default 4) taken. Phase 3 (no NIFTY): same day-gap filter, but then computes T — the retracement extreme reached AFTER the day\'s high/low was set — from today\'s 3-minute candles (only for the small post-filter set, capped at Phase 3 Candle Check Limit). Requires the day extreme to be ≥ Phase 3 Retest % away from T, and price to have bounced back ≥ Phase 3 Bounce % off T TOWARD the original extreme — the OPPOSITE convention from Phase 2 (a high-extreme stock bounces back UP off T → BUY; a low-extreme stock bounces back DOWN off T → SELL). Same depth + volume funnel, top Phase 3 Position Count (default 2) taken. Every position from any phase (₹5,000 entry, or 1 share if price > ₹5,000, → Q shares) is managed identically: every Confirmation Interval seconds, a position in profit gets a flat quantity addition — First Addition Multiplier × Q (default 9) the first time, Addition Multiplier × Q (default 10) every time after — capped at Position Cap Multiplier × Q (default 40, i.e. exactly 4 additions: Q → 10Q → 20Q → 30Q → 40Q). Nothing happens on a loss. Once quantity first reaches Floor Activation Multiplier × Q (default 10 — right after the first addition), a trailing min-profit floor arms: Min Profit Ratio % of current P&L, ratcheting up only. Separately, once P&L ever reaches Max Profit (₹), the floor is additionally, permanently anchored to at least that value — reaching Max Profit does NOT close the position here (unlike every other algo\'s Max Profit field), it only raises the floor\'s permanent minimum. All orders (entry, addition, square-off) are MARKET-first with LIMIT fallback.',
    example: [
      'Phase 1 (09:15): NIFTY is up 0.14% from its own open (≥ 0.1% threshold) → 80/20 split of Positions to Create (10) → 8 on-market, 2 off-market. NIFTY-direction classification + circuit-gap pool + volume rank picks 8 on-market and 2 off-market stocks, ₹5,000 each. RELIANCE (on-market) enters BUY 1 share (price > ₹5,000) — that\'s Q.',
      'Phase 1 accumulation: RELIANCE moves into profit. First confirmation in profit → adds 9×Q. Position is now 10×Q — the min-profit floor arms, locking 50% of current P&L. Next profitable confirmation adds 10×Q more (20×Q total); the floor only ratchets up, never down. A 4th addition (10×Q) brings it to the 40×Q cap — no further additions, only the floor keeps ratcheting until 10:30 AM force-closes whatever is left.',
      'Phase 2 (10:45): TATASTEEL\'s day high is 9% above today\'s open (≥ 8%), and it\'s currently trading 2.5% below that high (≥ 2% pullback) → SELL, riding the pullback further down. Filtered by sell-side depth dominance, ranked by volume — it\'s one of the top 4 picked.',
      'Phase 3 (13:00): ADANIPOWER\'s day high was 8.7% above open (open ₹103 → high ₹112). 3-minute candles show it then dropped to ₹104 after the high (7.1% retest, ≥ 7%) — that\'s T. Now at ₹105.5, it has bounced 1.4% off T (≥ 1%) back toward the original high → BUY (opposite convention from Phase 2 — riding the bounce back, not the pullback). Depth + volume funnel picks it as one of the top 2.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                    label: 'Timing',                                 type: 'section' },
      { key: 'phase1_start_time',                   label: 'Phase 1 Start Time',                     type: 'time',   default: '09:15', help: 'Phase 1\'s one-and-only scan fires on the first tick at/after this time.' },
      { key: 'phase1_end_time',                     label: 'Phase 1 End Time',                        type: 'time',   default: '10:30', help: 'Every Phase 1 position is force-closed at MARKET at this time, regardless of P&L. Also the deadline for Phase 1\'s scan itself.' },
      { key: 'phase2_start_time',                   label: 'Phase 2 Start Time',                      type: 'time',   default: '10:45', help: 'Phase 2\'s one-and-only scan fires on the first tick at/after this time (once Phase 1\'s scan has already happened).' },
      { key: 'phase2_end_time',                     label: 'Phase 2 End Time',                        type: 'time',   default: '12:45', help: 'Every Phase 2 position is force-closed at MARKET at this time, regardless of P&L.' },
      { key: 'phase3_start_time',                   label: 'Phase 3 Start Time',                      type: 'time',   default: '13:00', help: 'Phase 3\'s one-and-only scan fires on the first tick at/after this time (once Phase 2\'s scan has already happened).' },
      { key: 'phase3_end_time',                     label: 'Phase 3 End Time',                        type: 'time',   default: '14:30', help: 'Every Phase 3 position is force-closed at MARKET at this time, regardless of P&L.' },
      { key: 'confirmation_interval_seconds',       label: 'Confirmation Interval (seconds)',         type: 'number', step: 5, min: 5, default: 10, help: 'How often every open position (any phase) is re-checked for the profit-triggered addition and the trailing min-profit floor.' },
      { key: '_section_phase1',                     label: 'Phase 1 — Selection',                     type: 'section' },
      { key: 'positions_to_create',                 label: 'Positions to be Created',                 type: 'number', step: 1, min: 1, default: 10, help: 'Total forced positions for Phase 1, split between on-market and off-market.' },
      { key: 'nifty_gap_threshold_pct',             label: 'NIFTY Gap Threshold (%)',                 type: 'number', step: 0.05, min: 0, default: 0.1, help: 'If NIFTY has moved at least this % from its own open, the on/off split below is used. Otherwise a fixed 50/50 split applies.' },
      { key: 'on_market_split_pct',                 label: 'On-Market Split (%)',                     type: 'number', step: 5, min: 1, max: 99, default: 80, help: 'Applied only when the NIFTY Gap Threshold is met — this % of Positions to be Created goes on-market, the remainder off-market.' },
      { key: 'min_gap_from_prevclose_pct',          label: 'Min Gap From Prev Close (%)',             type: 'number', step: 0.5, min: 0, default: 2, help: 'A stock must have moved at least this % from its own previous close to be classified as a candidate at all.' },
      { key: 'on_market_circuit_pool_size',         label: 'On-Market Circuit Pool Size',              type: 'number', step: 10, min: 1, default: 100, help: 'On-market candidates are sorted by distance to their own circuit (furthest first) and the top N form the pool ranked by volume next.' },
      { key: 'off_market_circuit_pool_size',        label: 'Off-Market Circuit Pool Size',             type: 'number', step: 10, min: 1, default: 50, help: 'Same as the on-market pool size, for off-market (contrarian) candidates.' },
      { key: '_section_phase2',                     label: 'Phase 2 — Selection',                     type: 'section' },
      { key: 'phase2_day_gap_pct',                  label: 'Phase 2 Day Gap (%)',                     type: 'number', step: 0.5, min: 0, default: 8, help: 'A stock qualifies once its day high (or day low) is at least this % away from today\'s open.' },
      { key: 'phase2_pullback_pct',                 label: 'Phase 2 Pullback (%)',                    type: 'number', step: 0.5, min: 0, default: 2, help: 'Price must currently be at least this % away from that day extreme, IN THE PULLBACK DIRECTION — a high-extreme pullback enters SELL, a low-extreme bounce enters BUY.' },
      { key: 'phase2_position_count',               label: 'Phase 2 Position Count',                  type: 'number', step: 1, min: 1, default: 4, help: 'Survivors are ranked by volume descending and the top N enter.' },
      { key: '_section_phase3',                     label: 'Phase 3 — Selection',                     type: 'section' },
      { key: 'phase3_day_gap_pct',                  label: 'Phase 3 Day Gap (%)',                     type: 'number', step: 0.5, min: 0, default: 8, help: 'Same day-gap-from-open filter as Phase 2 — the initial funnel before the historical-candle check.' },
      { key: 'phase3_retest_pct',                   label: 'Phase 3 Retest (%)',                      type: 'number', step: 0.5, min: 0, default: 7, help: 'T (the retracement extreme AFTER the day high/low formed, from 3-minute candles) must be at least this % away from the day extreme.' },
      { key: 'phase3_bounce_pct',                   label: 'Phase 3 Bounce (%)',                      type: 'number', step: 0.5, min: 0, default: 1, help: 'Price must have bounced back at least this % off T, TOWARD the original day extreme — the opposite convention from Phase 2\'s pullback.' },
      { key: 'phase3_position_count',               label: 'Phase 3 Position Count',                  type: 'number', step: 1, min: 1, default: 2, help: 'Survivors are ranked by volume descending and the top N enter.' },
      { key: 'phase3_candle_check_limit',           label: 'Phase 3 Candle Check Limit',               type: 'number', step: 10, min: 1, default: 100, help: 'Safety ceiling on how many day-gap survivors get their 3-minute candles fetched (ranked by gap % first) — keeps the historical-data call volume bounded on highly volatile days.' },
      { key: '_section_sizing',                     label: 'Sizing',                                  type: 'section' },
      { key: 'entry_amount',                        label: 'Entry Amount (₹)',                        type: 'number', step: 500, min: 500, default: 5000, help: 'Entry size for every position in every phase. Quantity Q = floor(this ÷ price) — at least 1 share even if price exceeds this amount.' },
      { key: '_section_accumulation',                label: 'Profit Accumulation',                     type: 'section' },
      { key: 'first_addition_multiplier',           label: 'First Addition Multiplier (× Q)',         type: 'number', step: 1, min: 1, default: 9, help: 'The FIRST profit-triggered addition adds this multiple of Q.' },
      { key: 'addition_multiplier',                 label: 'Addition Multiplier (× Q)',                type: 'number', step: 1, min: 1, default: 10, help: 'Every profit-triggered addition AFTER the first adds this multiple of Q.' },
      { key: 'position_cap_multiplier',             label: 'Position Cap (× Q)',                      type: 'number', step: 1, min: 1, default: 40, help: 'Additions stop once quantity reaches this multiple of Q.' },
      { key: 'floor_activation_multiplier',         label: 'Floor Activation (× Q)',                   type: 'number', step: 1, min: 1, default: 10, help: 'The trailing min-profit floor only arms once quantity first reaches this multiple of Q — right after the first addition, by default.' },
      { key: 'min_profit_ratio',                    label: 'Min Profit Value vs Current Profit (%)',  type: 'number', step: 5, min: 1, max: 100, default: 50, help: 'Once armed, the floor locks in this % of current P&L every confirmation, ratcheting up only.' },
      { key: 'max_profit_rupees',                   label: 'Max Profit (₹)',                          type: 'number', step: 500, min: 1, default: 2000, help: 'Does NOT close the position here. Once P&L ever reaches this amount, the floor is additionally, permanently anchored to at least this value.' },
      { key: '_section_filters',                     label: 'Phase 2 & 3 — Book Filter',               type: 'section' },
      { key: 'depth_dominance_ratio',               label: 'Depth Dominance Ratio',                    type: 'number', step: 0.5, min: 1, default: 2, help: 'Book depth on the entry-direction side must be at least this many times the opposite side.' },
      { key: '_section_orders',                     label: 'Order Fallback',                          type: 'section' },
      { key: 'square_off_at_circuit',               label: 'Square Off at Circuit',                    type: 'checkbox', default: true, help: 'Force-close at MARKET the moment a position\'s price reaches the upper/lower circuit.' },
      { key: 'pending_order_timeout_mins',          label: 'Pending Entry/Addition Timeout (min)',    type: 'number', step: 1, min: 1, default: 2, help: 'Every order tries MARKET first, then falls back to a LIMIT order if rejected. Exits retry indefinitely; entries/additions are cancelled after this many minutes unfilled.' },
    ],
  },
  avg: {
    type: 'avg',
    name: 'Avenger',
    abbr: 'AVG',
    hideStocks: true,
    description: 'Two independent cadences: stock-selection scanning (event-driven, not on a fixed interval) and NIFTY direction monitoring (every NIFTY Check Interval seconds). The FIRST scan, at Start Time: rank the whole NSE universe by |buy qty − sell qty| (book-depth "bid diff") descending, keep the top Bid Diff Pool Size; re-rank that pool by volume descending, keep the top Volume Pool Size; of those, keep only stocks whose gap from their own previous close exceeds Min Gap From Prev Close %; sort the survivors by that gap % ASCENDING (closest to the threshold first) and enter the top Max Concurrent Positions — BUY if price is above prev close, SELL if below. Quantity Q = ceil(Entry Amount ÷ price), at least 1 share, fixed for the position\'s life; the bid-diff value AND dominant side seen at entry is also locked in. Separately, NIFTY\'s direction is established the first tick it differs from its own previous close, and a running "window extreme" is seeded to NIFTY\'s price at that moment — extended favorably every check after. A REVERSAL fires once NIFTY is NIFTY Reversal % away from that extreme, against the established direction. On a confirmed reversal: direction flips, the window extreme resets to NIFTY\'s current price, every OPEN position whose OWN stock has also reversed (current price vs today\'s own first traded price now sits opposite that position\'s original direction) is squared off at market, and a fresh scan fills ONLY the empty slots left — using the SAME pipeline, except the previous-close reference is replaced with the high/low each stock made since the PREVIOUS scan (initial or rescan). This whole cycle — monitor, reverse, squareoff, rescan — repeats all day until Scan Cutoff Time, after which no more scans or NIFTY monitoring happen at all (positions already open keep being fully risk-managed regardless). Independently, every Verification Interval seconds, each open position\'s price is compared to its OWN price at its OWN previous verification check (not entry, not NIFTY): moving further in the position\'s favor adds Add Qty Multiplier × Q immediately; moving against it falls back to the bid-diff snapshot from entry — if the SAME side is still dominant AND at least Bid Diff Reversal Threshold % as strong as it was at entry, it\'s treated the same as a favorable tick (still adds); otherwise the whole position is squared off at market. Additions never push quantity past Position Cap (× Q) — a capped position simply stops adding, it isn\'t forced closed for that alone. Max Profit works differently here than everywhere else: reaching it does NOT close the position — instead the min-profit floor is raised to Max-Profit Ratchet Floor % of the CURRENT profit at that moment (not the ceiling threshold itself), only if that\'s higher than the existing floor, and the ceiling itself grows by Max-Profit Ratchet Growth % for next time, repeating indefinitely as profit climbs. Max Loss, Profit Activation, and Min Profit Value vs Current Profit otherwise work exactly as in Dhurandhar.',
    example: [
      'Initial scan (09:15): of the 100 stocks with the widest buy/sell qty imbalance, the 40 highest by volume are kept. Of those, 12 have moved more than 3% from their own previous close. Sorted by that gap % ascending, the 10 closest to the 3% line enter — BUY for the ones trading above prev close, SELL for the ones below. RELIANCE\'s gap was 3.2% (price above prev close) → BUY, price ₹2,450 → Q = ceil(5000/2450) = 3 shares. Its buy-side depth was 1,200 more than sell-side at that moment — that (1200, BUY_DOMINANT) is locked in as the entry snapshot.',
      'Verifier, same direction: 10 seconds later RELIANCE is at ₹2,455 (up from ₹2,450 at the last check) → adds 3×4 = 12 shares immediately, total 15.',
      'Verifier, opposite but book still confirms: next check RELIANCE dips to ₹2,451 (down from ₹2,455 — opposite tick). Current buy/sell imbalance is still buy-dominant and measures 450 — that\'s 37.5% of the entry\'s 1,200, above the 30% threshold — so it STILL adds 12 more shares (total 27) instead of exiting.',
      'Verifier, real reversal: a later check has price down again AND the current imbalance has flipped to sell-dominant (or is buy-dominant but only 200, under 30% of 1,200) → the whole 27-share position is squared off at market.',
      'NIFTY reversal cascade: NIFTY was UP, ran to a window extreme of 24,850, then drops to 24,801 (0.2% off the extreme) → reversal confirmed, direction flips to DOWN. Of the 6 currently open positions, the 4 that were BUYs and whose own stock price has also fallen below its own day-open are squared off; 2 BUYs whose stocks are still trading above their own day-open are left alone. A fresh scan then fills the 4 freed slots using each stock\'s high/low since the LAST scan (not prev close) as the reference, same 3%-gap-ascending selection.',
      'Max-profit ratchet: a position\'s P&L reaches ₹2,020, past its ₹2,000 ceiling. No square-off — the floor is set to 90% of the CURRENT profit, ₹2,020 × 0.9 = ₹1,818 (if that\'s higher than any existing floor) and the ceiling grows to ₹2,000 × 1.25 = ₹2,500 for next time. If P&L then only reaches ₹2,080 (still under ₹2,500), the ratchet doesn\'t refire — the ordinary Min Profit Value vs Current Profit calc (₹2,080 × 60% = ₹1,248) is lower than the existing ₹1,818 floor, so the floor stays at ₹1,818, never reduced.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                       label: 'Timing',                                  type: 'section' },
      { key: 'start_time',                             label: 'Start Time',                              type: 'time',   default: '09:15', help: 'The one-shot initial scan fires at/after this time.' },
      { key: 'scan_cutoff_time',                       label: 'Scan Cutoff Time',                        type: 'time',   default: '13:30', help: 'No new scans (rescans) and no NIFTY monitoring happen after this time. Existing positions keep being fully managed — verifier, max loss/profit, final square-off — regardless.' },
      { key: 'final_square_off_time',                  label: 'Final Square-off Time',                   type: 'time',   default: '15:15', help: 'Any still-open position is force-closed at MARKET at this time if nothing else has triggered yet.' },
      { key: 'nifty_check_interval_seconds',           label: 'NIFTY Check Interval (seconds)',          type: 'number', step: 5, min: 5, default: 30, help: 'How often NIFTY\'s direction/reversal is checked. A confirmed reversal triggers position squareoffs and a rescan on the spot.' },
      { key: 'verification_interval_seconds',          label: 'Verification Interval (seconds)',         type: 'number', step: 5, min: 5, default: 10, help: 'How often each OPEN position is re-checked for add/squareoff, independent of the NIFTY check cadence above.' },
      { key: '_section_selection',                     label: 'Stock Selection',                         type: 'section' },
      { key: 'bid_diff_pool_size',                     label: 'Bid Diff Pool Size',                      type: 'number', step: 10, min: 1, default: 100, help: 'The whole universe is ranked by |buy qty − sell qty| descending and the top N form the pool that gets ranked by volume next.' },
      { key: 'volume_pool_size',                       label: 'Volume Pool Size',                        type: 'number', step: 5, min: 1, default: 40, help: 'From the bid-diff pool, the top N by volume descending survive to the gap-% filter.' },
      { key: 'min_gap_from_prevclose_pct',             label: 'Min Gap From Prev Close (%)',             type: 'number', step: 0.5, min: 0, default: 3, help: 'Initial scan: gap from the stock\'s own previous close. Rescans: gap from the high/low that stock made since the PREVIOUS scan. Either way, only stocks exceeding this % survive.' },
      { key: 'max_positions',                          label: 'Max Concurrent Positions',                type: 'number', step: 1, min: 1, default: 10, help: 'The initial scan enters exactly this many (the closest-to-threshold gap % survivors, ascending). Every rescan afterward only fills whatever slots are currently empty, never exceeding this total.' },
      { key: '_section_sizing',                        label: 'Sizing',                                  type: 'section' },
      { key: 'entry_amount',                           label: 'Entry Amount (₹)',                        type: 'number', step: 500, min: 500, default: 5000, help: 'Entry quantity Q = ceil(this ÷ price), at least 1 share — fixed for the position\'s life.' },
      { key: 'add_qty_multiplier',                     label: 'Add Qty Multiplier (× Q)',                type: 'number', step: 1, min: 1, default: 4, help: 'Every qualifying verifier tick (favorable, or opposite-but-book-confirmed) adds this multiple of Q.' },
      { key: 'bid_diff_reversal_threshold_pct',        label: 'Bid Diff Reversal Threshold (%)',         type: 'number', step: 5, min: 1, max: 100, default: 30, help: 'On an opposite-direction verifier tick: if the current buy/sell imbalance is still the SAME dominant side as at entry AND at least this % as strong as the entry value, it still adds instead of exiting.' },
      { key: 'nifty_reversal_pct',                     label: 'NIFTY Reversal (%)',                      type: 'number', step: 0.05, min: 0.01, default: 0.2, help: 'A reversal fires once NIFTY is this % away from its running window extreme, against the currently established direction.' },
      { key: 'position_cap_multiplier',                label: 'Position Cap (× Q)',                      type: 'number', step: 1, min: 1, default: 5, help: 'Additions stop once quantity reaches this multiple of Q — the position just holds there until squared off some other way, it is not force-closed purely for being at the cap.' },
      { key: '_section_risk',                          label: 'Risk',                                    type: 'section' },
      { key: 'max_loss_rupees',                        label: 'Max Loss (₹)',                            type: 'number', step: 100, min: 1, default: 500, help: 'Per position: closes immediately once ITS OWN P&L falls to or below −this amount.' },
      { key: 'max_profit_rupees',                      label: 'Max Profit (₹) — starting ceiling',       type: 'number', step: 100, min: 1, default: 2000, help: 'Per position: reaching this does NOT close the position — it ratchets the min-profit floor up and grows this ceiling instead (see the two fields below). The ceiling is tracked per-position from this starting value.' },
      { key: 'max_profit_ratchet_floor_pct',           label: 'Max-Profit Ratchet Floor (%)',            type: 'number', step: 5, min: 1, max: 100, default: 90, help: 'Each time the position\'s profit ceiling is reached, the min-profit floor is raised to this % of the CURRENT profit at that moment — not the ceiling threshold itself — only if that\'s higher than the existing floor.' },
      { key: 'max_profit_ratchet_growth_pct',          label: 'Max-Profit Ratchet Growth (%)',           type: 'number', step: 5, min: 1, default: 25, help: 'Each time the position\'s profit ceiling is reached, the ceiling itself grows by this % for next time — this can repeat indefinitely as profit keeps climbing.' },
      { key: 'profit_activation_threshold',            label: 'Profit Value to Activate Min Profit (₹)', type: 'number', step: 100, min: 1, default: 1300, help: 'Per position: the ordinary trailing min-profit floor (independent of the ratchet above) only comes into play once that position\'s OWN P&L has ever reached this amount.' },
      { key: 'min_profit_ratio',                       label: 'Min Profit Value vs Current Profit (%)',  type: 'number', step: 5, min: 1, max: 100, default: 75, help: 'Once activated, the floor locks in this % of the position\'s current profit, ratcheting up only. Whichever of this and the ratchet floor is higher wins, since both only ever raise the same floor.' },
      { key: 'square_off_at_circuit',                  label: 'Square Off at Circuit',                   type: 'checkbox', default: true, help: 'Force-close at MARKET the moment a position\'s price reaches the upper/lower circuit.' },
      { key: '_section_orders',                        label: 'Order Fallback',                          type: 'section' },
      { key: 'pending_order_timeout_mins',             label: 'Pending Entry/Addition Timeout (min)',    type: 'number', step: 1, min: 1, default: 2, help: 'Every order tries MARKET first, then falls back to a LIMIT order if rejected. Exits retry indefinitely; entries/additions are cancelled after this many minutes unfilled.' },
    ],
  },
  pya: {
    type: 'pya',
    name: 'Pick Your Avenger',
    abbr: 'PYA',
    hideStocks: true,
    description: 'Avenger\'s sibling with no scanning at all — trades only the stocks YOU pick, via the Watchlist below. Search and add symbols to build a persistent watchlist, then check which of those you want traded this session. At Start Time, EVERY checked stock gets a position unconditionally — direction purely from its own price vs previous close (BUY if up, SELL if down, skipped if flat) — no bid-diff pool, no volume pool, no gap % threshold, no ranking, no position-count cap. Quantity Q = ceil(Entry Amount ÷ price), at least 1 share; the bid-diff value AND dominant side seen at entry is also locked in, purely for the ongoing verifier below (it plays no role in selection here). There are no rescans, ever, and NO NIFTY monitoring of any kind — the algo enters once per session and stops; only the verifier and risk exits keep running for the rest of the day. Every Verification Interval seconds, each open position\'s price is compared to its OWN price at its OWN previous verification check: moving further in the position\'s favor adds Add Qty Multiplier × Q immediately; moving against it falls back to the entry-time bid-diff snapshot — if the SAME side is still dominant AND at least Bid Diff Reversal Threshold % as strong as it was at entry, it still adds; otherwise the whole position is squared off at market. Additions never push quantity past Position Cap (× Q). Max Profit works like Avenger\'s: reaching it does NOT close the position — the min-profit floor is raised to Max-Profit Ratchet Floor % of the CURRENT profit (only if higher than the existing floor) and the ceiling grows by Max-Profit Ratchet Growth % for next time, repeating indefinitely. A genuine square-off (Max Loss, Min Profit floor, Final Square-off Time, Circuit, the verifier\'s own square-off, or manually via Square Off/Stop Trade) automatically UNCHECKS every stock in the watchlist, forcing you to consciously reselect before it can trade again — Max Profit never triggers a square-off here, so it never unchecks anything either. Deactivating then reactivating is always a full fresh start: whatever is currently checked gets a brand new entry pass, and nothing from the previous session carries over.',
    example: [
      'Setup: you search and add RELIANCE, TCS, and INFY to the Watchlist, then check all three. Start Time 09:15.',
      'Entry (09:15): RELIANCE is up vs prev close → BUY, price ₹2,450 → Q = ceil(5000/2450) = 3 shares, entry bid-diff (1,200, BUY_DOMINANT) locked in. TCS is down vs prev close → SELL. INFY is unchanged vs prev close → skipped entirely. No further scan happens today, and NIFTY is never watched.',
      'Verifier, same direction: 10 seconds later RELIANCE ticks up again → adds 3×4 = 12 shares immediately.',
      'Verifier, real reversal: a later check has RELIANCE\'s price down AND its current buy/sell imbalance has flipped to sell-dominant (or is still buy-dominant but under 30% of the entry\'s 1,200) → the whole position is squared off at market — a genuine square-off, so RELIANCE, TCS, and INFY all get unchecked.',
      'Restart same day: you deactivate, check TCS and INFY only, and reactivate. This is a full fresh session — a brand new entry pass runs immediately (Start Time has already passed) for whatever is now checked.',
    ],
    requiresSnapshot: false,
    fields: [
      { key: '_section_timing',                       label: 'Timing',                                  type: 'section' },
      { key: 'start_time',                             label: 'Start Time',                              type: 'time',   default: '09:15', help: 'The one entry pass happens at this time (or immediately, if the algo is reactivated later than this). Every currently-checked stock enters unconditionally — no filter, no ranking, no cap.' },
      { key: 'final_square_off_time',                  label: 'Final Square-off Time',                   type: 'time',   default: '15:15', help: 'Any still-open position is force-closed at MARKET at this time if nothing else has triggered yet — this counts as a genuine square-off and unchecks the whole watchlist.' },
      { key: 'verification_interval_seconds',          label: 'Verification Interval (seconds)',         type: 'number', step: 5, min: 5, default: 10, help: 'How often each OPEN position is re-checked for add/squareoff.' },
      { key: '_section_sizing',                        label: 'Sizing',                                  type: 'section' },
      { key: 'entry_amount',                           label: 'Entry Amount (₹)',                        type: 'number', step: 500, min: 500, default: 5000, help: 'Entry quantity Q = ceil(this ÷ price), at least 1 share — fixed for the position\'s life.' },
      { key: 'add_qty_multiplier',                     label: 'Add Qty Multiplier (× Q)',                type: 'number', step: 1, min: 1, default: 4, help: 'Every qualifying verifier tick (favorable, or opposite-but-book-confirmed) adds this multiple of Q.' },
      { key: 'bid_diff_reversal_threshold_pct',        label: 'Bid Diff Reversal Threshold (%)',         type: 'number', step: 5, min: 1, max: 100, default: 30, help: 'On an opposite-direction verifier tick: if the current buy/sell imbalance is still the SAME dominant side as at entry AND at least this % as strong as the entry value, it still adds instead of exiting.' },
      { key: 'position_cap_multiplier',                label: 'Position Cap (× Q)',                      type: 'number', step: 1, min: 1, default: 5, help: 'Additions stop once quantity reaches this multiple of Q — the position just holds there until squared off some other way.' },
      { key: '_section_risk',                          label: 'Risk',                                    type: 'section' },
      { key: 'max_loss_rupees',                        label: 'Max Loss (₹)',                            type: 'number', step: 100, min: 1, default: 500, help: 'Per position: closes immediately once ITS OWN P&L falls to or below −this amount. Counts as a genuine square-off.' },
      { key: 'max_profit_rupees',                      label: 'Max Profit (₹) — starting ceiling',       type: 'number', step: 100, min: 1, default: 2000, help: 'Per position: reaching this does NOT close the position — it ratchets the min-profit floor up and grows this ceiling instead (see the two fields below), and never counts as a square-off.' },
      { key: 'max_profit_ratchet_floor_pct',           label: 'Max-Profit Ratchet Floor (%)',            type: 'number', step: 5, min: 1, max: 100, default: 90, help: 'Each time the position\'s profit ceiling is reached, the min-profit floor is raised to this % of the CURRENT profit at that moment — not the ceiling threshold itself — only if that\'s higher than the existing floor.' },
      { key: 'max_profit_ratchet_growth_pct',          label: 'Max-Profit Ratchet Growth (%)',           type: 'number', step: 5, min: 1, default: 25, help: 'Each time the position\'s profit ceiling is reached, the ceiling itself grows by this % for next time — this can repeat indefinitely as profit keeps climbing.' },
      { key: 'profit_activation_threshold',            label: 'Profit Value to Activate Min Profit (₹)', type: 'number', step: 100, min: 1, default: 1300, help: 'Per position: the ordinary trailing min-profit floor (independent of the ratchet above) only comes into play once that position\'s OWN P&L has ever reached this amount.' },
      { key: 'min_profit_ratio',                       label: 'Min Profit Value vs Current Profit (%)',  type: 'number', step: 5, min: 1, max: 100, default: 75, help: 'Once activated, the floor locks in this % of the position\'s current profit, ratcheting up only. Whichever of this and the ratchet floor is higher wins. Breaching it counts as a genuine square-off.' },
      { key: 'square_off_at_circuit',                  label: 'Square Off at Circuit',                   type: 'checkbox', default: true, help: 'Force-close at MARKET the moment a position\'s price reaches the upper/lower circuit. Counts as a genuine square-off.' },
      { key: '_section_orders',                        label: 'Order Fallback',                          type: 'section' },
      { key: 'pending_order_timeout_mins',             label: 'Pending Entry/Addition Timeout (min)',    type: 'number', step: 1, min: 1, default: 2, help: 'Every order tries MARKET first, then falls back to a LIMIT order if rejected. Exits retry indefinitely; entries/additions are cancelled after this many minutes unfilled.' },
    ],
  },
};

// Legend colors/abbreviations for known strategy types
const ALGO_TYPE_STYLES = {
  orderBookMomentum:   { color: '#2563eb', abbr: 'OBM'  },
  obmVwapHybrid:       { color: '#ec4899', abbr: 'HYB'  },

  slowButSteady:        { color: '#84cc16', abbr: 'SBS'  },
  bahubali:             { color: '#eab308', abbr: 'BAH'  },
  terminator:           { color: '#dc2626', abbr: 'TERM' },
  jaiMaaLakshmi:         { color: '#7c3aed', abbr: 'JML'  },
  avg:                   { color: '#ef4444', abbr: 'AVG'  },
  pya:                   { color: '#06b6d4', abbr: 'PYA'  },
};

// Fallback palette for custom algos — cycles through distinct colors
const ALGO_FALLBACK_COLORS = ['#9c27b0','#0d9488','#6366f1','#0ea5e9','#f97316','#84cc16','#64748b','#e11d48'];

const CONDITION_LABELS = {
  breakout_prev_high: 'Price breaks above previous day high',
  breakout_prev_low:  'Price breaks below previous day low',
};

const DEFAULT_PARAMS = {
  entry: {
    condition:          'breakout_prev_high',
    transaction_type:   'BUY',
    volume_multiplier:  1.5,
    entry_start:        '09:30',
    entry_end:          '12:30',
    price_buffer_pct:   0.1,
    orb_window_minutes: 15,
    // cattle walk
    bid_qty_ratio:      2.0,
    open_price_pct:     0.25,
    imbalance_pct:      50,
    max_positions:      3,
  },
  exit: {
    target_pct:      2.0,
    stop_loss_pct:   1.0,
    trailing_sl:     false,
    trailing_sl_pct: 0.5,
    time_exit:       '15:15',
    rr_ratio:        2.0,
  },
  risk: {
    max_position_value:  50000,
    max_daily_loss:      5000,
    max_open_trades:     3,
    // cattle walk
    order_value:         50000,
    max_invested_value:  150000,
    max_loss_value:      5000,
    max_follow_on:       20,
    follow_on_wait_mins: 5,
  },
};

function calcFixedPos(ref, popupWidth = 240, gap = -10) {
  if (!ref.current) return null;
  const rect = ref.current.getBoundingClientRect();
  let left = rect.left + rect.width / 2;
  left = Math.max(popupWidth / 2 + 8, Math.min(left, window.innerWidth - popupWidth / 2 - 8));
  return { position: 'fixed', top: rect.top + gap, left, transform: 'translate(-50%, -100%)' };
}

function PField({ label, help, children }) {
  const helpRef = useRef();
  const [popupStyle, setPopupStyle] = useState(null);

  return (
    <div className="pfield">
      <span className="pfield-label">
        {label}
        {help && (
          <span
            className="pfield-help"
            ref={helpRef}
            onMouseEnter={() => setPopupStyle(calcFixedPos(helpRef, 240))}
            onMouseLeave={() => setPopupStyle(null)}
          >
            ?
            {popupStyle && <span className="pfield-popup" style={popupStyle}>{help}</span>}
          </span>
        )}
      </span>
      <div className="pfield-control">{children}</div>
    </div>
  );
}

function StocksChip({ symbols, obmAll, hybLabel, oobExtra }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const [downloading, setDownloading] = useState(false);
  const ref = useRef();
  const hideTimer = useRef(null);

  const clearHideTimer = () => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
  };

  const scheduleHide = () => {
    clearHideTimer();
    hideTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const handleChipEnter = () => {
    clearHideTimer();
    const s = calcFixedPos(ref, 290, 4);
    if (s) { setStyle(s); setOpen(true); }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    setDownloading(true);
    try { await downloadNseStocksData(); } catch {}
    setDownloading(false);
  };

  const count = symbols?.length || 0;
  const chipLabel = obmAll ? 'All NSE Stocks'
    : oobExtra ? `All 883${count > 0 ? ` +${count} extra` : ''}`
    : hybLabel || `${count} Stock${count !== 1 ? 's' : ''}`;

  return (
    <span
      ref={ref}
      className={`stocks-chip${open ? ' open' : ''}`}
      onMouseEnter={handleChipEnter}
      onMouseLeave={scheduleHide}
      role="button"
      tabIndex={0}
    >
      {chipLabel}
      {open && (
        <div className="stocks-popout" style={style}
          onMouseEnter={clearHideTimer}
          onMouseLeave={scheduleHide}
        >
          {obmAll ? (
            <>
              <div className="stocks-popout-title">All NSE-Listed Stocks</div>
              <div className="stocks-popout-note">Scans all ~883 NSE stocks automatically — no manual selection needed.</div>
              <button className="stocks-dl-btn" data-mobile-icon="⬇" disabled={downloading} onClick={handleDownload}>
                {downloading ? 'Downloading…' : '⬇ Download depth data (.json)'}
              </button>
            </>
          ) : oobExtra ? (
            <>
              <div className="stocks-popout-title">All 883 NSE Stocks{count > 0 ? ' + Extras' : ''}</div>
              {count === 0
                ? <div className="stocks-popout-note">No extra stocks added. Always scans all ~883 NSE stocks automatically.</div>
                : <div className="stocks-popout-grid">{symbols.map(s => <span key={s} className="stocks-popout-item">{s}</span>)}</div>
              }
              {count > 0 && <div className="stocks-popout-note">Always scans all ~883 NSE stocks automatically, plus these extras.</div>}
            </>
          ) : hybLabel ? (
            <>
              <div className="stocks-popout-title">RANGING Mode Symbols</div>
              {count === 0
                ? <div className="stocks-popout-note">No symbols configured. Used for VWAP reversion on RANGING days.</div>
                : <div className="stocks-popout-grid">{symbols.map(s => <span key={s} className="stocks-popout-item">{s}</span>)}</div>
              }
              <div className="stocks-popout-note">TRENDING days scan all ~883 NSE stocks automatically.</div>
            </>
          ) : (
            <>
              <div className="stocks-popout-title">{count} Stocks</div>
              {count === 0
                ? <div className="stocks-popout-note">No stocks selected.</div>
                : <div className="stocks-popout-grid">{symbols.map(s => <span key={s} className="stocks-popout-item">{s}</span>)}</div>
              }
            </>
          )}
        </div>
      )}
    </span>
  );
}

function AlgoEditor({ algo, onSave, onCancel, stockUsage = {} }) {
  const [stocks, setStocks] = useState([]);
  // extraInput holds free-form symbols not in the configured stock list
  const [extraInput, setExtraInput] = useState('');
  const [form, setForm] = useState({
    name:        algo?.name        || '',
    type:        algo?.type        || 'intraday',
    description: algo?.description || '',
    symbols:     algo?.symbols     || [],
    params:      algo?.params      || DEFAULT_PARAMS,
  });

  useEffect(() => {
    getStockConfig().then(d => {
      const s = d.stocks || [];
      setStocks(s);
      if (!algo) {
        setForm(f => ({ ...f, symbols: s.map(st => st.symbol) }));
      } else {
        // Populate extraInput with symbols not in the configured stock list
        const configSet = new Set(s.map(st => st.symbol));
        const extra = (algo.symbols || []).filter(sym => !configSet.has(sym));
        if (extra.length) setExtraInput(extra.join(', '));
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSymbol = (sym) => {
    setForm(f => ({
      ...f,
      symbols: f.symbols.includes(sym)
        ? f.symbols.filter(s => s !== sym)
        : [...f.symbols, sym]
    }));
  };

  const setParam = (section, key, val) =>
    setForm(f => ({ ...f, params: { ...f.params, [section]: { ...f.params[section], [key]: val } } }));

  const numParam = (section, key, val) =>
    setParam(section, key, isNaN(parseFloat(val)) ? val : parseFloat(val));

  return (
    <div className="algo-editor-popup">
      <div className="editor-header">
        <h3>{algo ? 'Edit Algorithm' : 'New Algorithm'}</h3>
        <button className="editor-close-btn" onClick={onCancel}>✕</button>
      </div>

      <div className="editor-body">
        {/* Name + Description + Type on same line */}
        <div className="editor-inline-row">
          <div className="editor-field editor-field-name">
            <label>Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Algorithm name" />
          </div>
          <div className="editor-field editor-field-grow">
            <label>Description</label>
            <input
              value={form.description}
              title={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Brief description of the strategy"
            />
          </div>
          <div className="editor-field editor-field-fixed">
            <label>Type</label>
            <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
              {ALGO_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
        </div>

        {/* Stocks — horizontal flex-wrap */}
        <div className="editor-stocks-section">
          <div className="editor-stocks-label">
            Stocks to Trade
            {stocks.length > 0 && (
              <button
                className="select-all-btn"
                onClick={() => setForm(f => ({
                  ...f,
                  symbols: f.symbols.length === stocks.length ? [] : stocks.map(s => s.symbol),
                }))}
              >
                {form.symbols.length === stocks.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          {stocks.length === 0 ? (
            <div className="no-stocks">Add stocks in Configuration tab first</div>
          ) : (() => {
            const legendItems = [];
            const seen = new Set();
            stocks.forEach(s => (stockUsage[s.symbol] || []).forEach(item => {
              if (!seen.has(item.abbr)) { seen.add(item.abbr); legendItems.push(item); }
            }));
            return (
              <>
                {legendItems.length > 0 && (
                  <div className="stock-legend">
                    {legendItems.map(item => (
                      <span key={item.abbr} className="legend-item">
                        <span className="legend-dot" style={{ background: item.color }} />
                        {item.abbr} — {item.fullName}
                      </span>
                    ))}
                  </div>
                )}
                <div className="editor-stocks-list">
                  {stocks.map(s => {
                    const usedIn = stockUsage[s.symbol] || [];
                    return (
                      <div key={s.symbol} className="stock-item-wrap">
                        <label className="editor-stock-chip" title={s.name || s.symbol}>
                          <input type="checkbox" checked={form.symbols.includes(s.symbol)} onChange={() => toggleSymbol(s.symbol)} />
                          <span>{s.symbol}</span>
                        </label>
                        {usedIn.length > 0 && (
                          <span className="algo-dots-row">
                            {usedIn.map((item, i) => (
                              <span key={i} className="algo-dot" style={{ background: item.color }} title={item.fullName} />
                            ))}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
          {form.params.entry?.condition === 'opening_range' && (
            <div className="editor-extra-symbols">
              <label className="editor-extra-label">Extra Symbols <span className="editor-extra-hint">(comma-separated, e.g. NIFTYBEES, GOLDBEES — Kite API used for prev-day data)</span></label>
              <input
                className="editor-extra-input"
                type="text"
                value={extraInput}
                onChange={e => setExtraInput(e.target.value)}
                placeholder="e.g. NIFTYBEES, GOLDBEES, BANKBEES"
              />
            </div>
          )}
        </div>

        {/* Entry Conditions — fields vary by trigger type */}
        {(() => {
          const isOrb = form.params.entry.condition === 'opening_range';
          const isCW  = form.params.entry.condition === 'cattle_walk';
          return (
            <div className="param-section">
              <div className="param-section-title">Entry Conditions</div>
              <div className="param-grid">
                <PField label="Trigger" help="Which market condition triggers an entry signal for this algo">
                  <select value={form.params.entry.condition} onChange={e => setParam('entry', 'condition', e.target.value)}>
                    {Object.entries(CONDITION_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </PField>

                {!isOrb && !isCW && (
                  <PField label="Direction" help="Whether to buy (go long) or sell short when the trigger fires">
                    <select value={form.params.entry.transaction_type || 'BUY'} onChange={e => setParam('entry', 'transaction_type', e.target.value)}>
                      <option value="BUY">Buy (Long)</option>
                      <option value="SELL">Sell (Short)</option>
                    </select>
                  </PField>
                )}
                {!isOrb && !isCW && (
                  <PField label="Vol. Multiplier" help="Current volume must be this many times the average to confirm the breakout signal">
                    <input type="number" step="0.1" min="1" value={form.params.entry.volume_multiplier}
                      onChange={e => numParam('entry', 'volume_multiplier', e.target.value)} />
                  </PField>
                )}
                {!isOrb && !isCW && (
                  <PField label="Price Buffer %" help="Price must exceed the reference level by this % before an entry is placed (reduces false signals)">
                    <input type="number" step="0.05" min="0" value={form.params.entry.price_buffer_pct}
                      onChange={e => numParam('entry', 'price_buffer_pct', e.target.value)} />
                  </PField>
                )}

                {isOrb && (
                  <PField label="ORB Window (min)" help="Minutes from 9:15 AM to form the opening range (e.g. 15 → range is 9:15–9:30)">
                    <input type="number" step="5" min="5" max="60" value={form.params.entry.orb_window_minutes ?? 15}
                      onChange={e => numParam('entry', 'orb_window_minutes', e.target.value)} />
                  </PField>
                )}

                {isCW && (
                  <PField label="Bid Qty Ratio" help="Initial entry: one side's bid quantity must be this multiple of the other side (e.g. 2 = sell bids ≥ 2× buy bids)">
                    <input type="number" step="0.1" min="1.1" value={form.params.entry.bid_qty_ratio ?? 2.0}
                      onChange={e => numParam('entry', 'bid_qty_ratio', e.target.value)} />
                  </PField>
                )}
                {isCW && (
                  <PField label="Open Price %" help="Initial entry: price must have moved at least this % away from the 9:15 AM open price">
                    <input type="number" step="0.05" min="0.05" value={form.params.entry.open_price_pct ?? 0.25}
                      onChange={e => numParam('entry', 'open_price_pct', e.target.value)} />
                  </PField>
                )}
                {isCW && (
                  <PField label="Imbalance %" help="Initial filter: the difference between buy and sell bid quantities must exceed this % of the larger side">
                    <input type="number" step="5" min="10" max="95" value={form.params.entry.imbalance_pct ?? 50}
                      onChange={e => numParam('entry', 'imbalance_pct', e.target.value)} />
                  </PField>
                )}
                {isCW && (
                  <PField label="Max Positions" help="Maximum number of symbols that can be in an active cattle walk at the same time">
                    <input type="number" step="1" min="1" max="5" value={form.params.entry.max_positions ?? 3}
                      onChange={e => numParam('entry', 'max_positions', e.target.value)} />
                  </PField>
                )}

                <PField label={isOrb ? 'Entries Start' : 'Entry From'} help={isOrb ? 'Earliest time to look for ORB breakout entries (usually equals ORB window end)' : 'Earliest time today to place a new entry order'}>
                  <input type="time" value={form.params.entry.entry_start} onChange={e => setParam('entry', 'entry_start', e.target.value)} />
                </PField>
                <PField label={isOrb ? 'Entries Until' : 'Entry To'} help="No new entries will be placed after this time">
                  <input type="time" value={form.params.entry.entry_end} onChange={e => setParam('entry', 'entry_end', e.target.value)} />
                </PField>
              </div>
            </div>
          );
        })()}

        {/* Exit Conditions */}
        {(() => {
          const isOrb = form.params.entry.condition === 'opening_range';
          const isCW  = form.params.entry.condition === 'cattle_walk';
          return (
            <div className="param-section">
              <div className="param-section-title">Exit Conditions</div>
              <div className="param-grid">
                {isOrb && (
                  <PField label="R:R Ratio" help="Target profit is this multiple of the initial risk (ORB range). E.g. 2 means target = entry ± 2 × range">
                    <input type="number" step="0.5" min="1" value={form.params.exit.rr_ratio ?? 2.0}
                      onChange={e => numParam('exit', 'rr_ratio', e.target.value)} />
                  </PField>
                )}
                {!isOrb && !isCW && (
                  <PField label="Target %" help="Close the position when price moves this % in your favour">
                    <input type="number" step="0.1" min="0.1" value={form.params.exit.target_pct}
                      onChange={e => numParam('exit', 'target_pct', e.target.value)} />
                  </PField>
                )}
                {!isOrb && !isCW && (
                  <PField label="Stop Loss %" help="Close the position when price moves this % against you">
                    <input type="number" step="0.1" min="0.1" value={form.params.exit.stop_loss_pct}
                      onChange={e => numParam('exit', 'stop_loss_pct', e.target.value)} />
                  </PField>
                )}
                {!isOrb && !isCW && (
                  <PField label="Trailing SL" help="Automatically move the stop-loss higher (for longs) as price rises, locking in gains">
                    <input type="checkbox" checked={form.params.exit.trailing_sl}
                      onChange={e => setParam('exit', 'trailing_sl', e.target.checked)}
                      style={{width:'auto', alignSelf:'flex-start', accentColor:'var(--tab-active)'}} />
                  </PField>
                )}
                {!isOrb && !isCW && form.params.exit.trailing_sl && (
                  <PField label="Trailing %" help="Stop-loss trails the peak price by this %; tighter values lock in more profit but risk early exit">
                    <input type="number" step="0.1" min="0.1" value={form.params.exit.trailing_sl_pct}
                      onChange={e => numParam('exit', 'trailing_sl_pct', e.target.value)} />
                  </PField>
                )}
                <PField label="Time Exit" help="Force-close all open positions at this time regardless of P&L (must be before 3:15 PM)">
                  <input type="time" value={form.params.exit.time_exit} onChange={e => setParam('exit', 'time_exit', e.target.value)} />
                </PField>
              </div>
            </div>
          );
        })()}

        {/* Risk Management */}
        <div className="param-section">
          <div className="param-section-title">Risk Management</div>
          <div className="param-grid">
            {form.params.entry.condition !== 'cattle_walk' && (
              <>
                <PField label="Max Position (₹)" help="Maximum capital to allocate per single trade entry">
                  <input type="number" step="1000" min="1000" value={form.params.risk.max_position_value}
                    onChange={e => numParam('risk', 'max_position_value', e.target.value)} />
                </PField>
                <PField label="Max Daily Loss (₹)" help="Stop taking new trades today once cumulative realised loss reaches this amount (0 = disabled)">
                  <input type="number" step="500" min="0" value={form.params.risk.max_daily_loss ?? 5000}
                    onChange={e => numParam('risk', 'max_daily_loss', e.target.value)} />
                </PField>
                <PField label="Max Open Trades" help="Maximum number of simultaneous open positions for this algo across all configured stocks">
                  <input type="number" step="1" min="1" max="20" value={form.params.risk.max_open_trades ?? 3}
                    onChange={e => numParam('risk', 'max_open_trades', e.target.value)} />
                </PField>
              </>
            )}
            {form.params.entry.condition === 'cattle_walk' && (
              <>
                <PField label="Order Value (₹)" help="Capital deployed per order — both the initial entry and every follow-on step use this amount">
                  <input type="number" step="1000" min="1000" value={form.params.risk.order_value ?? 50000}
                    onChange={e => numParam('risk', 'order_value', e.target.value)} />
                </PField>
                <PField label="Max Invested (₹)" help="Stop placing new orders once total invested capital (all open orders combined) reaches this limit">
                  <input type="number" step="10000" min="10000" value={form.params.risk.max_invested_value ?? 150000}
                    onChange={e => numParam('risk', 'max_invested_value', e.target.value)} />
                </PField>
                <PField label="Max Loss (₹)" help="Close all positions and stop when unrealised loss on this symbol reaches this amount">
                  <input type="number" step="500" min="500" value={form.params.risk.max_loss_value ?? 5000}
                    onChange={e => numParam('risk', 'max_loss_value', e.target.value)} />
                </PField>
                <PField label="Max Follow-on" help="Maximum number of follow-on orders after the initial entry (each waits Follow-on Wait minutes)">
                  <input type="number" step="1" min="1" max="50" value={form.params.risk.max_follow_on ?? 20}
                    onChange={e => numParam('risk', 'max_follow_on', e.target.value)} />
                </PField>
                <PField label="Follow-on Wait (min)" help="Wait this many minutes after the last order before checking the follow-on conditions again">
                  <input type="number" step="1" min="1" max="60" value={form.params.risk.follow_on_wait_mins ?? 5}
                    onChange={e => numParam('risk', 'follow_on_wait_mins', e.target.value)} />
                </PField>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="editor-footer">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => {
          // Merge extra symbols (free-form input) into the symbols list
          const extraSyms = extraInput.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
          const merged    = Array.from(new Set([...form.symbols, ...extraSyms]));
          onSave({ ...form, symbols: merged });
        }}>Save Algorithm</button>
      </div>
    </div>
  );
}

function fmt(n) { return n != null ? Number(n).toLocaleString('en-IN') : '—'; }

function buildAlgoSteps(condition, p) {
  const e = p.entry || {};
  const x = p.exit  || {};
  const r = p.risk  || {};

  if (condition === 'breakout_prev_high') {
    const buf = e.price_buffer_pct || 0;
    const bufText = buf > 0 ? ` + ${buf}% buffer` : '';
    const exQty = Math.floor((r.max_position_value || 50000) / 1420);
    return [
      `Fetches previous trading day's HIGH price for each selected stock via Kite's daily candle API before market open.`,
      `From ${e.entry_start || '09:30'} to ${e.entry_end || '12:30'}, monitors live prices every ~10 sec. Entry fires only when price CROSSES from below to above prev-day high${bufText} (crossover, not just "is above").`,
      `On trigger: places a BUY MIS market order. Qty = ₹${fmt(r.max_position_value || 50000)} ÷ entry price (e.g. prev-high ₹1,420 → qty = ${exQty} shares). Qty is locked for the entire day — the same qty is used for any subsequent orders from this algo.`,
      `Target: +${x.target_pct || 2}% above entry. Stop loss: −${x.stop_loss_pct || 1}% below entry. Whichever triggers first closes the position.`,
      `Force-closes all positions at ${x.time_exit || '15:15'}. Stops new entries once daily loss exceeds ₹${fmt(r.max_daily_loss || 5000)}. Max ${r.max_open_trades || 3} open trades at once.`,
    ];
  }

  if (condition === 'breakout_prev_low') {
    const buf = e.price_buffer_pct || 0;
    const bufText = buf > 0 ? ` − ${buf}% buffer` : '';
    const exQty = Math.floor((r.max_position_value || 50000) / 940);
    return [
      `Fetches previous trading day's LOW price for each selected stock via Kite's daily candle API before market open.`,
      `From ${e.entry_start || '09:30'} to ${e.entry_end || '12:30'}, monitors live prices every ~10 sec. Entry fires only when price CROSSES from above to below prev-day low${bufText} (downward crossover).`,
      `On trigger: places a SELL (short) MIS market order. Qty = ₹${fmt(r.max_position_value || 50000)} ÷ entry price (e.g. prev-low ₹940 → qty = ${exQty} shares). Qty is locked for the day.`,
      `Target: +${x.target_pct || 2}% gain as price continues lower. Stop loss: +${x.stop_loss_pct || 1}% (price moves adversely upward).`,
      `Force-closes all positions at ${x.time_exit || '15:15'}. Stops new entries once daily loss exceeds ₹${fmt(r.max_daily_loss || 5000)}. Max ${r.max_open_trades || 3} open trades at once.`,
    ];
  }

  return [`Strategy type "${condition}" — expand the editor to review parameters.`];
}

function buildStrategySteps(defType, c) {
  const p = c?.params || {};

  if (defType === 'orderBookMomentum') {
    return [
      `At ~3:30 PM the previous trading day, takes a "depth snapshot" of the NSE order book (all 5 bid/ask levels) for all ~883 NSE stocks.`,
      `At ${p.entry_time || '09:30'} next day, compares live depth to the snapshot. Flags stocks where buy-side surged ≥ ${p.long_multiplier || 2}× (→ BUY) or sell-side surged ≥ ${p.short_multiplier || 2}× (→ SELL), AND the opposing side is ≤ ${p.long_short_ratio || 0.5}× the snapshot (one-directional pressure confirmed).`,
      `Waits ${p.depth_confirmation_seconds || 0}s to re-confirm the imbalance isn't a spoof. Then places a MIS order for ₹${fmt(p.max_position_value || 50000)}. Max ${p.max_positions || 3} stocks at once. Stocks outside ₹${fmt(p.min_price || 10)}–₹${fmt(p.max_price || 10000)} price range are skipped.`,
      `Pyramids up to ${p.max_pyramids || 5} more entries every ${p.pyramid_interval_mins || 5} min if depth imbalance still holds (≥ ${p.pyramid_depth_confirm_pct || 50}% of original ratio) and price has moved ≥ ${p.pyramid_min_move_pct ?? 0.5}% in trade direction from last entry.`,
      `After ${p.trailing_duration_mins || 60} min, switches to trailing SL (checks every ${p.trailing_interval_mins || 10} min; instant exit if adverse move > ${p.trailing_trigger_pct || 0.5}%). Force-closes all at ${p.square_off_time || '15:15'}.`,
    ];
  }

  if (defType === 'obmVwapHybrid') {
    return [
      `At ${p.regime_check_time || '09:30'}, checks NIFTY 50's opening range vs its ${p.atr_days || 14}-day ATR. OR/ATR ≥ ${p.regime_threshold || 0.35} → TRENDING day (uses OBM scan). Otherwise → RANGING day (uses VWAP reversion on configured stocks).`,
      `TRENDING mode: scans all ~883 NSE stocks. Flags buy-side surge ≥ ${p.long_multiplier || 2}× or sell-side ≥ ${p.short_multiplier || 2}× vs prev-day 3 PM snapshot, with opposing side ≤ ${p.long_short_ratio || 0.5}×. Up to ${p.max_positions || 3} positions. Price filter: ₹${fmt(p.min_price || 10)}–₹${fmt(p.max_price || 10000)}.`,
      `RANGING mode: watches your configured stocks for VWAP crossovers with volume > ${p.vwap_volume_multiplier || 1.5}× the ${p.vwap_volume_sma_period || 20}-period average. VWAP entries accepted until ${p.vwap_entry_end || '14:00'}.`,
      `Both modes: position size = ₹${fmt(p.max_position_value || 50000)}. SL = ${p.sl_pct || 0.5}%. Pyramids up to ${p.max_pyramids || 3} times every ${p.pyramid_interval_mins || 5} min if signal holds. VWAP band = ${p.vwap_band_k || 2}σ.`,
      `Exits when SL is hit or price exits the VWAP band. Force-closes all at ${p.square_off_time || '15:15'}.`,
    ];
  }

  if (defType === 'neverMatchThisRemoved') {
    const orderVal = fmt(p.order_value || 500000);
    const cutoff   = p.entry_cutoff_time || '14:00';
    const interval = p.scan_interval_mins || 30;
    const baseline = p.baseline_time || '09:15';
    return [
      `First scan at ${baseline}: checks all ~883 NSE stocks for volume + order-book imbalance vs the previous day's close. Records each stock's ${baseline} price as the cycle baseline.`,
      `Every ${interval} min thereafter (until ${cutoff}), re-scans all stocks and compares current price to the previous cycle's snapshot. Both current and previous cycle must agree on depth direction to qualify.`,
      `Signal filter: if "Price Change Since Open" = ${p.price_change_since_open || 0}%, stock must have moved that much from yesterday's close in the signal direction. Top ${p.max_positions_per_scan || 1} qualifying stock(s) per scan entered.`,
      `Entry: ${p.entry_order_type === 'limit' ? 'Limit' : 'Market'} order for ₹${orderVal} (qty = floor(₹${orderVal} ÷ price)). Max ${p.max_positions || 5} concurrent positions.`,
      `Exit: SL at ${p.sl_pct || 0.5}% from entry. Max profit target ${p.max_profit_pct || 2}% (MARKET). Profit-lock trail: every ${p.trail_check_secs || 30}s, SL ratchets to best price ∓ ${p.trail_pct || 0.5}% if above entry. Force-closes all at ${p.square_off_time || '15:15'}.`,
    ];
  }

  if (defType === 'tttt') {
    return [
      `Starting at ${p.start_time || '09:20'} (activated earlier just waits; activated after this has passed today starts on the next check), every ${p.price_check_interval_seconds || 30}s: sorts the Analysis page's own top-50-by-volume list by |Change from Prev Close| (biggest movers either way), takes the top 3.`,
      `Any of those 3 with no open position gets one at ₹${fmt(p.investment_amount || 5000)} in its own Buy/Sell recommendation. A symbol that just hit Max Profit is skipped this cycle even if still top 3 (backfilled next cycle); one that hit Max Loss can re-enter the very same cycle.`,
      `An open position only gets averaged into while its symbol is STILL in the top 3 — another order at the same size once price moves ≥${p.average_step_pct || 0.5}% against its last order, or past its very first order's price in its favor, as long as its quantity would stay within ${p.max_position_qty_multiplier || 10}× its first entry's size. Fell out of the top 3? Left alone, no forced exit.`,
      `Live P&L check, highest priority: ≥₹${fmt(p.max_profit_rupees || 500)} profit or ₹${fmt(p.max_loss_rupees || 500)} loss squares that position off immediately — never averaged into the same cycle it exits.`,
      `Max ${p.max_positions || 10} positions open at once. Everything still open is force-closed at ${p.square_off_time || '15:10'}, regardless of P&L.`,
    ];
  }

  if (defType === 'sps') {
    const pnlOn = p.pnl_management_enabled !== false;
    const addsLine = `price at/beyond the last order in its favor adds one trade qty; ${p.add_opposite_dir_pct || 1}% against it adds ${p.add_opposite_qty_multiplier || 1}× trade qty (position capped at ${p.max_position_qty_multiplier || 10}× trade qty — an add is trimmed to fit).`;
    return [
      `Scan at ${p.start_time || '09:15'} (or on activation, if later), then every ${p.scan_interval_mins || 30} min until ${p.last_scan_time || '14:30'}: NIFTY 50 and each stock vs their previous-scan price (first one: prev close at 09:15, else the 09:15 value). NIFTY within ±${p.min_market_move_pct ?? 0.05}% → nothing done.`,
      'Positions against BOTH the market and their own stock are reduced by one trade qty. Then the Analysis top 50, sorted by Change from Prev Close (gainers on top): UP market → top 4 + bottom 1, DOWN market → bottom 4 + top 1, each in its own change\'s direction. Already held → skipped, not replaced.',
      `Each order = trade qty = ₹${fmt(p.investment_amount || 10000)} ÷ price, fixed per stock for the session. Max ${p.max_positions || 20} positions open at once.`,
      ...(pnlOn ? [
        `Every ${p.price_check_interval_seconds || 10}s: P&L ≥ ₹${fmt(p.max_profit_rupees || 2000)} or ≤ −₹${fmt(p.max_loss_rupees || 500)} squares off the whole position. From ₹${fmt(p.profit_activation_threshold || 1000)} P&L, a min-profit floor locks ${p.min_profit_ratio || 75}% of it, ratcheting up with each new high — P&L back at the floor squares off too. Otherwise, ${addsLine}`,
        `No new position once today's SPS orders (buys + sells) reach ${Math.floor((p.max_charges_rupees || 4000) * 2000 / (p.investment_amount || 10000))} (= ₹${fmt(p.max_charges_rupees || 4000)} Maximum Charges × 2000 ÷ ₹${fmt(p.investment_amount || 10000)}); open positions keep being managed.`,
      ] : [
        `Every ${p.price_check_interval_seconds || 10}s: ${addsLine}`,
        'Profit & Loss section disabled — no Max Profit / Max Loss / min-profit floor square-offs and no Maximum Charges limit on new positions.',
      ]),
      `Everything still open is closed at ${p.square_off_time || '15:10'}. Autopilot off releases open positions as-is in Kite.`,
    ];
  }

  if (defType === 'ftm') {
    return [
      `Volume scan at ${p.start_time || '09:15'} (or on activation, if later), then every ${p.scan_interval_mins || 30} min: NIFTY 50 vs the previous Volume scan (first one: prev close at 09:15, else the 09:15 value). Top ${p.top_n || 20} by volume → ${p.strong_pick_count || 3} (Strong, ≥${p.strong_market_pct || 0.2}%) or ${p.normal_pick_count || 1} (Weak) with the market, plus ${p.normal_pick_count || 1} against it.`,
      `Price scan ${p.price_scan_offset_mins || 15} min after each Volume scan: NIFTY 50 vs the previous Price scan (first one: the 09:15 value). Top ${p.top_n || 20} gainers by volume → BUY, top ${p.top_n || 20} losers by volume → SELL; ${p.strong_pick_count || 3} on the side a Strong market is moving, else ${p.normal_pick_count || 1}. A NIFTY change of exactly 0% → no orders. No scans after ${p.last_scan_time || '14:30'}.`,
      `A pick is skipped for the next stock if already held, moving against the order's direction, or once ${p.max_positions || 20} positions are open. Each order = ₹${fmt(p.trade_value || 10000)} ÷ price.`,
      `Every ${p.price_check_interval_seconds || 10}s: +${p.add_same_dir_pct || 0.2}% in favor or ${p.add_opposite_dir_pct || 0.5}% against since the last order adds one trade qty (max ${p.max_qty_multiplier || 50}×). Profit ≥ min(₹${fmt(p.profit_target_rupees || 1000)}, ${p.profit_target_pct || 1}%) or loss ≥ max(₹${fmt(p.loss_limit_rupees || 500)}, ${p.loss_limit_pct || 0.5}%) reduces by one trade qty.`,
      `Everything still open is closed at ${p.square_off_time || '15:10'}. Deactivating releases open positions as-is in Kite and clears all scan state.`,
    ];
  }

  return [`Strategy type "${defType}" — see configuration for parameters.`];
}

const formatTradeDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function TradesPopoutHeader({ title, dates, currentDate, onChangeDate, pnlSum, chargesSum, onRefresh, refreshing, onClose }) {
  const idx = dates.indexOf(currentDate);
  const canPrev = idx > 0;
  const canNext = idx >= 0 && idx < dates.length - 1;
  const netPnl = pnlSum != null && chargesSum != null ? pnlSum - chargesSum : null;
  return (
    <div className="editor-header trades-popout-header">
      <h3>{title}</h3>
      <div className="trades-date-nav">
        <button className="trades-date-chevron" onClick={() => canPrev && onChangeDate(dates[idx - 1])} disabled={!canPrev} title="Previous day">‹</button>
        <span className="trades-date-label">{formatTradeDate(currentDate)}</span>
        <button className="trades-date-chevron" onClick={() => canNext && onChangeDate(dates[idx + 1])} disabled={!canNext} title="Next day">›</button>
      </div>
      <span className="trades-title-actions">
        {pnlSum != null && (
          <span className="trades-pnl-breakdown">
            <span className={`trades-date-pnl ${pnlSum >= 0 ? 'profit' : 'loss'}`}>
              {pnlSum >= 0 ? '+' : ''}₹{pnlSum.toFixed(0)}
            </span>
            {chargesSum != null && (
              <>
                <span className="charges-label"> − ₹{chargesSum.toFixed(0)}</span>
                <span className={`net-pnl-label ${netPnl >= 0 ? 'profit' : 'loss'}`}> = {netPnl >= 0 ? '+' : ''}₹{netPnl.toFixed(0)}</span>
              </>
            )}
          </span>
        )}
        <button className="trades-refresh-btn" onClick={onRefresh} title="Refresh" disabled={refreshing}>↻</button>
        <button className="editor-close-btn" onClick={onClose}>✕</button>
      </span>
    </div>
  );
}

// Combined positions view across every algo/strategy source, plus whatever's
// left over live at Kite that none of them track ("user created" / manual
// positions). Polls every 10s while open; makes zero server calls once closed.
function formatIndianNumber(n, decimals = 0) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function MyTradesModal({ onClose }) {
  const [dates, setDates] = useState([]);
  const [date, setDate] = useState(null);
  const [data, setData] = useState(null); // { positions, summary }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [squaringOffId, setSquaringOffId] = useState(null);
  const pollRef = useRef(null);

  const fetchDates = async () => {
    try {
      const d = await getMyTradesDates();
      setDates(d.dates || []);
      setDate(curr => curr || (d.dates || [])[(d.dates || []).length - 1] || null);
    } catch { /* keep whatever we had */ }
  };

  const fetchData = async (forDate, silent) => {
    if (!forDate) return;
    if (!silent) setLoading(true);
    try {
      const d = await getMyTrades(forDate);
      setData(d);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDates(); }, []);

  useEffect(() => {
    if (!date) return;
    fetchData(date, false);
    pollRef.current = setInterval(() => fetchData(date, true), 10000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handleRefresh = () => fetchData(date, false);

  const handleSquareOff = async (row) => {
    if (!window.confirm(`Square off ${row.symbol}?`)) return;
    setSquaringOffId(row.id);
    try {
      await squareOffMyTrade({ symbol: row.symbol, tradeId: row.tradeId, strategyType: row.strategyType, sourceFile: row.sourceFile });
      fetchData(date, true);
    } catch (err) {
      alert(err.response?.data?.error || 'Square-off failed');
    } finally {
      setSquaringOffId(null);
    }
  };

  const positions = data?.positions || [];
  const summary   = data?.summary || null;
  const idx = dates.indexOf(date);
  const canPrev = idx > 0;
  const canNext = idx >= 0 && idx < dates.length - 1;

  return (
    <div className="algo-editor-overlay" onClick={onClose}>
      <div className="algo-editor-modal trades-popout-modal" onClick={e => e.stopPropagation()}>
        <div className="editor-header trades-popout-header">
          <h3>My Trades</h3>
          <div className="trades-date-nav">
            <button className="trades-date-chevron" onClick={() => canPrev && setDate(dates[idx - 1])} disabled={!canPrev} title="Previous day">‹</button>
            <span className="trades-date-label">{formatTradeDate(date)}</span>
            <button className="trades-date-chevron" onClick={() => canNext && setDate(dates[idx + 1])} disabled={!canNext} title="Next day">›</button>
          </div>
          <span className="trades-title-actions">
            {summary && (
              <span className="trades-pnl-breakdown">
                <span className={`trades-date-pnl ${summary.netPnl >= 0 ? 'profit' : 'loss'}`}>
                  {summary.netPnl >= 0 ? '+' : ''}{formatIndianNumber(summary.netPnl)}
                  {' '}({summary.netPnl >= 0 ? '+' : ''}{summary.netPnlPct.toFixed(2)}%)
                </span>
              </span>
            )}
            <button className="trades-refresh-btn" onClick={handleRefresh} title="Refresh" disabled={loading}>↻</button>
            <button className="editor-close-btn" onClick={onClose}>✕</button>
          </span>
        </div>
        {error && <div className="algo-error">{error}</div>}
        <div className="editor-body trades-popout-body">
          {loading && !data ? (
            <div className="trade-loading">Loading...</div>
          ) : positions.length === 0 ? (
            <div className="no-trades">No trades on this date</div>
          ) : (
            <div className="simulation-grid-wrap">
              <table className="simulation-grid">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Algo</th>
                    <th>Dir</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Avg</th>
                    <th style={{ textAlign: 'right' }}>Current</th>
                    <th style={{ textAlign: 'right' }}>Invested</th>
                    <th style={{ textAlign: 'right' }}>P&amp;L</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map(p => (
                    <tr key={p.id} className={p.status === 'open' ? '' : (p.pnl >= 0 ? 'profit' : 'loss')}>
                      <td className="symbol">{p.symbol}</td>
                      <td>{p.algoName}</td>
                      <td className={p.direction === 'BUY' ? 'buy' : 'sell'}>{p.direction}</td>
                      <td className="numeric">{p.qty}</td>
                      <td className="price">{formatIndianNumber(p.avgPrice || 0, 2)}</td>
                      <td className="price">
                        {p.status === 'open' && p.currentPrice ? (
                          <span className={p.currentPrice >= p.avgPrice ? 'profit' : 'loss'}>{formatIndianNumber(p.currentPrice, 2)}</span>
                        ) : '—'}
                      </td>
                      <td className="price">{p.status === 'open' && p.invested != null ? formatIndianNumber(p.invested) : '—'}</td>
                      <td className={p.pnl == null ? '' : (p.pnl >= 0 ? 'profit' : 'loss')}>
                        {p.pnl != null ? `${p.pnl >= 0 ? '+' : ''}${formatIndianNumber(p.pnl)}` : '—'}
                      </td>
                      <td>{p.status}{p.status === 'closed' && p.exitReason ? ` (${p.exitReason.replace(/_/g, ' ')})` : ''}</td>
                      <td>
                        {p.status === 'open' && (
                          <button
                            className="oob-squareoff-btn"
                            disabled={squaringOffId === p.id}
                            onClick={() => handleSquareOff(p)}
                          >
                            {squaringOffId === p.id ? '...' : 'Square Off'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AlgoCard({ algo, onToggle, onEdit, onDelete, onFavorite, onLaunchSimulation }) {
  const [expanded, setExpanded] = useState(false);
  const [showTradesPopout, setShowTradesPopout] = useState(false);
  const [popoutDates, setPopoutDates] = useState([]);
  const [popoutDate, setPopoutDate] = useState(null);
  const [popoutTrades, setPopoutTrades] = useState([]);
  const [popoutLoading, setPopoutLoading] = useState(false);
  const p = algo.params || {};

  const openTradesPopout = async () => {
    setShowTradesPopout(true);
    setPopoutLoading(true);
    try {
      const dd = await getTradeDates(algo.id);
      const dates = dd.dates || [];
      setPopoutDates(dates);
      setPopoutDate(dates[dates.length - 1] || null);
    } catch {
      setPopoutDates([]);
      setPopoutDate(null);
    }
  };

  const fetchPopoutTrades = (date, silent = false) => {
    if (!date) { setPopoutTrades([]); setPopoutLoading(false); return; }
    if (!silent) setPopoutLoading(true);
    getTrades(algo.id, date)
      .then(d => setPopoutTrades(d.trades || []))
      .catch(() => {})
      .finally(() => { if (!silent) setPopoutLoading(false); });
  };

  useEffect(() => {
    if (!showTradesPopout) return;
    fetchPopoutTrades(popoutDate);
    const interval = setInterval(() => fetchPopoutTrades(popoutDate, true), 10000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTradesPopout, popoutDate]);

  const closedPopoutTrades = popoutTrades.filter(t => t.status === 'closed');
  const popoutPnl = closedPopoutTrades.length > 0
    ? closedPopoutTrades.reduce((s, t) => s + (t.pnl || 0), 0)
    : null;
  const popoutCharges = closedPopoutTrades.length > 0
    ? closedPopoutTrades.reduce((s, t) => s + calcTradeCharges(t), 0)
    : null;

  const renderTradesList = (list, loading) => {
    if (loading) return <div className="trade-loading">Loading...</div>;
    if (list.length === 0) return <div className="no-trades">No trades yet</div>;
    return (
      <div className="trades-list">
        {list.map(t => (
          <div key={t.id} className={`trade-item ${t.status}`}>
            <div className="trade-symbol">{t.symbol}</div>
            <div className="trade-info">
              <span className="trade-entry">Entry: ₹{t.entryPrice.toFixed(2)}</span>
              {t.status === 'closed' && (
                <span className={`trade-pnl ${t.pnl >= 0 ? 'profit' : 'loss'}`}>
                  {t.pnl >= 0 ? '+' : ''}₹{t.pnl?.toFixed(0)} ({t.pnlPct?.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`algo-card ${algo.active ? 'active' : ''}`}>
      <div className="algo-card-row">
        <button className="algo-chevron" onClick={() => setExpanded(e => !e)}>
          {expanded ? '▾' : '▸'}
        </button>
        <div className="algo-info" onClick={() => setExpanded(e => !e)}>
          <span className="algo-name-text">{algo.name}</span>
          {algo.isDefault && <span className="default-badge">Default</span>}
          <span className={`type-badge type-${algo.type}`}>{algo.type}</span>
          <StocksChip symbols={algo.symbols || []} />
        </div>
        <div className="algo-controls">
          {!HideAlgoBacktestIcons && (
            <button className="backtest-icon-btn" onClick={() => onLaunchSimulation && onLaunchSimulation({ kind: 'algo', algo })} title="Backtest">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="8" width="2" height="5" rx="1" fill="currentColor"/><rect x="4.5" y="5" width="2" height="8" rx="1" fill="currentColor"/><rect x="8" y="2" width="2" height="11" rx="1" fill="currentColor"/><rect x="11.5" y="6" width="2" height="7" rx="1" fill="currentColor"/></svg>
            </button>
          )}
          <button
            className={`fav-btn${algo.favorite ? ' fav-btn--on' : ''}`}
            onClick={() => onFavorite(algo)}
            title={algo.favorite ? 'Remove from favorites' : 'Add to favorites (auto-activates on login)'}
          >★</button>
          <button className="trades-btn" onClick={openTradesPopout} title="Show trades">⛶</button>
          <label className="active-toggle" title={algo.active ? 'Deactivate' : 'Activate'}>
            <input type="checkbox" checked={algo.active} onChange={() => onToggle(algo)} />
            <span className="slider" />
          </label>
          <button className="edit-btn" onClick={() => onEdit(algo)} title="Edit">✎</button>
          <button className="del-btn" onClick={() => onDelete(algo.id)} title="Delete">✕</button>
        </div>
      </div>

      {expanded && p.entry && (
        <div className="algo-details">
          <div className="how-it-works-grid">
            <div className="how-it-works-left">
              <div className="detail-title">How It Works</div>
              <ol className="algo-steps">
                {buildAlgoSteps(p.entry.condition, p).map((step, i) => (
                  <li key={i} className="algo-step">{step}</li>
                ))}
              </ol>
            </div>
            <div className="how-it-works-right">
              <div className="detail-title">Example</div>
              <p className="how-it-works-example-text">Example varies based on your configured parameters and entry condition.</p>
            </div>
          </div>
        </div>
      )}
      {showTradesPopout && (
        <div className="algo-editor-overlay" onClick={() => setShowTradesPopout(false)}>
          <div className="algo-editor-modal trades-popout-modal" onClick={e => e.stopPropagation()}>
            <TradesPopoutHeader
              title={`${algo.name} — Trades`}
              dates={popoutDates}
              currentDate={popoutDate}
              onChangeDate={setPopoutDate}
              pnlSum={popoutPnl}
              chargesSum={popoutCharges}
              onRefresh={() => fetchPopoutTrades(popoutDate)}
              refreshing={popoutLoading}
              onClose={() => setShowTradesPopout(false)}
            />
            <div className="editor-body trades-popout-body">
              {renderTradesList(popoutTrades, popoutLoading)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SOSStockPicker({ form, setForm, label = 'Watchlist' }) {
  const [query, setQuery]             = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected]       = useState(null);
  const suggestRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  const watchlist = form.watchlist || [];

  useEffect(() => {
    if (suggestions.length > 0 && suggestRef.current) {
      const r = suggestRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    } else {
      setDropdownRect(null);
    }
  }, [suggestions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = (e) => { if (suggestRef.current && !suggestRef.current.contains(e.target)) setSuggestions([]); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleSearch = async (val) => {
    setQuery(val);
    setSelected(null);
    if (!val.length) { setSuggestions([]); return; }
    try {
      const response = await fetch('/instruments.json');
      const data = await response.json();
      const instruments = data.instruments || [];
      const searchVal = val.toUpperCase();
      const matches = instruments.filter(i =>
        i.symbol.includes(searchVal) || i.name.toUpperCase().includes(searchVal)
      );
      setSuggestions(matches.slice(0, 10));
    } catch (err) {
      console.error('Error loading instruments:', err);
      setSuggestions([]);
    }
  };

  const handleSelect = (instr) => {
    setSelected(instr);
    setQuery(`${instr.symbol} (${instr.exchange})`);
    setSuggestions([]);
  };

  const handleAdd = () => {
    if (!selected) return;
    if (watchlist.includes(selected.symbol)) { setQuery(''); setSelected(null); return; }
    setForm(f => ({
      ...f,
      watchlist: [...(f.watchlist || []), selected.symbol],
      symbols: f.symbols.includes(selected.symbol) ? f.symbols : [...f.symbols, selected.symbol],
    }));
    setQuery('');
    setSelected(null);
  };

  const toggleChecked = (sym) => setForm(f => ({
    ...f,
    symbols: f.symbols.includes(sym) ? f.symbols.filter(s => s !== sym) : [...f.symbols, sym],
  }));

  const removeStock = (sym) => setForm(f => ({
    ...f,
    watchlist: (f.watchlist || []).filter(s => s !== sym),
    symbols: f.symbols.filter(s => s !== sym),
  }));

  return (
    <div className="editor-stocks-section">
      <div className="editor-stocks-label">
        {label}
        {watchlist.length > 0 && (
          <button
            className="select-all-btn"
            onClick={() => setForm(f => ({
              ...f,
              symbols: f.symbols.length === watchlist.length ? [] : [...watchlist],
            }))}
          >
            {form.symbols.length === watchlist.length ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      <div className="config-toolbar">
        <div className="search-wrap" ref={suggestRef}>
          <input
            className="stock-search-input"
            placeholder="Search NSE symbol or company name…"
            value={query}
            onChange={e => handleSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && suggestions.length > 0 && handleSelect(suggestions[0])}
          />
          {suggestions.length > 0 && (
            <div
              className="suggestions-dropdown"
              style={dropdownRect ? {
                position: 'fixed',
                top:   dropdownRect.top,
                left:  dropdownRect.left,
                width: dropdownRect.width,
                zIndex: 9999,
              } : undefined}
            >
              {suggestions.map((s, idx) => (
                <div key={`${s.symbol}-${s.exchange}-${idx}`} className="suggestion-item" onClick={() => handleSelect(s)}>
                  <span className="sug-symbol">{s.symbol}</span>
                  <span className="sug-name">{s.name}</span>
                  <span className="sug-type">{s.exchange}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button className="add-stock-btn" onClick={handleAdd} disabled={!selected}>+ Add Stock</button>
      </div>

      {watchlist.length === 0 ? (
        <div className="no-stocks">No stocks added yet — search and add above.</div>
      ) : (
        <div className="editor-stocks-list">
          {watchlist.map(sym => (
            <div key={sym} className="stock-item-wrap">
              <label className="editor-stock-chip" title={sym}>
                <input type="checkbox" checked={form.symbols.includes(sym)} onChange={() => toggleChecked(sym)} />
                <span>{sym}</span>
              </label>
              <button type="button" className="sos-chip-remove" title="Remove from watchlist" onClick={() => removeStock(sym)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StrategyEditor({ def, config, onSave, onCancel, stockUsage = {} }) {
  const [stocks, setStocks] = useState([]);
  const defaultParams = {};
  def.fields.forEach(f => {
    if (f.type === 'section') {
      // A section with toggleKey has its own on/off switch, saved as that param.
      if (f.toggleKey) defaultParams[f.toggleKey] = config?.params?.[f.toggleKey] ?? f.toggleDefault ?? true;
      return;
    }
    defaultParams[f.key] = config?.params?.[f.key] ?? f.default ?? '';
  });

  const [form, setForm] = useState({
    symbols: config?.symbols || [],
    watchlist: config?.watchlist || [],
    params: { ...defaultParams },
  });

  useEffect(() => {
    getStockConfig().then(d => setStocks(d.stocks || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSymbol = sym => setForm(f => ({
    ...f,
    symbols: f.symbols.includes(sym) ? f.symbols.filter(s => s !== sym) : [...f.symbols, sym],
  }));

  const setParam = (key, val) => setForm(f => ({ ...f, params: { ...f.params, [key]: val } }));
  const numParam = (key, val) => setParam(key, isNaN(parseFloat(val)) ? val : parseFloat(val));

  return (
    <div className="algo-editor-popup">
      <div className="editor-header">
        <h3>Configure: {def.name}</h3>
        <button className="editor-close-btn" onClick={onCancel}>✕</button>
      </div>
      <div className="editor-body">
        <p className="strategy-editor-desc">{def.description}</p>

        {def.type === 'sos' && <SOSStockPicker form={form} setForm={setForm} label="Watchlist" />}
        {def.type === 'ddr' && <SOSStockPicker form={form} setForm={setForm} label="Exceptions" />}
        {def.type === 'pya' && <SOSStockPicker form={form} setForm={setForm} label="Watchlist" />}

        {def.type !== 'orderBookMomentum' && !def.hideStocks && (
          <div className="editor-stocks-section">
            <div className="editor-stocks-label">
              Stocks to Trade
              {def.type === 'obmVwapHybrid' && <span className="editor-stocks-hint"> (RANGING/VWAP mode only — TRENDING mode scans all 883 NSE stocks automatically)</span>}
              {stocks.length > 0 && (
                <button
                  className="select-all-btn"
                  onClick={() => setForm(f => ({
                    ...f,
                    symbols: f.symbols.length === stocks.length ? [] : stocks.map(s => s.symbol),
                  }))}
                >
                  {form.symbols.length === stocks.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
            {stocks.length === 0 ? (
              <div className="no-stocks">Add stocks in Configuration tab first</div>
            ) : (() => {
              const legendItems = [];
              const seen = new Set();
              stocks.forEach(s => (stockUsage[s.symbol] || []).forEach(item => {
                if (!seen.has(item.abbr)) { seen.add(item.abbr); legendItems.push(item); }
              }));
              return (
                <>
                  {legendItems.length > 0 && (
                    <div className="stock-legend">
                      {legendItems.map(item => (
                        <span key={item.abbr} className="legend-item">
                          <span className="legend-dot" style={{ background: item.color }} />
                          {item.abbr} — {item.fullName}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="editor-stocks-list">
                    {stocks.map(s => {
                      const usedIn = stockUsage[s.symbol] || [];
                      return (
                        <div key={s.symbol} className="stock-item-wrap">
                          <label className="editor-stock-chip" title={s.name || s.symbol}>
                            <input type="checkbox" checked={form.symbols.includes(s.symbol)} onChange={() => toggleSymbol(s.symbol)} />
                            <span>{s.symbol}</span>
                          </label>
                          {usedIn.length > 0 && (
                            <span className="algo-dots-row">
                              {usedIn.map((item, i) => (
                                <span key={i} className="algo-dot" style={{ background: item.color }} title={item.fullName} />
                              ))}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {(() => {
          const sections = [];
          let cur = { label: 'Strategy Parameters', fields: [] };
          for (const f of def.fields) {
            if (f.type === 'section') { sections.push(cur); cur = { label: f.label, toggleKey: f.toggleKey, toggleHelp: f.toggleHelp, fields: [] }; }
            else cur.fields.push(f);
          }
          sections.push(cur);
          return sections.map((section, si) => {
            const sectionOn = !section.toggleKey || form.params[section.toggleKey] !== false;
            return (
            <div key={si} className="param-section">
              <div className="param-section-title">
                {section.toggleKey ? (
                  <label title={section.toggleHelp} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={sectionOn} onChange={e => setParam(section.toggleKey, e.target.checked)}
                      style={{ width: 'auto', accentColor: 'var(--tab-active)' }} />
                    {section.label} {sectionOn ? '' : '(disabled)'}
                  </label>
                ) : section.label}
              </div>
              <fieldset disabled={!sectionOn} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, opacity: sectionOn ? 1 : 0.45 }}>
              <div className="param-grid">
                {section.fields.map(field => {
                  if (field.conditionalOn) {
                    const _cv = form.params[field.conditionalOn];
                    const _visible = typeof _cv === 'boolean' ? _cv : (parseFloat(_cv) > (field.conditionalMin ?? 0));
                    if (!_visible) return null;
                  }
                  if (field.conditionalOnValue && form.params[field.conditionalOnValue.field] !== field.conditionalOnValue.value) return null;
                  if (field.conditionalOnNotValue && form.params[field.conditionalOnNotValue.field] === field.conditionalOnNotValue.value) return null;
                  return (
                    <PField key={field.key} label={field.label} help={field.help}>
                      {field.type === 'time' ? (
                        <input type="time" value={form.params[field.key] || ''} onChange={e => setParam(field.key, e.target.value)} />
                      ) : field.type === 'checkbox' ? (
                        <input type="checkbox" checked={!!form.params[field.key]} onChange={e => setParam(field.key, e.target.checked)}
                          style={{ width: 'auto', alignSelf: 'flex-start', accentColor: 'var(--tab-active)' }} />
                      ) : field.type === 'select' ? (
                        <select value={form.params[field.key] ?? field.default ?? ''} onChange={e => setParam(field.key, e.target.value)}>
                          {(field.options || []).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      ) : field.type === 'text' ? (
                        <input type="text" value={form.params[field.key] ?? ''} onChange={e => setParam(field.key, e.target.value)} />
                      ) : (
                        <input type="number" step={field.step} min={field.min} max={field.max}
                          value={form.params[field.key] ?? ''} onChange={e => numParam(field.key, e.target.value)} />
                      )}
                    </PField>
                  );
                })}
              </div>
              </fieldset>
            </div>
            );
          });
        })()}
      </div>
      <div className="editor-footer">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave(def.type, form)}>Save Strategy</button>
      </div>
    </div>
  );
}

const OOB_EXIT_LABELS = {
  max_loss:              'Max Loss Hit',
  max_profit_target:     'Max Profit Target Hit',
  min_profit:            'Profit Lock Hit',
  time_exit:             'Time Exit',
  manual_button:         'Squared Off (button)',
  square_off_all_button: 'Squared Off (Square Off All)',
  global_pnl_limit:      'Squared Off (Global P&L Limit)',
  manual_external:       'Manually Squared Off',
  momentum_reversal:     'Momentum Reversed',
  circuit_exit:         'Circuit Reached (Exit)',
  stale_session_cleanup: 'Stale Record Cleared (Session Refresh)',
};

function OOBTradeItem({ trade: t, onSquareOff, squaringOff, onStopTrade, stoppingTrade, isExcepted, onManualMode, settingManualMode, onNoReverse, settingNoReverse }) {
  const [expanded, setExpanded] = useState(false);
  const exitLabel = OOB_EXIT_LABELS[t.exitReason] || t.exitReason?.replace(/_/g, ' ');

  if (t.status === 'failed_confirmation') {
    const detectedTime = t.detectedAt ? new Date(t.detectedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null;
    const stageLabel = (i, total) => i === 0 ? 'Detected' : i === total - 1 ? 'Failed' : `Pass ${i}`;
    return (
      <div className="trade-item oob-trade-item failed_confirmation">
        <div className="oob-trade-header">
          <button className="oob-trade-chevron" onClick={() => setExpanded(e => !e)} title="Show/hide checks">
            {expanded ? '▾' : '▸'}
          </button>
          <div className="trade-symbol failed-symbol">
            {t.symbol}
          </div>
          <div className="trade-info">
            <span className="trade-entry">{t.direction} @ ₹{t.price?.toFixed(2)}{detectedTime ? ` · ${detectedTime}` : ''}</span>
            <span className="trade-pnl loss">Signal lost · {t.confirmCount}/{t.scanConfirmations} confirmed</span>
          </div>
        </div>
        {expanded && (
          <div className="oob-checks-grid-wrap">
            {(!t.checks || t.checks.length === 0) ? (
              <div className="oob-no-checks">No scan data recorded</div>
            ) : (
              <table className="oob-checks-grid">
                <thead>
                  <tr><th>Stage</th><th>Check Time</th><th>Price</th><th>Volume</th><th>Buy Bids</th><th>Sell Bids</th></tr>
                </thead>
                <tbody>
                  {t.checks.map((c, i) => (
                    <tr key={i}>
                      <td>{stageLabel(i, t.checks.length)}</td>
                      <td>{c.time ? new Date(c.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</td>
                      <td>₹{c.price?.toFixed(2)}</td>
                      <td>{(c.volume || 0).toLocaleString()}</td>
                      <td>{(c.buyQty || 0).toLocaleString()}</td>
                      <td>{(c.sellQty || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`trade-item oob-trade-item ${t.status}`}>
      <div className="oob-trade-header">
        <button className="oob-trade-chevron" onClick={() => setExpanded(e => !e)} title="Show/hide checks">
          {expanded ? '▾' : '▸'}
        </button>
        <div className="trade-symbol">{t.symbol}</div>
        <div className="trade-info">
          <span className="trade-entry">
            {t.direction} {t.quantity}× @ ₹{t.entryPrice?.toFixed(2)}
          </span>
          {t.status === 'open' && (
            <span className="oob-trade-levels">
              <span className="trade-maxloss">Max Loss {t.maxLossPrice != null ? `₹${parseFloat(t.maxLossPrice).toFixed(2)}` : '-'}</span>
              <span className="trade-maxprofit">Max Profit {t.maxProfitPrice != null ? `₹${parseFloat(t.maxProfitPrice).toFixed(2)}` : '-'}</span>
              <span className="trade-minprofit">Min Profit {t.minProfitPrice != null ? `₹${parseFloat(t.minProfitPrice).toFixed(2)}` : '-'}</span>
            </span>
          )}
          {t.status === 'closed' && t.pnl != null && (
            <span className={`trade-pnl ${t.pnl >= 0 ? 'profit' : 'loss'}`}>
              {t.pnl >= 0 ? '+' : ''}₹{t.pnl.toFixed(0)} · {exitLabel}{t.exitDetail ? ` (${t.exitDetail})` : ''}
            </span>
          )}
        </div>
        {t.status === 'open' && (
          <button
            className="oob-squareoff-btn"
            disabled={squaringOff}
            onClick={() => onSquareOff(t.id, t.symbol)}
          >
            {squaringOff ? '...' : 'Square Off'}
          </button>
        )}
        {!isExcepted && (
          <button
            className="oob-stoptrade-btn"
            disabled={stoppingTrade}
            onClick={() => onStopTrade(t.symbol, t.status === 'open')}
            title={t.status === 'open' ? 'Square off and block from future scans today' : 'Block from future scans today'}
          >
            {stoppingTrade ? '...' : 'Stop Trade'}
          </button>
        )}
        {onManualMode && t.status === 'open' && !isExcepted && (
          <button
            className="oob-manualmode-btn"
            disabled={settingManualMode}
            onClick={() => onManualMode(t.symbol)}
            title="Leave this position exactly as-is and stop the algo from managing it further today"
          >
            {settingManualMode ? '...' : 'Manual Mode'}
          </button>
        )}
        {onNoReverse && t.status === 'open' && (
          t.noReverse ? (
            <span className="oob-noreverse-badge" title="If price reverses against this position without book/volume confirming it, the algo will hold instead of cutting it">No Reverse ✓</span>
          ) : (
            <button
              className="oob-noreverse-btn"
              disabled={settingNoReverse}
              onClick={() => onNoReverse(t.symbol)}
              title="If price reverses against this position and book/volume don't confirm it, hold instead of cutting the position — skip that trade"
            >
              {settingNoReverse ? '...' : 'No Reverse'}
            </button>
          )
        )}
      </div>
      {expanded && (
        <div className="oob-checks-grid-wrap">
          {(!t.checks || t.checks.length === 0) ? (
            <div className="oob-no-checks">No decision data recorded for this trade</div>
          ) : (
            <table className="oob-checks-grid">
              <thead>
                <tr>
                  <th>Stage</th><th>Check Time</th><th>Price</th><th>Volume</th><th>Buy Bids</th><th>Sell Bids</th>
                </tr>
              </thead>
              <tbody>
                {t.checks.map((c, i) => (
                  <tr key={i}>
                    <td>{i === 0 ? 'Detected' : i === t.checks.length - 1 ? 'Entry' : `Pass ${i}`}</td>
                    <td>{c.time ? new Date(c.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</td>
                    <td>₹{c.price?.toFixed(2)}</td>
                    <td>{(c.volume || 0).toLocaleString()}</td>
                    <td>{(c.buyQty || 0).toLocaleString()}</td>
                    <td>{(c.sellQty || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function StrategyCard({ def, config, onEdit, onToggle, onFavorite, onAutopilotEligible, onBacktest, allStrategies, onLaunchSimulation }) {
  const [expanded, setExpanded] = useState(false);
  const [squaringOffId, setSquaringOffId] = useState(null);
  const [stoppingTradeSymbol, setStoppingTradeSymbol] = useState(null);
  const [noReverseSymbol, setNoReverseSymbol] = useState(null);
  const [exceptedSymbols, setExceptedSymbols] = useState([]);
  const [showTradesPopout, setShowTradesPopout] = useState(false);
  const [popoutDates, setPopoutDates] = useState([]);
  const [popoutDate, setPopoutDate] = useState(null);
  const [popoutTrades, setPopoutTrades] = useState([]);
  const [popoutLoading, setPopoutLoading] = useState(false);

  const openTradesPopout = async () => {
    setShowTradesPopout(true);
    setPopoutLoading(true);
    try {
      const dd = await getMyStrategyTradeDates(def.type);
      const dates = dd.dates || [];
      setPopoutDates(dates);
      setPopoutDate(dates[dates.length - 1] || null);
    } catch {
      setPopoutDates([]);
      setPopoutDate(null);
    }
  };

  const fetchPopoutTrades = (date, silent = false) => {
    if (!date) { setPopoutTrades([]); setPopoutLoading(false); return; }
    if (!silent) setPopoutLoading(true);
    getMyStrategyTrades(def.type, date)
      .then(d => { setPopoutTrades(d.trades || []); setExceptedSymbols(d.exceptedSymbols || []); })
      .catch(() => {})
      .finally(() => { if (!silent) setPopoutLoading(false); });
  };

  useEffect(() => {
    if (!showTradesPopout) return;
    fetchPopoutTrades(popoutDate);
    const interval = setInterval(() => fetchPopoutTrades(popoutDate, true), 10000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTradesPopout, popoutDate]);

  // Square Off / Stop Trade buttons apply to every algo with per-symbol manual controls wired up server-side.
  const hasSquareOffUI = def.type === 'slowButSteady' || def.type === 'bahubali' || def.type === 'sos' || def.type === 'ddr' || def.type === 'terminator' || def.type === 'jaiMaaLakshmi' || def.type === 'avg' || def.type === 'pya';

  const closedPopoutTrades = popoutTrades.filter(t => t.status === 'closed' && t.pnl != null);
  const popoutPnl = closedPopoutTrades.length > 0
    ? popoutTrades.reduce((s, t) => s + (t.pnl || 0), 0)
    : null;
  const popoutCharges = closedPopoutTrades.length > 0
    ? closedPopoutTrades.reduce((s, t) => s + calcTradeCharges(t), 0)
    : null;

  const isObm = def.type === 'orderBookMomentum';
  const isHyb = def.type === 'obmVwapHybrid';

  const handleSquareOff = async (tradeId, symbol) => {
    if (!window.confirm(`Square off the open ${symbol} position now?`)) return;
    setSquaringOffId(tradeId);
    try {
      await squareOffMyStrategyTrade(def.type, tradeId);
      fetchPopoutTrades(popoutDate);
    } catch (err) {
      alert(err.response?.data?.error || 'Square-off failed');
    } finally {
      setSquaringOffId(null);
    }
  };

  const handleStopTrade = async (symbol, isOpen) => {
    const msg = isOpen
      ? `Square off ${symbol} and block it from future scans today?`
      : `Block ${symbol} from future scans today?`;
    if (!window.confirm(msg)) return;
    setStoppingTradeSymbol(symbol);
    try {
      await stopAlgoTrade(def.type, symbol);
      fetchPopoutTrades(popoutDate);
    } catch (err) {
      alert(err.response?.data?.error || 'Stop trade failed');
    } finally {
      setStoppingTradeSymbol(null);
    }
  };

  const isBahubali = def.type === 'bahubali';

  const handleNoReverse = async (symbol) => {
    if (!window.confirm(`Set No Reverse for ${symbol}? If price reverses against this position and book/volume don't confirm it, the algo will hold instead of cutting it — for this position only.`)) return;
    setNoReverseSymbol(symbol);
    try {
      await noReverseTrade(def.type, symbol);
      fetchPopoutTrades(popoutDate);
    } catch (err) {
      alert(err.response?.data?.error || 'No Reverse failed');
    } finally {
      setNoReverseSymbol(null);
    }
  };

  const renderTradesBody = (list, loading) => {
    if (loading) return <div className="trade-loading">Loading...</div>;
    if (list.length === 0) return <div className="no-trades">No trades yet</div>;
    return (
      <div className="trades-list">
        {list.map(t => (
          hasSquareOffUI ? (
            <OOBTradeItem
              key={t.id}
              trade={t}
              onSquareOff={handleSquareOff}
              squaringOff={squaringOffId === t.id}
              onStopTrade={handleStopTrade}
              stoppingTrade={stoppingTradeSymbol === t.symbol}
              isExcepted={exceptedSymbols.includes(t.symbol)}
              onNoReverse={(isBahubali || def.type === 'sos') ? handleNoReverse : null}
              settingNoReverse={noReverseSymbol === t.symbol}
            />
          ) : (
            <div key={t.id} className={`trade-item ${t.status}`}>
              <div className="trade-symbol">{t.symbol}</div>
              <div className="trade-info">
                <span className="trade-entry">
                  {t.direction} {t.quantity}× @ ₹{t.entryPrice?.toFixed(2)}
                </span>
                {t.status === 'open' && t.slPrice && (
                  <span className="trade-sl">SL ₹{parseFloat(t.slPrice).toFixed(2)}</span>
                )}
                {t.status === 'closed' && t.pnl != null && (
                  <span className={`trade-pnl ${t.pnl >= 0 ? 'profit' : 'loss'}`}>
                    {t.pnl >= 0 ? '+' : ''}₹{t.pnl.toFixed(0)} · {t.exitReason?.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </div>
          )
        ))}
      </div>
    );
  };

  return (
    <div className={`algo-card strategy-type-${def.type} ${config?.active ? 'active' : ''}`}>
      <div className="algo-card-row">
        <button className="algo-chevron" onClick={() => setExpanded(e => !e)}>{expanded ? '▾' : '▸'}</button>
        <div className="algo-info" onClick={() => setExpanded(e => !e)}>
          <span className={`type-badge strategy-${def.type}-badge`}>{def.abbr}</span>
          <span className="algo-name-text">{def.name}</span>
          {isObm
            ? <StocksChip symbols={[]} obmAll />
            : isHyb
            ? <StocksChip symbols={config?.symbols || []} hybLabel={`${config?.symbols?.length || 0} Stock${(config?.symbols?.length || 0) !== 1 ? 's' : ''}`} />
            : <StocksChip symbols={config?.symbols || []} />
          }
        </div>
        <div className="algo-controls">
          {!HideAlgoBacktestIcons && (
            <button
              className="backtest-icon-btn"
              title="Backtest"
              onClick={() => onLaunchSimulation && onLaunchSimulation({ kind: 'strategy', type: def.type })}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="8" width="2" height="5" rx="1" fill="currentColor"/><rect x="4.5" y="5" width="2" height="8" rx="1" fill="currentColor"/><rect x="8" y="2" width="2" height="11" rx="1" fill="currentColor"/><rect x="11.5" y="6" width="2" height="7" rx="1" fill="currentColor"/></svg>
            </button>
          )}
          <button
            className={`fav-btn${config?.favorite ? ' fav-btn--on' : ''}`}
            onClick={() => onFavorite(def.type)}
            title={config?.favorite ? 'Remove from favorites' : 'Add to favorites (auto-activates on login)'}
          >★</button>
          <button className="trades-btn" onClick={openTradesPopout} title="Show trades">⛶</button>
          {!def.noAutopilot && (
            <button
              className={`autopilot-eligible-btn${config?.autopilotEligible ? ' autopilot-eligible-btn--on' : ''}`}
              onClick={() => onAutopilotEligible(def.type)}
              title={config?.autopilotEligible ? 'Remove from Autopilot selection' : 'Enable for Autopilot (selectable from the Analysis page)'}
            >⚡</button>
          )}
          <button className="edit-btn" onClick={() => onEdit(def.type)} title="Configure">✎</button>
          <label className="active-toggle" title={config?.active ? 'Deactivate' : 'Activate'}>
            <input type="checkbox" checked={config?.active || false} onChange={() => onToggle(def.type)} />
            <span className="slider" />
          </label>
        </div>
      </div>
      {expanded && (
        <div className="algo-details">
          <div className="how-it-works-grid">
            <div className="how-it-works-left">
              <div className="detail-title">How It Works</div>
              <ol className="algo-steps">
                {buildStrategySteps(def.type, config).map((step, i) => (
                  <li key={i} className="algo-step">{step}</li>
                ))}
              </ol>
              {def.docUrl && (<p className="how-it-works-doc-link">For more info — click <a href={def.docUrl} target="_blank" rel="noopener noreferrer">here</a>.</p>)}
            </div>
            <div className="how-it-works-right">
              <div className="detail-title">Example</div>
              {(def.example || []).map((para, i) => (
                <p key={i} className="how-it-works-example-text">{para}</p>
              ))}
            </div>
          </div>
        </div>
      )}
      {showTradesPopout && (
        <div className="algo-editor-overlay" onClick={() => setShowTradesPopout(false)}>
          <div className="algo-editor-modal trades-popout-modal" onClick={e => e.stopPropagation()}>
            <TradesPopoutHeader
              title={`${def.name} — Trades`}
              dates={popoutDates}
              currentDate={popoutDate}
              onChangeDate={d => setPopoutDate(d)}
              pnlSum={popoutPnl}
              chargesSum={popoutCharges}
              onRefresh={() => fetchPopoutTrades(popoutDate)}
              refreshing={popoutLoading}
              onClose={() => setShowTradesPopout(false)}
            />
            <div className="editor-body trades-popout-body">
              {renderTradesBody(popoutTrades, popoutLoading)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BacktestSimulationClock({ currentTime }) {
  const pad = (n) => String(n).padStart(2, '0');
  const timeStr = `${pad(currentTime.getHours())}:${pad(currentTime.getMinutes())}:${pad(currentTime.getSeconds())}`;
  return (
    <div className="simulation-clock">
      <div className="time-display">{timeStr}</div>
    </div>
  );
}

// Shared helpers for P&L limit functions
function _buildCandleMaps(candlesBySymbol) {
  const candleMap = {};
  const tsStrMap  = {};
  const tsSet     = new Set();
  for (const [symbol, candles] of Object.entries(candlesBySymbol)) {
    for (const candle of candles) {
      const tsMs = new Date(candle[0]).getTime();
      tsSet.add(tsMs);
      if (!candleMap[tsMs]) candleMap[tsMs] = {};
      candleMap[tsMs][symbol] = candle[4];
      if (!tsStrMap[tsMs]) tsStrMap[tsMs] = candle[0];
    }
  }
  return { candleMap, tsStrMap, allTicks: Array.from(tsSet).sort((a, b) => a - b) };
}

function _buildOrdersByTs(orders) {
  const ordersByTs = {};
  for (const order of orders) {
    const tsMs = new Date(order.timestamp).getTime();
    if (!ordersByTs[tsMs]) ordersByTs[tsMs] = [];
    ordersByTs[tsMs].push(order);
  }
  return ordersByTs;
}

function _calcUnrealized(openPositions, latestPrice) {
  let u = 0;
  for (const [sym, pos] of Object.entries(openPositions)) {
    const price = latestPrice[sym] ?? pos.avgPrice;
    u += pos.entryType === 'BUY' ? (price - pos.avgPrice) * pos.qty : (pos.avgPrice - price) * pos.qty;
  }
  return u;
}

function _forceClose(openPositions, latestPrice, tsStr, result) {
  for (const [sym, pos] of Object.entries(openPositions)) {
    result.push({
      symbol: sym,
      transactionType: pos.entryType === 'BUY' ? 'SELL' : 'BUY',
      quantity: pos.qty,
      price: latestPrice[sym] ?? pos.avgPrice,
      status: 'completed',
      timestamp: tsStr,
    });
  }
}

function _isLimitHit(totalPnL, maxDailyProfit, maxDailyLoss) {
  return (maxDailyProfit > 0 && totalPnL >= maxDailyProfit) ||
         (maxDailyLoss   > 0 && totalPnL <= -maxDailyLoss);
}

// Core tick-by-tick P&L limit engine.
// Checks limit BEFORE orders at each candle tick (catches unrealized breaches),
// and AFTER each individual close order (catches realized breaches mid-tick).
function _runPnLLimitEngine(orders, maxDailyProfit, maxDailyLoss, candlesBySymbol) {
  const { candleMap, tsStrMap, allTicks } = _buildCandleMaps(candlesBySymbol);
  const ordersByTs = _buildOrdersByTs(orders);

  const openPositions = {};
  const latestPrice   = {};
  let realizedPnL = 0;
  let limitHit    = false;
  let limitHitTs  = null;
  const result    = [];

  for (const tsMs of allTicks) {
    if (limitHit) break;

    // Update prices from this candle
    const tick = candleMap[tsMs];
    if (tick) {
      for (const [sym, close] of Object.entries(tick)) latestPrice[sym] = close;
    }

    // Check limit based on unrealized at updated candle price BEFORE any orders at this tick.
    // This catches cases where price movement alone breaches the limit.
    if (Object.keys(openPositions).length > 0) {
      const unrealized = _calcUnrealized(openPositions, latestPrice);
      if (_isLimitHit(realizedPnL + unrealized, maxDailyProfit, maxDailyLoss)) {
        limitHit = true;
        limitHitTs = tsMs;
        const tsStr = tsStrMap[tsMs] || new Date(tsMs).toISOString();
        _forceClose(openPositions, latestPrice, tsStr, result);
        break;
      }
    }

    // Process orders at this tick one by one
    for (const order of (ordersByTs[tsMs] || [])) {
      if (limitHit) break;
      const sym = order.symbol;
      if (!openPositions[sym]) {
        openPositions[sym] = { qty: order.quantity, avgPrice: order.price, entryType: order.transactionType };
        result.push(order);
      } else {
        const pos = openPositions[sym];
        realizedPnL += pos.entryType === 'BUY'
          ? (order.price - pos.avgPrice) * pos.qty
          : (pos.avgPrice - order.price) * pos.qty;
        delete openPositions[sym];
        result.push(order);

        // Check limit after each close — realized P&L just changed.
        const unrealized = _calcUnrealized(openPositions, latestPrice);
        if (_isLimitHit(realizedPnL + unrealized, maxDailyProfit, maxDailyLoss)) {
          limitHit = true;
          limitHitTs = tsMs;
          const tsStr = tsStrMap[tsMs] || new Date(tsMs).toISOString();
          _forceClose(openPositions, latestPrice, tsStr, result);
        }
      }
    }
  }
  return { result, limitHitTs };
}

function calcOrderCharges(transactionType, price, quantity) {
  const tradedValue = price * quantity;
  const brokerage = tradedValue * 0.0000307 * 1.18;
  return transactionType === 'BUY'
    ? brokerage + 50
    : brokerage + (tradedValue * 0.00025) + 34;
}

function calcTradeCharges(t) {
  const qty = t.qty || t.quantity || 1;
  if (t.pnl == null || !t.entryPrice || !t.direction) return 0;
  const exitPrice = t.direction === 'BUY'
    ? t.entryPrice + t.pnl / qty
    : t.entryPrice - t.pnl / qty;
  return calcOrderCharges(t.direction, t.entryPrice, qty)
       + calcOrderCharges(t.direction === 'BUY' ? 'SELL' : 'BUY', exitPrice, qty);
}

// Deduplicates combined orders cross-algo: if a BUY position is already open for a symbol,
// any additional BUY entry (from another algo) is skipped along with its exit.
// Same applies for SELL positions. Orders are processed in timestamp order.
function deduplicateCrossAlgoOrders(orders) {
  if (orders.length === 0) return orders;
  const sorted = [...orders].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const openLong  = {};
  const openShort = {};
  const openLongQty  = {};  // quantity of the real open long position per symbol
  const openShortQty = {};  // quantity of the real open short position per symbol
  const pendingLongExits  = {};
  const pendingShortExits = {};
  const result = [];

  for (const order of sorted) {
    const sym = order.symbol;
    if (order.transactionType === 'BUY') {
      if (openLong[sym]) {
        pendingLongExits[sym] = (pendingLongExits[sym] || 0) + 1;
      } else if (openShort[sym]) {
        // Closing an existing short — use its exact quantity
        const posQty = openShortQty[sym];
        openShort[sym] = false;
        delete openShortQty[sym];
        result.push(posQty !== order.quantity ? { ...order, quantity: posQty } : order);
      } else if ((pendingShortExits[sym] || 0) > 0) {
        pendingShortExits[sym]--;
      } else {
        openLong[sym] = true;
        openLongQty[sym] = order.quantity;
        result.push(order);
      }
    } else {
      if (openShort[sym]) {
        pendingShortExits[sym] = (pendingShortExits[sym] || 0) + 1;
      } else if (openLong[sym]) {
        // Closing an existing long — use its exact quantity
        const posQty = openLongQty[sym];
        openLong[sym] = false;
        delete openLongQty[sym];
        result.push(posQty !== order.quantity ? { ...order, quantity: posQty } : order);
      } else if ((pendingLongExits[sym] || 0) > 0) {
        pendingLongExits[sym]--;
      } else {
        openShort[sym] = true;
        openShortQty[sym] = order.quantity;
        result.push(order);
      }
    }
  }
  return result;
}

// Applies the global daily P&L limit across ALL orders (every algo + strategy combined).
function applyGlobalPnLLimits(allOrders, maxDailyProfit, maxDailyLoss, candlesBySymbol) {
  if (!(maxDailyProfit > 0) && !(maxDailyLoss > 0)) return allOrders;
  if (allOrders.length === 0) return allOrders;
  const { result } = _runPnLLimitEngine(allOrders, maxDailyProfit, maxDailyLoss, candlesBySymbol);
  return result;
}


function AlgosPage() {
  const [algos, setAlgos]               = useState([]);
  const [editing, setEditing]           = useState(null);
  const [myStrategies, setMyStrategies]         = useState({});
  const [editingMyStrategy, setEditingMyStrategy] = useState(null);
  const [nseSnapshotStatus, setNseSnapshotStatus] = useState(null);
  const [snapshotJustCaptured, setSnapshotJustCaptured] = useState(false);
  const [snapshotCapturing, setSnapshotCapturing] = useState(false);
  const prevSnapshotRef = useRef(null);
  const [activeTab, setActiveTab]           = useState('favourites');
  const [showMyTrades, setShowMyTrades]     = useState(false);

  const [error, setError]                 = useState('');
  const [backtestDates, setBacktestDates]             = useState([]);
  // 'backtest' = captured minutely data (backtest-backend); 'user' = user-downloaded historical data
  const [backtestDataSource, setBacktestDataSource]   = useState(() => localStorage.getItem('backtestDataSource') || 'backtest');
  const [globalSettings, setGlobalSettings] = useState({ max_profit_per_day: 0, max_loss_per_day: 0, profit_activation_threshold: 0, min_profit_ratio: 0, max_profit_ratchet_floor_pct: 90, max_profit_ratchet_growth_pct: 25 });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved]   = useState(false);
  const [squaringOff, setSquaringOff]       = useState(false);
  const [squareOffMsg, setSquareOffMsg]     = useState('');
  const [pnlStatus, setPnlStatus]           = useState(null);
  const [pnlStatusLoading, setPnlStatusLoading] = useState(false);
  const [showBacktestModal, setShowBacktestModal] = useState(false);
  const [backtestSingleTarget, setBacktestSingleTarget] = useState(null); // { kind:'algo',algo } | { kind:'strategy',type }
  const [backtestDate, setBacktestDate] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [simulationTime, setSimulationTime] = useState(null);
  const [positions, setPositions]   = useState([]);
  const [orders, setOrders]         = useState([]);
  const [isPaused, setIsPaused]         = useState(false);
  const [isFastForward, setIsFastForward] = useState(false);
  const [showAllSymbols, setShowAllSymbols] = useState(false);
  const [simulationDirty, setSimulationDirty] = useState(false);
  const [activeBacktestSymbols, setActiveBacktestSymbols] = useState([]);
  const simulationIntervalRef  = useRef(null);
  const [candles, setCandles]       = useState([]);
  const allOrdersRef           = useRef([]);
  const candlesBySymbolRef     = useRef({});
  const allThresholdEventsRef  = useRef([]);

  useEffect(() => {
    getAlgos().then(d => setAlgos(d.algos || [])).catch(() => {});
    getMyStrategies().then(d => setMyStrategies(d.myStrategies || {})).catch(() => {});
    getNseStocksStatus().then(d => { setNseSnapshotStatus(d); prevSnapshotRef.current = d; }).catch(() => {});
    // Backtest dates are reloaded when the source changes (see separate effect below)
    getGlobalSettings()
      .then(d => setGlobalSettings(d || { max_profit_per_day: 0, max_loss_per_day: 0, profit_activation_threshold: 0, min_profit_ratio: 0, max_profit_ratchet_floor_pct: 90, max_profit_ratchet_growth_pct: 25 }))
      .catch(() => {});
  }, []);

  const handleSquareOffAll = async () => {
    if (!window.confirm('Square off ALL open positions and deactivate all algos?\n\nP&L limits will be cleared. Favorites reactivate automatically on next login, or flip them back on individually.\n\nThis cannot be undone.')) return;
    setSquaringOff(true);
    setSquareOffMsg('');
    try {
      const res = await manualSquareOffAll();
      setSquareOffMsg(res.message || 'Done');
      // Refresh settings — P&L limits are cleared server-side after square-off
      getGlobalSettings()
        .then(d => setGlobalSettings(d || { max_profit_per_day: 0, max_loss_per_day: 0, profit_activation_threshold: 0, min_profit_ratio: 0, max_profit_ratchet_floor_pct: 90, max_profit_ratchet_growth_pct: 25 }))
        .catch(() => {});
      setTimeout(() => setSquareOffMsg(''), 8000);
    } catch (e) {
      setSquareOffMsg(e.response?.data?.error || 'Square-off failed');
      setTimeout(() => setSquareOffMsg(''), 5000);
    } finally {
      setSquaringOff(false);
    }
  };

  const fetchPnLStatus = useCallback(async (manual) => {
    if (manual) setPnlStatusLoading(true);
    try {
      const d = await getPnLStatus();
      setPnlStatus(d);
    } catch {
      // Silent — this is a passive display, not worth surfacing a page-level error for
    } finally {
      if (manual) setPnlStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPnLStatus(false);
    const interval = setInterval(() => fetchPnLStatus(false), 15000);
    return () => clearInterval(interval);
  }, [fetchPnLStatus]);

  const handleSaveGlobalSettings = async () => {
    setSettingsSaving(true);
    setSettingsSaved(false);
    try {
      await saveGlobalSettings(globalSettings);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch { setError('Failed to save global settings'); }
    finally { setSettingsSaving(false); }
  };

  // Poll snapshot status every 30s; show "just captured" banner when it transitions to ready
  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const d = await getNseStocksStatus();
        const prev = prevSnapshotRef.current;
        const justCaptured = d && d.hasData && (!prev || !prev.hasData || prev.date !== d.date);
        prevSnapshotRef.current = d;
        setNseSnapshotStatus(d);
        if (justCaptured) {
          setSnapshotCapturing(false);
          setSnapshotJustCaptured(true);
          setTimeout(() => setSnapshotJustCaptured(false), 30000);
        }
      } catch { /* silent */ }
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  // Auto-select most recent date when backtestDates loads
  useEffect(() => {
    if (backtestDates.length > 0) {
      setBacktestDate(backtestDates[backtestDates.length - 1]);
    } else {
      setBacktestDate('');
    }
  }, [backtestDates]);

  const handleToggle = async (algo) => {
    try {
      if (!algo.active && (!algo.symbols || algo.symbols.length === 0)) {
        setError('Select at least one stock before activating');
        return;
      }
      const updates = { active: !algo.active };
      const res = await updateAlgo(algo.id, { ...algo, ...updates });
      setAlgos(prev => prev.map(a => a.id === algo.id ? res.algo : a));
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update');
    }
  };

  // Returns { symbol: [{abbr, color, fullName}, ...] } for all algos/strategies, excluding the one being edited
  const getStockUsage = (excludeAlgoId, excludeStratType) => {
    const usage = {};
    const add = (sym, abbr, color, fullName) => {
      if (!usage[sym]) usage[sym] = [];
      if (!usage[sym].find(x => x.abbr === abbr)) usage[sym].push({ abbr, color, fullName });
    };
    // Assign distinct colors to unique algo abbreviations in order of first appearance
    const algoAbbrColorMap = {};
    let colorIdx = 0;
    const algoColorFor = (abbr) => {
      if (!algoAbbrColorMap[abbr]) {
        algoAbbrColorMap[abbr] = ALGO_FALLBACK_COLORS[colorIdx % ALGO_FALLBACK_COLORS.length];
        colorIdx++;
      }
      return algoAbbrColorMap[abbr];
    };
    for (const algo of algos) {
      if (excludeAlgoId && algo.id === excludeAlgoId) continue;
      if (!algo.active) continue;
      const abbr = (algo.name || '?').substring(0, 3).toUpperCase();
      (algo.symbols || []).forEach(sym => add(sym, abbr, algoColorFor(abbr), algo.name));
    }
    for (const [type, strat] of Object.entries(myStrategies)) {
      if (excludeStratType && type === excludeStratType) continue;
      if (!strat?.active) continue;
      const style    = ALGO_TYPE_STYLES[type] || { color: algoColorFor(type.substring(0, 3).toUpperCase()), abbr: type.substring(0, 3).toUpperCase() };
      const fullName = MY_STRATEGY_DEFS[type]?.name || type;
      (strat?.symbols || []).forEach(sym => add(sym, style.abbr, style.color, fullName));
    }
    return usage;
  };

  const handleSave = async (form) => {
    try {
      if (editing === 'new') {
        const res = await createAlgo(form);
        setAlgos(prev => [...prev, res.algo]);
      } else {
        const res = await updateAlgo(editing.id, { ...editing, ...form });
        setAlgos(prev => prev.map(a => a.id === editing.id ? res.algo : a));
      }
      setEditing(null);
    } catch { setError('Failed to save algorithm'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this algorithm?')) return;
    try {
      await deleteAlgo(id);
      setAlgos(prev => prev.filter(a => a.id !== id));
    } catch { setError('Failed to delete'); }
  };

  const performMyStrategySave = async (type, form) => {
    const res = await updateMyStrategy(type, form);
    setMyStrategies(prev => ({ ...prev, [type]: res.strategy }));
    setEditingMyStrategy(null);
  };

  const handleMyStrategySave = async (type, form) => {
    try { await performMyStrategySave(type, form); } catch { setError('Failed to save strategy'); }
  };

  const handleAlgoFavorite = async (algo) => {
    try {
      const res = await toggleAlgoFavorite(algo.id, !algo.favorite);
      setAlgos(prev => prev.map(a => a.id === algo.id ? res.algo : a));
    } catch { setError('Failed to update favorite'); }
  };

  const handleMyStrategyFavorite = async (type) => {
    const strategy = myStrategies[type];
    try {
      const res = await toggleMyStrategyFavorite(type, !strategy?.favorite);
      setMyStrategies(prev => ({ ...prev, [type]: res.strategy }));
    } catch { setError('Failed to update favorite'); }
  };

  const handleMyStrategyAutopilotEligible = async (type) => {
    const strategy = myStrategies[type];
    try {
      const res = await toggleMyStrategyAutopilotEligible(type, !strategy?.autopilotEligible);
      setMyStrategies(prev => ({ ...prev, [type]: res.strategy }));
    } catch { setError('Failed to update Autopilot eligibility'); }
  };


  const handleTakeSnapshot = async () => {
    setSnapshotCapturing(true);
    setError(null);
    try {
      const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
      const d = new Date(ist);
      d.setDate(d.getDate() - 1);
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
      const prevTradingDay = d.toISOString().substring(0, 10);
      await triggerSnapshot(prevTradingDay);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start snapshot');
      setSnapshotCapturing(false);
    }
  };

  // Reload available dates whenever the backtest data source changes
  useEffect(() => {
    const loadDates = backtestDataSource === 'user' ? getUserDataDates : getBacktestDates;
    loadDates()
      .then(d => setBacktestDates(d.dates || []))
      .catch(() => setBacktestDates([]));
  }, [backtestDataSource]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeBacktestSource = (src) => {
    setBacktestDataSource(src);
    localStorage.setItem('backtestDataSource', src);
    setBacktestDates([]);   // cleared; useEffect above refills them
    setBacktestDate('');
  };

  const handleMyStrategyToggle = async (type) => {
    const strategy = myStrategies[type];
    if (!strategy) return;
    const def = MY_STRATEGY_DEFS[type];
    if (!strategy.active && def && def.requiresSnapshot && !(nseSnapshotStatus && nseSnapshotStatus.hasData)) {
      setError('Cannot activate: previous-day 3PM depth snapshot has not been captured yet. It runs automatically at 3:00 PM IST on weekdays.');
      return;
    }
    try {
      const updates = { ...strategy, active: !strategy.active };
      const res = await updateMyStrategy(type, updates);
      setMyStrategies(prev => ({ ...prev, [type]: res.strategy }));
      setError('');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update strategy';
      setError(msg);
    }
  };

  // Advance the simulation clock (1 min/s normal, 5 min/50ms fast-forward)
  useEffect(() => {
    if (!simulating || isPaused || candles.length === 0) {
      if (simulationIntervalRef.current) {
        clearInterval(simulationIntervalRef.current);
        simulationIntervalRef.current = null;
      }
      return;
    }
    if (!simulationTime) return;

    const lastCandleTime = new Date(candles[candles.length - 1][0]);
    const intervalMs = isFastForward ? 50 : 1000;
    const stepMs     = isFastForward ? 5 * 60 * 1000 : 60 * 1000;
    const interval = setInterval(() => {
      setSimulationTime(prev => {
        if (!prev || prev.getTime() >= lastCandleTime.getTime()) return prev;
        return new Date(Math.min(prev.getTime() + stepMs, lastCandleTime.getTime()));
      });
    }, intervalMs);
    simulationIntervalRef.current = interval;
    return () => clearInterval(interval);
  }, [simulating, isPaused, isFastForward, candles, simulationTime]);

  // As clock advances: reveal orders; track net position per symbol with running realized P&L
  useEffect(() => {
    if (!simulating || !simulationTime) return;

    const visibleOrders = allOrdersRef.current.filter(
      o => new Date(o.timestamp) <= simulationTime
    );
    setOrders(visibleOrders);

    // Net-position model per symbol:
    //   netQty > 0  → long    netQty < 0  → short    netQty === 0  → flat (blurred)
    //   avgPrice    → weighted average entry price for the current open portion
    //   realizedPnl → cumulative P&L from all closed portions so far
    const posMap = {};

    for (const order of visibleOrders) {
      const { symbol, transactionType, quantity, price } = order;
      const orderQty = transactionType === 'BUY' ? +quantity : -quantity;
      const prev = posMap[symbol] || { netQty: 0, avgPrice: 0, realizedPnl: 0, charges: 0 };
      const prevNetQty = prev.netQty;
      const newNetQty  = prevNetQty + orderQty;
      let { avgPrice, realizedPnl } = prev;
      const charges = prev.charges + calcOrderCharges(transactionType, price, Math.abs(quantity));

      if (prevNetQty === 0) {
        // Opening a fresh position
        avgPrice = price;
      } else if (newNetQty === 0 || Math.sign(newNetQty) !== Math.sign(prevNetQty)) {
        // Going flat OR flipping direction — realize P&L on the entire previous position
        const pnlPerUnit = prevNetQty > 0 ? price - avgPrice : avgPrice - price;
        realizedPnl += pnlPerUnit * Math.abs(prevNetQty);
        // If flipping, the leftover qty opens in the opposite direction at this price
        avgPrice = newNetQty !== 0 ? price : 0;
      } else if (Math.abs(newNetQty) > Math.abs(prevNetQty)) {
        // Adding to an existing position (same direction) — update weighted avg price
        avgPrice = (avgPrice * Math.abs(prevNetQty) + price * Math.abs(orderQty)) / Math.abs(newNetQty);
      } else {
        // Partially reducing an existing position — realize P&L on the closed portion only
        const closedQty = Math.abs(prevNetQty) - Math.abs(newNetQty);
        const pnlPerUnit = prevNetQty > 0 ? price - avgPrice : avgPrice - price;
        realizedPnl += pnlPerUnit * closedQty;
        // avgPrice unchanged — remaining position keeps same avg entry
      }

      posMap[symbol] = { netQty: newNetQty, avgPrice, realizedPnl, charges };
    }

    // Build threshold map: latest threshold event per symbol up to simulationTime
    const thresholdMap = {};
    for (const ev of allThresholdEventsRef.current) {
      if (new Date(ev.timestamp) <= simulationTime) {
        thresholdMap[ev.symbol] = { minPnL: ev.minPnL, maxPnL: ev.maxPnL };
      }
    }

    // Update current market price from candles for each symbol
    const derivedPositions = Object.entries(posMap).map(([symbol, pos]) => {
      const posCandles = candlesBySymbolRef.current[symbol];
      let currentPrice = pos.avgPrice;
      if (posCandles) {
        for (let i = posCandles.length - 1; i >= 0; i--) {
          if (new Date(posCandles[i][0]) <= simulationTime) {
            currentPrice = posCandles[i][4];
            break;
          }
        }
      }
      const thresh = thresholdMap[symbol];
      return { symbol, ...pos, currentPrice, minPnL: thresh?.minPnL, maxPnL: thresh?.maxPnL };
    });

    setPositions(derivedPositions);
  }, [simulationTime, simulating]);

  const handleStartBacktest = async () => {
    if (!backtestDate) {
      setError('Select a backtest date');
      return;
    }

    let activeAlgos, activeMyStrats;
    if (backtestSingleTarget?.kind === 'algo') {
      activeAlgos    = [backtestSingleTarget.algo];
      activeMyStrats = [];
    } else if (backtestSingleTarget?.kind === 'strategy') {
      activeAlgos    = [];
      activeMyStrats = [backtestSingleTarget.type];
    } else {
      activeAlgos    = algos.filter(a => a.active);
      activeMyStrats = Object.keys(MY_STRATEGY_DEFS).filter(type => {
        const config = myStrategies[type];
        if (!config) return false;
        if (config.active) return true;
        return Array.isArray(config.symbols) && config.symbols.length > 0;
      });
    }

    if (activeAlgos.length === 0 && activeMyStrats.length === 0) {
      setError('No algo or strategy selected for backtest');
      return;
    }

    const algoSymbols = new Set();
    for (const algo of activeAlgos) (algo.symbols || []).forEach(s => algoSymbols.add(s));

    if (activeAlgos.length > 0 && algoSymbols.size === 0 && activeMyStrats.length === 0) {
      setError('No stocks configured in any active algo');
      return;
    }

    // OBM, HYB require captured order-book depth data - skip them in user data mode
    const depthRequired = ['orderBookMomentum', 'obmVwapHybrid'];
    const runMyStrats   = backtestDataSource === 'user'
      ? activeMyStrats.filter(t => !depthRequired.includes(t))
      : activeMyStrats;

    if (backtestDataSource === 'user') {
      const skipped = activeMyStrats.filter(t => depthRequired.includes(t));
      if (skipped.length > 0 && activeAlgos.length === 0 && runMyStrats.length === 0) {
        const names = skipped.map(t => MY_STRATEGY_DEFS[t]?.name || t).join(', ');
        setError(`${names} require captured order-book depth data and cannot run in User Data mode. Switch to "Backtest Data" source.`);
        return;
      }
    }

    allOrdersRef.current         = [];
    candlesBySymbolRef.current   = {};
    allThresholdEventsRef.current = [];
    setSimulationDirty(false);
    setShowBacktestModal(false);
    setSimulating(true);
    setIsPaused(false);
    setIsFastForward(false);
    setShowAllSymbols(false);
    setPositions([]);
    setOrders([]);

    try {
      let firstCandleTime  = null;
      const backtestSymbols = [];

      // Load candles for general algos (OBM/HYB/TOB/PROS use their own backend simulation)
      const needCandles  = activeAlgos.length > 0;
      const symbolFilter = Array.from(algoSymbols);
      const fetchCandles = backtestDataSource === 'user' ? getUserDataCandles : getBacktestCandles;
      const candleMap    = needCandles
        ? (await fetchCandles(backtestDate, symbolFilter)).candles || {}
        : {};

      for (const [symbol, dayCandles] of Object.entries(candleMap)) {
        if (!dayCandles || dayCandles.length === 0) continue;

        backtestSymbols.push(symbol);
        candlesBySymbolRef.current[symbol] = dayCandles;

        if (!firstCandleTime) {
          setCandles(dayCandles);
          firstCandleTime = new Date(dayCandles[0][0]);
          setSimulationTime(firstCandleTime);
        }

        // Regular algos: run server-side backtest for their configured symbols
        const algosForSymbol = activeAlgos.filter(a => (a.symbols || []).includes(symbol));
        for (const algo of algosForSymbol) {
          const response = await apiClient.post('/api/analysis/backtest', {
            algoId:       algo.id,
            candles:      dayCandles,
            symbol,
            prevDayByDate: {},
          });
          if (response.data.orders) allOrdersRef.current.push(...response.data.orders);
        }
      }

      // OBM / HYB: server-side simulation using stored depth data
      for (const stratType of runMyStrats.filter(t => t === 'orderBookMomentum' || t === 'obmVwapHybrid')) {
        const stratConfig = myStrategies[stratType];
        const stratParams = stratConfig?.params || {};
        try {
          const simResult = await simulateOBMBacktest(backtestDate, stratParams, stratType);
          if (simResult.orders) allOrdersRef.current.push(...simResult.orders);
          if (simResult.candles) {
            for (const [sym, syCandles] of Object.entries(simResult.candles)) {
              if (!candlesBySymbolRef.current[sym]) {
                candlesBySymbolRef.current[sym] = syCandles;
                backtestSymbols.push(sym);
                if (!firstCandleTime && syCandles.length) {
                  setCandles(syCandles);
                  firstCandleTime = new Date(syCandles[0][0]);
                  setSimulationTime(firstCandleTime);
                }
              }
            }
          }
        } catch (err) {
          setError(err.response?.data?.error || err.message || 'OBM backtest failed');
          setSimulating(false);
          return;
        }
      }

      allOrdersRef.current = deduplicateCrossAlgoOrders(allOrdersRef.current);
      allOrdersRef.current = applyGlobalPnLLimits(
        allOrdersRef.current,
        parseFloat(globalSettings.max_profit_per_day) || 0,
        parseFloat(globalSettings.max_loss_per_day)   || 0,
        candlesBySymbolRef.current
      );

      setActiveBacktestSymbols(backtestSymbols);

      if (!firstCandleTime) {
        setError(`No backtest data found for ${backtestDate}. Data is captured automatically during market hours.`);
        setSimulating(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Backtest failed');
      setSimulating(false);
    }
  };

  const handleFastForward = () => { setIsFastForward(true); setIsPaused(false); };

  const handlePauseResume = () => {
    if (isPaused) { setIsPaused(false); }
    else { setIsFastForward(false); setIsPaused(true); }
  };

  const handleStopBacktest = () => {
    if (simulationIntervalRef.current) clearInterval(simulationIntervalRef.current);
    allOrdersRef.current = [];
    candlesBySymbolRef.current = {};
    allThresholdEventsRef.current = [];
    setSimulating(false);
    setSimulationDirty(false);
    setIsPaused(false);
    setIsFastForward(false);
    setShowAllSymbols(false);
    setSimulationTime(null);
    setPositions([]);
    setOrders([]);
  };

  const handlePopupDateChange = (newDate) => {
    setBacktestDate(newDate);
    if (simulationIntervalRef.current) clearInterval(simulationIntervalRef.current);
    allOrdersRef.current = [];
    candlesBySymbolRef.current = {};
    allThresholdEventsRef.current = [];
    setCandles([]);
    setSimulationTime(null);
    setPositions([]);
    setOrders([]);
    setIsPaused(false);
    setIsFastForward(false);
    setSimulating(false);
    setSimulationDirty(true);
  };

  const handleRestartBacktest = () => {
    setSimulationDirty(false);
    if (simulationIntervalRef.current) clearInterval(simulationIntervalRef.current);
    allOrdersRef.current = [];
    candlesBySymbolRef.current = {};
    allThresholdEventsRef.current = [];
    setCandles([]);
    setSimulationTime(null);
    setPositions([]);
    setOrders([]);
    setIsPaused(false);
    setIsFastForward(false);
    setShowAllSymbols(false);
    handleStartBacktest();
  };

  return (
    <div className="algos-page">
      {editing && (
        <div className="algo-editor-overlay">
          <div className="algo-editor-modal">
            <AlgoEditor
              algo={editing === 'new' ? null : editing}
              onSave={handleSave}
              onCancel={() => setEditing(null)}
              stockUsage={getStockUsage(editing === 'new' ? null : editing?.id, null)}
            />
          </div>
        </div>
      )}

      {editingMyStrategy && MY_STRATEGY_DEFS[editingMyStrategy] && (
        <div className="algo-editor-overlay">
          <div className="algo-editor-modal">
            <StrategyEditor
              def={MY_STRATEGY_DEFS[editingMyStrategy]}
              config={myStrategies[editingMyStrategy]}
              onSave={handleMyStrategySave}
              onCancel={() => setEditingMyStrategy(null)}
              stockUsage={getStockUsage(null, editingMyStrategy)}
            />
          </div>
        </div>
      )}

      <div className="algos-main">
        <div className="algos-toolbar">
          <div>
            <h2 className="algos-title">My Trading Algorithms</h2>
            <p className="algos-sub">Applied to stocks in the Configuration list. Toggle to activate.</p>
          </div>
          <div className="algos-toolbar-actions">
            <button className="my-trades-link-btn" onClick={() => setShowMyTrades(true)}>My Trades</button>
            {activeTab === 'general' && (
              <button className="new-algo-btn" data-mobile-icon="+" onClick={() => setEditing('new')}>+ New Algorithm</button>
            )}
          </div>
        </div>

        {showMyTrades && <MyTradesModal onClose={() => setShowMyTrades(false)} />}

        {error && <div className="algo-error">{error} <button onClick={() => setError('')}>✕</button></div>}

        <div className="algo-tabs">
          <button className={`algo-tab-btn${activeTab === 'favourites' ? ' active' : ''}`} onClick={() => setActiveTab('favourites')}>
            Favourites
          </button>
          <button className={`algo-tab-btn${activeTab === 'general' ? ' active' : ''}`} onClick={() => setActiveTab('general')}>
            General Strategies
          </button>
          <button className={`algo-tab-btn${activeTab === 'mystrategies' ? ' active' : ''}`} onClick={() => setActiveTab('mystrategies')}>
            Complex Strategies
          </button>
        </div>

        {activeTab === 'favourites' && (
          <div className="algo-list">
            {algos.filter(a => a.favorite).map(algo => (
              <AlgoCard
                key={algo.id}
                algo={algo}
                onToggle={handleToggle}
                onEdit={setEditing}
                onDelete={handleDelete}
                onFavorite={handleAlgoFavorite}
                onLaunchSimulation={(target) => { setSimulating(false); setSimulationDirty(false); setBacktestSingleTarget(target || null); setShowBacktestModal(true); }}
              />
            ))}
            {Object.values(MY_STRATEGY_DEFS).filter(def => myStrategies[def.type]?.favorite).map(def => (
              <StrategyCard
                key={def.type}
                def={def}
                config={myStrategies[def.type]}
                onEdit={type => setEditingMyStrategy(type)}
                onToggle={handleMyStrategyToggle}
                onFavorite={handleMyStrategyFavorite}
                onAutopilotEligible={handleMyStrategyAutopilotEligible}
                allStrategies={myStrategies}
                onLaunchSimulation={(target) => { setSimulating(false); setSimulationDirty(false); setBacktestSingleTarget(target || null); setShowBacktestModal(true); }}
              />
            ))}
            {algos.filter(a => a.favorite).length === 0
              && Object.values(MY_STRATEGY_DEFS).filter(def => myStrategies[def.type]?.favorite).length === 0 && (
              <div className="no-trades">No favourites yet — star an algo from General or Complex Strategies to see it here.</div>
            )}
          </div>
        )}

        {activeTab === 'general' && (
          <div className="algo-list">
            {algos.map(algo => (
              <AlgoCard
                key={algo.id}
                algo={algo}
                onToggle={handleToggle}
                onEdit={setEditing}
                onDelete={handleDelete}
                onFavorite={handleAlgoFavorite}
                onLaunchSimulation={(target) => { setSimulating(false); setSimulationDirty(false); setBacktestSingleTarget(target || null); setShowBacktestModal(true); }}
              />
            ))}
          </div>
        )}

        {activeTab === 'mystrategies' && (
          <div className="algo-list">
            {nseSnapshotStatus && !nseSnapshotStatus.hasData && (
              <div className="obm-snapshot-banner">
                <strong>Order Book Momentum — Data Pending</strong>
                <span>Previous-day 3PM depth snapshot has not been captured yet. It runs automatically at 3:00 PM IST every weekday. OBM cannot be activated until data is available.</span>
                <button
                  className="snapshot-trigger-btn"
                  onClick={handleTakeSnapshot}
                  disabled={snapshotCapturing}
                >
                  {snapshotCapturing ? 'Capturing... (~3-4 min)' : 'Take Snapshot Now'}
                </button>
              </div>
            )}
            {snapshotJustCaptured && nseSnapshotStatus && (
              <div className="obm-snapshot-banner obm-snapshot-banner--captured">
                <strong>Snapshot Captured Successfully</strong>
                <span>3PM depth data for {nseSnapshotStatus.date} is now ready — {nseSnapshotStatus.stockCount}/{nseSnapshotStatus.totalStocks} stocks captured. OBM strategies can now be activated.</span>
              </div>
            )}
            {Object.values(MY_STRATEGY_DEFS).map(def => (
              <StrategyCard
                key={def.type}
                def={def}
                config={myStrategies[def.type]}
                onEdit={type => setEditingMyStrategy(type)}
                onToggle={handleMyStrategyToggle}
                onFavorite={handleMyStrategyFavorite}
                onAutopilotEligible={handleMyStrategyAutopilotEligible}
                allStrategies={myStrategies}
                onLaunchSimulation={(target) => { setSimulating(false); setSimulationDirty(false); setBacktestSingleTarget(target || null); setShowBacktestModal(true); }}
              />
            ))}
          </div>
        )}


          {showBacktestModal && !simulating && !simulationDirty && (
            <div className="simulation-modal-overlay">
              <div className="backtest-prestart-modal">
                <div className="backtest-prestart-header">
                  <span className="backtest-prestart-title">
                    {backtestSingleTarget?.kind === 'algo'
                      ? `Backtest — ${backtestSingleTarget.algo.name}`
                      : backtestSingleTarget?.kind === 'strategy'
                      ? `Backtest — ${MY_STRATEGY_DEFS[backtestSingleTarget.type]?.name || backtestSingleTarget.type}`
                      : 'Backtest on Historical Data'}
                  </span>
                  <button className="editor-close-btn" onClick={() => { setShowBacktestModal(false); setBacktestSingleTarget(null); }}>✕</button>
                </div>

                <div className="backtest-prestart-body">
                  <div className="backtest-prestart-row">
                    <label className="backtest-label">Data Source:</label>
                    <select
                      value={backtestDataSource}
                      onChange={e => changeBacktestSource(e.target.value)}
                      className="backtest-date-select"
                    >
                      <option value="backtest">Backtest Data — minutely captured</option>
                      <option value="user">User Data — downloaded in Configuration</option>
                    </select>
                  </div>
                  {backtestDates.length === 0 ? (
                    <div className="no-data-message">
                      {backtestDataSource === 'user'
                        ? 'No downloaded historical data found. Download data for your stocks in Configuration → stock row → Historical Data.'
                        : 'No backtest data available yet. Data is captured automatically during market hours (09:15–15:30 IST) on trading days.'}
                    </div>
                  ) : (
                    <div className="backtest-prestart-row">
                      <label className="backtest-label">Select Date:</label>
                      <select
                        value={backtestDate}
                        onChange={e => setBacktestDate(e.target.value)}
                        className="backtest-date-select"
                      >
                        {backtestDates.map(d => {
                          const dt = new Date(d + 'T00:00:00');
                          const label = dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
                          return <option key={d} value={d}>{label}</option>;
                        })}
                      </select>
                    </div>
                  )}
                </div>
                {backtestDates.length > 0 && (
                  <div className="backtest-prestart-footer">
                    <button className="btn-cancel" onClick={() => { setShowBacktestModal(false); setBacktestSingleTarget(null); }}>Cancel</button>
                    <button className="backtest-btn" onClick={handleStartBacktest} disabled={!backtestDate}>
                      ▶ Start Backtest
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {(simulating || simulationDirty) && (
            <div className="simulation-modal-overlay">
              <div className="simulation-modal-content">
                <div className="simulation-modal-header">
                  <div className="simulation-modal-title">
                    <div className="simulation-header-date">
                      <span className="simulation-backtest-label">Backtesting —</span>
                      <select
                        value={backtestDate}
                        onChange={e => handlePopupDateChange(e.target.value)}
                        className="backtest-date-select simulation-date-select"
                      >
                        {backtestDates.map(d => {
                          const dt = new Date(d + 'T00:00:00');
                          return (
                            <option key={d} value={d}>
                              {dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                            </option>
                          );
                        })}
                      </select>
                      <button
                        className="popup-reload-btn simulation-restart-btn"
                        onClick={handleRestartBacktest}
                        disabled={!backtestDate}
                        title="Restart backtest with selected date"
                      >
                        ↺ Restart
                      </button>
                    </div>
                    {(() => {
                      const symbols = activeBacktestSymbols;
                      const shown = symbols.slice(0, 5);
                      const rest  = symbols.slice(5);
                      return (
                        <p className="simulation-symbols">
                          {shown.join(', ')}
                          {rest.length > 0 && (
                            <>
                              {' '}
                              <button className="more-symbols-link" onClick={() => setShowAllSymbols(s => !s)}>
                                +{rest.length} more
                              </button>
                              {showAllSymbols && (
                                <span className="all-symbols-popup">
                                  {symbols.join(', ')}
                                  <button className="close-symbols-popup" onClick={e => { e.stopPropagation(); setShowAllSymbols(false); }}>✕</button>
                                </span>
                              )}
                            </>
                          )}
                        </p>
                      );
                    })()}
                  </div>
                  {candles.length > 0 && (
                    <BacktestSimulationClock currentTime={simulationTime || new Date(candles[0][0])} />
                  )}
                  <div className="simulation-buttons">
                    <button className="ff-btn" onClick={handleFastForward} title="Complete remaining in ~5 seconds" disabled={!simulating || isFastForward}>
                      ⏩ Fast Forward
                    </button>
                    {simulating ? (
                      <button className={`pause-btn ${isPaused ? 'paused' : ''}`} onClick={handlePauseResume}>
                        {isPaused ? '▶ Resume' : '⏸ Pause'}
                      </button>
                    ) : (
                      <button className="backtest-btn" onClick={handleRestartBacktest} disabled={!backtestDate}>
                        ▶ Start
                      </button>
                    )}
                    <button className="stop-simulation-btn" onClick={handleStopBacktest}>
                      ✕ Close
                    </button>
                  </div>
                </div>
                <div className="backtest-modal-grids">
                  <div className="backtest-modal-grid-section">
                    {(() => {
                      let grossPnl = 0, totalCharges = 0;
                      for (const pos of positions) {
                        const unrealized = pos.netQty > 0
                          ? (pos.currentPrice - pos.avgPrice) * pos.netQty
                          : pos.netQty < 0
                          ? (pos.avgPrice - pos.currentPrice) * Math.abs(pos.netQty)
                          : 0;
                        grossPnl += pos.realizedPnl + unrealized;
                        totalCharges += pos.charges || 0;
                      }
                      const netPnl = grossPnl - totalCharges;
                      return (
                        <div className="grid-title">
                          <span>Positions ({positions.length})</span>
                          {positions.length > 0 && (
                            <span className="positions-pnl-summary">
                              <span className={grossPnl >= 0 ? 'profit' : 'loss'}>Gross: ₹{grossPnl.toFixed(0)}</span>
                              <span className="charges-label"> − Charges: ₹{totalCharges.toFixed(0)}</span>
                              <span className={`net-pnl-label ${netPnl >= 0 ? 'profit' : 'loss'}`}> = Net: ₹{netPnl.toFixed(0)}</span>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <div className="simulation-grid-wrap">
                      <table className="simulation-grid">
                        <thead>
                          <tr>
                            <th>Symbol</th>
                            <th style={{textAlign:'right'}}>Min P&amp;L ₹</th>
                            <th style={{textAlign:'right'}}>Max P&amp;L ₹</th>
                            <th style={{textAlign:'right'}}>Net Qty</th>
                            <th style={{textAlign:'right'}}>Avg ₹</th>
                            <th style={{textAlign:'right'}}>LTP ₹</th>
                            <th style={{textAlign:'right'}}>Charges (₹)</th>
                            <th style={{textAlign:'right'}}>Net P&amp;L (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {positions.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="no-data">No positions yet</td>
                            </tr>
                          ) : (
                            positions.map((pos) => {
                              const isFlat = pos.netQty === 0;
                              const unrealizedPnl = pos.netQty > 0
                                ? (pos.currentPrice - pos.avgPrice) * pos.netQty
                                : pos.netQty < 0
                                ? (pos.avgPrice - pos.currentPrice) * Math.abs(pos.netQty)
                                : 0;
                              const grossPnl = pos.realizedPnl + unrealizedPnl;
                              const netPnl = grossPnl - (pos.charges || 0);
                              return (
                                <tr key={pos.symbol} className={isFlat ? 'pos-closed' : (netPnl >= 0 ? 'profit' : 'loss')}>
                                  <td className="symbol">{pos.symbol}</td>
                                  <td className="price loss">{pos.minPnL != null ? `₹${pos.minPnL.toFixed(0)}` : '—'}</td>
                                  <td className="price profit">{pos.maxPnL != null ? `₹${pos.maxPnL.toFixed(0)}` : '—'}</td>
                                  <td className={`numeric ${pos.netQty > 0 ? 'buy' : pos.netQty < 0 ? 'sell' : ''}`}>
                                    {pos.netQty > 0 ? '+' : ''}{pos.netQty}
                                  </td>
                                  <td className="price">₹{pos.avgPrice.toFixed(2)}</td>
                                  <td className="price">₹{pos.currentPrice.toFixed(2)}</td>
                                  <td className="loss">₹{(pos.charges || 0).toFixed(0)}</td>
                                  <td className={netPnl >= 0 ? 'profit' : 'loss'}>₹{netPnl.toFixed(2)}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="backtest-modal-grid-section">
                    <div className="grid-title">Orders ({orders.length})</div>
                    <div className="simulation-grid-wrap">
                      <table className="simulation-grid">
                        <thead>
                          <tr>
                            <th>Symbol</th>
                            <th>Type</th>
                            <th style={{textAlign:'right'}}>Qty</th>
                            <th style={{textAlign:'right'}}>Price</th>
                            <th style={{textAlign:'right'}}>Charges</th>
                            <th style={{textAlign:'right'}}>Status</th>
                            <th style={{textAlign:'right'}}>Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="no-data">No orders yet</td>
                            </tr>
                          ) : (
                            orders.map((order, idx) => {
                              const time = new Date(order.timestamp).toLocaleTimeString('en-IN', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false,
                              });
                              const charges = calcOrderCharges(order.transactionType, order.price, order.quantity);
                              return (
                                <tr key={idx}>
                                  <td className="symbol">{order.symbol}</td>
                                  <td className={order.transactionType === 'BUY' ? 'buy' : 'sell'}>
                                    {order.transactionType}
                                  </td>
                                  <td className="numeric">{order.quantity}</td>
                                  <td className="price">₹{order.price.toFixed(2)}</td>
                                  <td className="loss">₹{charges.toFixed(0)}</td>
                                  <td className={order.transactionType === 'BUY' ? 'buy' : 'sell'}>{order.status}</td>
                                  <td className={order.transactionType === 'BUY' ? 'buy' : 'sell'}>{time}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Global Trade Settings ── */}
          <div className="config-section">
            <div className="config-section-header">
              <h3 className="config-section-title">Global Trade Settings</h3>
            </div>
            {squareOffMsg && (
              <div className={`squareoff-msg squareoff-msg--banner ${squareOffMsg.toLowerCase().includes('fail') ? 'squareoff-msg--err' : 'squareoff-msg--ok'}`}>
                {squareOffMsg}
              </div>
            )}
            <div className="global-settings-body">
            <div className="global-settings-inputs">
              <div className="global-setting-row global-setting-row--wide">
                <label className="global-setting-label">
                  Max Profit Per Day (₹) — starting ceiling
                  <GsHelp text="Reaching this does NOT square off or deactivate anything. Instead the Min Profit floor is raised to Max-Profit Ratchet Floor % of the CURRENT net P&L at that moment (only if higher than the existing floor) and this ceiling itself grows by Max-Profit Ratchet Growth % for next time — repeating indefinitely as net P&L keeps climbing. The ceiling is tracked from this starting value and resets back to it whenever the Min Profit floor triggers a square-off." />
                </label>
                <input
                  type="number"
                  className="global-setting-input"
                  min="0"
                  step="100"
                  value={globalSettings.max_profit_per_day || ''}
                  placeholder="0 = disabled"
                  onChange={e => setGlobalSettings(s => ({ ...s, max_profit_per_day: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="global-setting-row global-setting-row--wide">
                <label className="global-setting-label">
                  Max-Profit Ratchet Floor (%)
                  <GsHelp text="Each time the ceiling above is reached, the Min Profit floor is raised to this % of the CURRENT net P&L at that moment — not the ceiling threshold itself — only if that's higher than the existing floor." />
                </label>
                <input
                  type="number"
                  className="global-setting-input"
                  min="1"
                  max="100"
                  step="5"
                  value={globalSettings.max_profit_ratchet_floor_pct || ''}
                  placeholder="90"
                  onChange={e => setGlobalSettings(s => ({ ...s, max_profit_ratchet_floor_pct: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="global-setting-row global-setting-row--wide">
                <label className="global-setting-label">
                  Max-Profit Ratchet Growth (%)
                  <GsHelp text="Each time the ceiling above is reached, it grows by this % for next time — this can repeat indefinitely as net P&L keeps climbing." />
                </label>
                <input
                  type="number"
                  className="global-setting-input"
                  min="1"
                  step="5"
                  value={globalSettings.max_profit_ratchet_growth_pct || ''}
                  placeholder="25"
                  onChange={e => setGlobalSettings(s => ({ ...s, max_profit_ratchet_growth_pct: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="global-setting-row">
                <label className="global-setting-label">Max Loss Per Day (₹)</label>
                <input
                  type="number"
                  className="global-setting-input"
                  min="0"
                  step="100"
                  value={globalSettings.max_loss_per_day || ''}
                  placeholder="0 = disabled"
                  onChange={e => setGlobalSettings(s => ({ ...s, max_loss_per_day: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="global-setting-row global-setting-row--wide">
                <label className="global-setting-label">
                  Profit Value to Activate Min Profit (₹)
                  <GsHelp text="Account-wide trailing profit floor. Once net MIS P&L across all positions ever reaches this amount, the floor arms at Min Profit Value vs Current Profit %. Checked every 20s and re-arms higher every time net P&L makes a new high — ratchets up only, never down. Breaching it squares off everything to lock in the gain, then resets and re-arms fresh — unlike Max Loss, algos are NOT deactivated and keep trading. Runs independently of the Max-Profit ratchet above; both only ever raise this same floor, so whichever is higher wins." />
                </label>
                <input
                  type="number"
                  className="global-setting-input"
                  min="0"
                  step="100"
                  value={globalSettings.profit_activation_threshold || ''}
                  placeholder="0 = disabled"
                  onChange={e => setGlobalSettings(s => ({ ...s, profit_activation_threshold: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="global-setting-row global-setting-row--wide">
                <label className="global-setting-label">
                  Min Profit Value vs Current Profit (%)
                  <GsHelp text="Once the floor above is armed, it locks in this % of whatever the current net P&L is. E.g. 75% with ₹5000 net profit → floor locks at ₹3750." />
                </label>
                <input
                  type="number"
                  className="global-setting-input"
                  min="0"
                  max="99"
                  step="1"
                  value={globalSettings.min_profit_ratio || ''}
                  placeholder="0 = disabled"
                  onChange={e => setGlobalSettings(s => ({ ...s, min_profit_ratio: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="global-settings-actions">
              <div className="global-settings-btns-end">
                <button
                  className="global-settings-save-btn"
                  onClick={handleSaveGlobalSettings}
                  disabled={settingsSaving}
                >
                  {settingsSaving ? 'Saving…' : settingsSaved ? 'Saved' : 'Save'}
                </button>
                <button
                  className="global-squareoff-btn"
                  onClick={handleSquareOffAll}
                  disabled={squaringOff}
                  title="Close all open Kite positions and deactivate all algos"
                >
                  {squaringOff ? 'Squaring Off…' : '🚪 Square Off All'}
                </button>
              </div>
              <div className="global-minprofit-status" title="Account-wide trailing min-profit floor — the net P&amp;L level that triggers an auto square-off if breached. Refreshes automatically every 15s.">
                <div className="global-minprofit-status-text">
                  <div className="global-minprofit-status-line">
                    <span className="global-minprofit-status-label">Current Min Profit Floor:</span>
                    <span className="global-minprofit-status-value">
                      {pnlStatus?.minProfitFloor != null ? `₹${pnlStatus.minProfitFloor.toFixed(0)}` : 'Not armed'}
                    </span>
                  </div>
                  <div className="global-minprofit-status-line">
                    <span className="global-minprofit-status-label">Current Max Profit Ceiling:</span>
                    <span className="global-minprofit-status-value">
                      {pnlStatus?.maxProfitCeiling != null ? `₹${pnlStatus.maxProfitCeiling.toFixed(0)}` : 'Not seeded'}
                    </span>
                  </div>
                  {pnlStatus?.peakPnLToday != null && (
                    <div className="global-minprofit-status-line">
                      <span className="global-minprofit-status-peak">(peak net P&amp;L today: ₹{pnlStatus.peakPnLToday.toFixed(0)})</span>
                    </div>
                  )}
                </div>
                <button
                  className="global-minprofit-refresh-btn"
                  onClick={() => fetchPnLStatus(true)}
                  disabled={pnlStatusLoading}
                  title="Refresh now"
                >
                  {pnlStatusLoading ? '…' : '⟳'}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
    </div>
  );
}

export default AlgosPage;
